// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  sourcesHeading: 'What to track',
  sourcesNote:
    'Sources come from certified Tool Store packs, and missing ones are added on request.',
  chooseSource: 'Choose {name}',
  readsLine: 'Reads {fields}',
  partialLine: 'Reads {fields}. Not yet: {gaps}.',
  awaitingLine: 'Not supported yet. Missing sources are added on request.',
  requestIt: 'Request it',
  requestedLine: 'Requested {date} · we’ll tell you when it’s ready.',
  needsConnection: 'Needs {whose} {system} connection',
  connect: 'Connect {system}',
  askOwner: 'Ask {owner}',
  coverageSummary: '{n} sources collect now; {m} will join when they’re supported.',
  coverageSummaryOne: '{n} source collects now; {m} will join when they’re supported.',
  scheduleHeading: 'Schedule and budget',
  scheduleTime: 'Runs every day at',
  scheduleDaily: 'Runs every day at {time}',
  scheduleEveryHour: 'Runs every hour',
  scheduleEveryMinutes: 'Runs every {minutes} minutes',
  scheduleTimezone: 'Time zone',
  budgetPerRun: 'Most to spend on one run',
  budgetMonthly: 'Most to spend in a month',
  scheduleSave: 'Save schedule',
  scheduleSaving: 'Saving…',
  scheduleSummary: 'Daily at {time} {zone} · up to {perRun} a run · up to {monthly} a month',
  oneRunPerDay: 'One run each day. A repeated or retried run never counts twice.',
  errTime: 'Enter a time like 07:00.',
  errCredits: 'Enter a whole number from {min} to {max}.',
  errOrder: 'The monthly limit must be at least the limit for one run.',
  collecting: 'Collecting daily at {time} · last read {last} · {rows} rows',
  collectingNever: 'Collecting daily at {time} · not read yet',
  awaitingData: 'Requested {date} · not collecting yet',
  stopped: 'Stopped: {reason}',
  reconnect: 'Reconnect',
  skippedBudget: 'Skipped {skipped} of {total} categories today: the monthly budget is reached.',
  gapMarker: 'No data for this period',
  gapBudget: 'Skipped: monthly budget reached',
  gapUnsupported: 'Skipped: not supported yet',
  gapFailed: 'Skipped: the source didn’t answer',
  chartLabel: 'Price over time. A skipped period is a break in the line, not zero.',
  firstRunTitle: 'First run done',
  firstRun: 'Read {read} sources · {unsupported} not supported yet · cost {cost} · next run {next}',
  firstRunUnmeasured:
    'Read {read} sources · {unsupported} not supported yet · cost not measured yet · next run {next}',
  firstRunNoNext:
    'Read {read} sources · {unsupported} not supported yet · cost {cost} · no run is scheduled',
  lastRunNone: 'No run yet. The first one runs at the scheduled time.',
  paused: 'Paused',
  pause: 'Pause',
  resume: 'Resume',
  historyFrom: 'History from {source} since {since} (dates from the source)',
  historyImportedMark: 'Imported',
  historyToday: 'History starts today, {date}.',
  budgetSave: 'Save budget',
  creditsUnit: 'credits',
  budgetInvalid: 'These limits can’t be saved. Check them and try again.',
  budgetSummary: 'Up to {perRun} a run · up to {monthly} a month',
  budgetNone: 'No budget is set yet.',
  budgetUnavailable: 'The budget isn’t available right now.',
  budgetDenied: 'You can’t change this budget.',
  budgetChanged: 'The budget changed since you opened this. Check the limits and try again.',
  budgetUnknown: 'We couldn’t confirm whether it was saved. Try again; it won’t be applied twice.',
  scheduleFacts: 'Runs {schedule} ({zone})',
  scheduleNext: 'Next run {next}',
  scheduleOn: 'On',
  scheduleNotScheduled: 'Not scheduled',
  scheduleEnable: 'Turn on',
  scheduleTimeNote: 'Times are in the schedule’s own time zone.',
  scheduleUnavailable: 'The schedule isn’t available right now.',
  scheduleDenied: 'You can’t change this schedule.',
  scheduleConflict: 'The schedule changed since you opened this. Check it and try again.',
  scheduleUnknown:
    'We couldn’t confirm whether the schedule changed. Try again; it won’t be applied twice.',
  budgetHeading: 'Budget',
  scheduleOnlyHeading: 'Schedule',
  scheduleJobOrdinal: 'Scheduled run {n}',
  budgetUnconfirmed: 'We couldn’t confirm whether it was saved.',
  scheduleUnconfirmed: 'We couldn’t confirm whether the schedule changed.',
  scheduleWeekdays: 'Runs on weekdays at {time}',
  scheduleWeekly: 'Runs every {days} at {time}',
  scheduleCustom: 'Runs on its own schedule',
  scheduleThen: 'Then {runs}',
  scheduleChange: 'Change schedule',
  scheduleChoose: 'Choose another time',
  pickerHeading: 'When it runs',
  presetEvery15: 'Every 15 minutes',
  presetEvery30: 'Every 30 minutes',
  presetHourly: 'Every hour',
  presetDaily: 'Every day',
  presetWeekdays: 'Weekdays',
  presetWeekly: 'Every week',
  presetCustom: 'Custom',
  pickerTime: 'Time',
  pickerDay: 'Day',
  pickerCron: 'Cron expression',
  pickerCronHelp:
    'Five fields: minute, hour, day of month, month and day of week, for example 0 9 1 * * for 09:00 on the first of each month.',
  pickerCustomPreview: 'Saving checks this schedule and shows its next runs.',
  pickerCancel: 'Cancel',
  scheduleTooFrequent: 'Runs can be at most every 15 minutes.',
  scheduleInvalidChoice: 'That schedule can’t be used. Check the expression and the time zone.',
  scheduleBusy: 'A run is in progress. Try again when it has finished.',
  runsHeading: 'Recent runs',
  runsEmpty: 'No runs yet.',
  runsUnavailable: 'Recent runs aren’t available right now.',
  runSucceeded: 'Succeeded',
  runFailed: 'Failed',
  runUnknown: 'Unconfirmed',
  runManual: 'Run by hand',
  runScheduled: 'Scheduled',
  runWebhook: 'Webhook',
  runEvent: 'Data change',
  runShowLog: 'Show log',
  runHideLog: 'Hide log',
  runLogEmpty: 'It printed nothing.',
  runLogDropped: '{n} earlier lines weren’t kept.',
  runError: 'Error',
  runPrinted: 'What it printed',
  runInput: 'Input',
  runLogUnavailable: 'The log isn’t available right now.',
  runFix: 'Fix in chat',
  runFixFailed: 'The App’s chat couldn’t be opened. Try again.',
  runsMore: 'Load more',
  fixScheduled: 'This App’s scheduled run failed. Please find the cause and fix it.',
  fixTriggered: 'This App’s run failed. Please find the cause and fix it.',
  fixUnknown:
    'This App’s run ended without a confirmed result. Please check what happened and fix it.',
  fixRunLine: 'Function {name} · run {id} · {time}',
  fixError: 'Error',
  fixPrinted: 'The last lines it printed',
  fixInput: 'Its input',
};

export type AppTrackerCopyKey = keyof typeof en;

export const appTrackerCopy: NamespaceCatalog<keyof typeof en> = {
  en: {
    sourcesHeading: 'What to track',
    sourcesNote:
      'Sources come from certified Tool Store packs, and missing ones are added on request.',
    chooseSource: 'Choose {name}',
    readsLine: 'Reads {fields}',
    partialLine: 'Reads {fields}. Not yet: {gaps}.',
    awaitingLine: 'Not supported yet. Missing sources are added on request.',
    requestIt: 'Request it',
    requestedLine: 'Requested {date} · we’ll tell you when it’s ready.',
    needsConnection: 'Needs {whose} {system} connection',
    connect: 'Connect {system}',
    askOwner: 'Ask {owner}',
    coverageSummary: '{n} sources collect now; {m} will join when they’re supported.',
    coverageSummaryOne: '{n} source collects now; {m} will join when they’re supported.',
    scheduleHeading: 'Schedule and budget',
    scheduleTime: 'Runs every day at',
    scheduleDaily: 'Runs every day at {time}',
    scheduleEveryHour: 'Runs every hour',
    scheduleEveryMinutes: 'Runs every {minutes} minutes',
    scheduleTimezone: 'Time zone',
    budgetPerRun: 'Most to spend on one run',
    budgetMonthly: 'Most to spend in a month',
    scheduleSave: 'Save schedule',
    scheduleSaving: 'Saving…',
    scheduleSummary: 'Daily at {time} {zone} · up to {perRun} a run · up to {monthly} a month',
    oneRunPerDay: 'One run each day. A repeated or retried run never counts twice.',
    errTime: 'Enter a time like 07:00.',
    errCredits: 'Enter a whole number from {min} to {max}.',
    errOrder: 'The monthly limit must be at least the limit for one run.',
    collecting: 'Collecting daily at {time} · last read {last} · {rows} rows',
    collectingNever: 'Collecting daily at {time} · not read yet',
    awaitingData: 'Requested {date} · not collecting yet',
    stopped: 'Stopped: {reason}',
    reconnect: 'Reconnect',
    skippedBudget: 'Skipped {skipped} of {total} categories today: the monthly budget is reached.',
    gapMarker: 'No data for this period',
    gapBudget: 'Skipped: monthly budget reached',
    gapUnsupported: 'Skipped: not supported yet',
    gapFailed: 'Skipped: the source didn’t answer',
    chartLabel: 'Price over time. A skipped period is a break in the line, not zero.',
    firstRunTitle: 'First run done',
    firstRun:
      'Read {read} sources · {unsupported} not supported yet · cost {cost} · next run {next}',
    firstRunUnmeasured:
      'Read {read} sources · {unsupported} not supported yet · cost not measured yet · next run {next}',
    firstRunNoNext:
      'Read {read} sources · {unsupported} not supported yet · cost {cost} · no run is scheduled',
    lastRunNone: 'No run yet. The first one runs at the scheduled time.',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    historyFrom: 'History from {source} since {since} (dates from the source)',
    historyImportedMark: 'Imported',
    historyToday: 'History starts today, {date}.',
    budgetSave: 'Save budget',
    creditsUnit: 'credits',
    budgetInvalid: 'These limits can’t be saved. Check them and try again.',
    budgetSummary: 'Up to {perRun} a run · up to {monthly} a month',
    budgetNone: 'No budget is set yet.',
    budgetUnavailable: 'The budget isn’t available right now.',
    budgetDenied: 'You can’t change this budget.',
    budgetChanged: 'The budget changed since you opened this. Check the limits and try again.',
    budgetUnknown:
      'We couldn’t confirm whether it was saved. Try again; it won’t be applied twice.',
    scheduleFacts: 'Runs {schedule} ({zone})',
    scheduleNext: 'Next run {next}',
    scheduleOn: 'On',
    scheduleNotScheduled: 'Not scheduled',
    scheduleEnable: 'Turn on',
    scheduleUnavailable: 'The schedule isn’t available right now.',
    scheduleDenied: 'You can’t change this schedule.',
    scheduleConflict: 'The schedule changed since you opened this. Check it and try again.',
    scheduleUnknown:
      'We couldn’t confirm whether the schedule changed. Try again; it won’t be applied twice.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Schedule',
    scheduleJobOrdinal: 'Scheduled run {n}',
    budgetUnconfirmed: 'We couldn’t confirm whether it was saved.',
    scheduleUnconfirmed: 'We couldn’t confirm whether the schedule changed.',
    scheduleTimeNote: 'Times are in the schedule’s own time zone.',
    scheduleWeekdays: 'Runs on weekdays at {time}',
    scheduleWeekly: 'Runs every {days} at {time}',
    scheduleCustom: 'Runs on its own schedule',
    scheduleThen: 'Then {runs}',
    scheduleChange: 'Change schedule',
    scheduleChoose: 'Choose another time',
    pickerHeading: 'When it runs',
    presetEvery15: 'Every 15 minutes',
    presetEvery30: 'Every 30 minutes',
    presetHourly: 'Every hour',
    presetDaily: 'Every day',
    presetWeekdays: 'Weekdays',
    presetWeekly: 'Every week',
    presetCustom: 'Custom',
    pickerTime: 'Time',
    pickerDay: 'Day',
    pickerCron: 'Cron expression',
    pickerCronHelp:
      'Five fields: minute, hour, day of month, month and day of week, for example 0 9 1 * * for 09:00 on the first of each month.',
    pickerCustomPreview: 'Saving checks this schedule and shows its next runs.',
    pickerCancel: 'Cancel',
    scheduleTooFrequent: 'Runs can be at most every 15 minutes.',
    scheduleInvalidChoice: 'That schedule can’t be used. Check the expression and the time zone.',
    scheduleBusy: 'A run is in progress. Try again when it has finished.',
    runsHeading: 'Recent runs',
    runsEmpty: 'No runs yet.',
    runsUnavailable: 'Recent runs aren’t available right now.',
    runSucceeded: 'Succeeded',
    runFailed: 'Failed',
    runUnknown: 'Unconfirmed',
    runManual: 'Run by hand',
    runScheduled: 'Scheduled',
    runWebhook: 'Webhook',
    runEvent: 'Data change',
    runShowLog: 'Show log',
    runHideLog: 'Hide log',
    runLogEmpty: 'It printed nothing.',
    runLogDropped: '{n} earlier lines weren’t kept.',
    runError: 'Error',
    runPrinted: 'What it printed',
    runInput: 'Input',
    runLogUnavailable: 'The log isn’t available right now.',
    runFix: 'Fix in chat',
    runFixFailed: 'The App’s chat couldn’t be opened. Try again.',
    runsMore: 'Load more',
    fixScheduled: 'This App’s scheduled run failed. Please find the cause and fix it.',
    fixTriggered: 'This App’s run failed. Please find the cause and fix it.',
    fixUnknown:
      'This App’s run ended without a confirmed result. Please check what happened and fix it.',
    fixRunLine: 'Function {name} · run {id} · {time}',
    fixError: 'Error',
    fixPrinted: 'The last lines it printed',
    fixInput: 'Its input',
  },
  bg: {
    sourcesHeading: 'Какво да се проследява',
    sourcesNote:
      'Източниците идват от сертифицирани пакети от Магазина за инструменти, а липсващите се добавят при заявка.',
    chooseSource: 'Изберете {name}',
    readsLine: 'Извлича {fields}',
    partialLine: 'Извлича {fields}. Все още липсват: {gaps}.',
    awaitingLine: 'Все още не се поддържа. Липсващите източници се добавят при заявка.',
    requestIt: 'Заявете го',
    requestedLine: 'Заявено на {date} · ще ви уведомим, когато е готово.',
    needsConnection: 'Изисква връзка на {whose} с {system}',
    connect: 'Свързване с {system}',
    askOwner: 'Попитайте {owner}',
    coverageSummary:
      'В момента се събират {n} източника; още {m} ще бъдат добавени, когато се поддържат.',
    coverageSummaryOne:
      'В момента се събира {n} източник; още {m} ще бъдат добавени, когато се поддържат.',
    scheduleHeading: 'График и бюджет',
    scheduleTime: 'Изпълнява се всеки ден в',
    scheduleDaily: 'Изпълнява се всеки ден в {time}',
    scheduleEveryHour: 'Изпълнява се всеки час',
    scheduleEveryMinutes: 'Изпълнява се на всеки {minutes} минути',
    scheduleTimezone: 'Часова зона',
    budgetPerRun: 'Максимална сума за едно изпълнение',
    budgetMonthly: 'Максимална сума за месец',
    scheduleSave: 'Запазване на графика',
    scheduleSaving: 'Запазване…',
    scheduleSummary:
      'Всеки ден в {time} {zone} · до {perRun} на изпълнение · до {monthly} на месец',
    oneRunPerDay:
      'Едно изпълнение дневно. Повторено или повторно стартирано изпълнение никога не се брои два пъти.',
    errTime: 'Въведете час, например 07:00.',
    errCredits: 'Въведете цяло число от {min} до {max}.',
    errOrder: 'Месечният лимит трябва да е поне колкото лимита за едно изпълнение.',
    collecting: 'Ежедневно събиране в {time} · последно извличане {last} · {rows} реда',
    collectingNever: 'Ежедневно събиране в {time} · все още няма извличане',
    awaitingData: 'Заявено на {date} · събирането още не е започнало',
    stopped: 'Спряно: {reason}',
    reconnect: 'Свързване отново',
    skippedBudget:
      'Днес са пропуснати {skipped} от {total} категории: месечният бюджет е достигнат.',
    gapMarker: 'Няма данни за този период',
    gapBudget: 'Пропуснато: месечният бюджет е достигнат',
    gapUnsupported: 'Пропуснато: все още не се поддържа',
    gapFailed: 'Пропуснато: източникът не отговори',
    chartLabel:
      'Цена във времето. Пропуснатият период е прекъсване на линията, а не нулева стойност.',
    firstRunTitle: 'Първото изпълнение завърши',
    firstRun:
      'Извлечени са данни от {read} източника · {unsupported} все още не се поддържат · цена {cost} · следващо изпълнение {next}',
    firstRunUnmeasured:
      'Извлечени са данни от {read} източника · {unsupported} все още не се поддържат · цената все още не е измерена · следващо изпълнение {next}',
    firstRunNoNext:
      'Извлечени са данни от {read} източника · {unsupported} все още не се поддържат · цена {cost} · няма планирано изпълнение',
    lastRunNone: 'Все още няма изпълнение. Първото ще се изпълни в планирания час.',
    paused: 'На пауза',
    pause: 'Пауза',
    resume: 'Продължаване',
    historyFrom: 'История от {source} от {since} насам (датите са от източника)',
    historyImportedMark: 'Импортирано',
    historyToday: 'Историята започва днес, {date}.',
    budgetSave: 'Запазване на бюджета',
    creditsUnit: 'кредита',
    budgetInvalid: 'Тези лимити не могат да бъдат запазени. Проверете ги и опитайте отново.',
    budgetSummary: 'До {perRun} на изпълнение · до {monthly} на месец',
    budgetNone: 'Все още няма зададен бюджет.',
    budgetUnavailable: 'Бюджетът в момента не е наличен.',
    budgetDenied: 'Не можете да променяте този бюджет.',
    budgetChanged:
      'Бюджетът се е променил, след като сте отворили тази страница. Проверете лимитите и опитайте отново.',
    budgetUnknown:
      'Не успяхме да потвърдим дали е запазен. Опитайте отново; няма да бъде приложен два пъти.',
    scheduleFacts: 'Изпълнява се {schedule} ({zone})',
    scheduleNext: 'Следващо изпълнение {next}',
    scheduleOn: 'Включено',
    scheduleNotScheduled: 'Няма график',
    scheduleEnable: 'Включване',
    scheduleUnavailable: 'Графикът не е наличен в момента.',
    scheduleDenied: 'Не можете да променяте този график.',
    scheduleConflict:
      'Графикът се промени, след като го отворихте. Проверете го и опитайте отново.',
    scheduleUnknown:
      'Не успяхме да потвърдим дали графикът се е променил. Опитайте отново; промяната няма да бъде приложена два пъти.',
    budgetHeading: 'Бюджет',
    scheduleOnlyHeading: 'График',
    scheduleJobOrdinal: 'Планирано изпълнение {n}',
    budgetUnconfirmed: 'Не успяхме да потвърдим дали е запазен.',
    scheduleUnconfirmed: 'Не успяхме да потвърдим дали графикът се е променил.',
    scheduleTimeNote: 'Часовете са в собствената часова зона на графика.',
    scheduleWeekdays: 'Изпълнява се в делнични дни в {time}',
    scheduleWeekly: 'Изпълнява се седмично: {days} в {time}',
    scheduleCustom: 'Изпълнява се по собствен график',
    scheduleThen: 'След това {runs}',
    scheduleChange: 'Промяна на графика',
    scheduleChoose: 'Избор на друго време',
    pickerHeading: 'Кога се изпълнява',
    presetEvery15: 'На всеки 15 минути',
    presetEvery30: 'На всеки 30 минути',
    presetHourly: 'Всеки час',
    presetDaily: 'Всеки ден',
    presetWeekdays: 'Делнични дни',
    presetWeekly: 'Всяка седмица',
    presetCustom: 'По избор',
    pickerTime: 'Час',
    pickerDay: 'Ден',
    pickerCron: 'Cron израз',
    pickerCronHelp:
      'Пет полета: минута, час, ден от месеца, месец и ден от седмицата, например 0 9 1 * * за 09:00 на първо число всеки месец.',
    pickerCustomPreview:
      'При запазване този график се проверява и се показват следващите изпълнения.',
    pickerCancel: 'Отказ',
    scheduleTooFrequent: 'Изпълненията могат да са най-много на всеки 15 минути.',
    scheduleInvalidChoice: 'Този график не може да се използва. Проверете израза и часовата зона.',
    scheduleBusy: 'В момента тече изпълнение. Опитайте отново, когато приключи.',
    runsHeading: 'Последни изпълнения',
    runsEmpty: 'Все още няма изпълнения.',
    runsUnavailable: 'Последните изпълнения не са налични в момента.',
    runSucceeded: 'Успешно',
    runFailed: 'Неуспешно',
    runUnknown: 'Непотвърдено',
    runManual: 'Стартирано ръчно',
    runScheduled: 'По график',
    runWebhook: 'Webhook',
    runEvent: 'Промяна в данните',
    runShowLog: 'Показване на журнала',
    runHideLog: 'Скриване на журнала',
    runLogEmpty: 'Не е отпечатало нищо.',
    runLogDropped: '{n} по-ранни реда не са запазени.',
    runError: 'Грешка',
    runPrinted: 'Какво е отпечатано',
    runInput: 'Вход',
    runLogUnavailable: 'Журналът не е наличен в момента.',
    runFix: 'Поправяне в чата',
    runFixFailed: 'Чатът на приложението не можа да се отвори. Опитайте отново.',
    runsMore: 'Зареждане на още',
    fixScheduled:
      'Планираното изпълнение на това приложение пропадна. Намерете причината и го поправете.',
    fixTriggered: 'Изпълнението на това приложение пропадна. Намерете причината и го поправете.',
    fixUnknown:
      'Изпълнението на това приложение приключи без потвърден резултат. Проверете какво се е случило и го поправете.',
    fixRunLine: 'Функция {name} · изпълнение {id} · {time}',
    fixError: 'Грешка',
    fixPrinted: 'Последните отпечатани редове',
    fixInput: 'Входът му',
  },
  cs: {
    sourcesHeading: 'Co sledovat',
    sourcesNote:
      'Zdroje pocházejí z certifikovaných balíčků Tool Store a chybějící zdroje se přidávají na vyžádání.',
    chooseSource: 'Vybrat {name}',
    readsLine: 'Načítá {fields}',
    partialLine: 'Načítá {fields}. Zatím ne: {gaps}.',
    awaitingLine: 'Zatím není podporováno. Chybějící zdroje se přidávají na vyžádání.',
    requestIt: 'Požádat o něj',
    requestedLine: 'Vyžádáno {date} · dáme vám vědět, až bude připraveno.',
    needsConnection: 'Vyžaduje připojení {whose} k systému {system}',
    connect: 'Připojit {system}',
    askOwner: 'Požádat uživatele {owner}',
    coverageSummary: '{n} zdrojů se nyní sbírá; {m} se přidá, až budou podporovány.',
    coverageSummaryOne: '{n} zdroj se nyní sbírá; {m} se přidá, až budou podporovány.',
    scheduleHeading: 'Plán a rozpočet',
    scheduleTime: 'Spouští se každý den v',
    scheduleDaily: 'Spouští se každý den v {time}',
    scheduleEveryHour: 'Spouští se každou hodinu',
    scheduleEveryMinutes: 'Spouští se každých {minutes} minut',
    scheduleTimezone: 'Časové pásmo',
    budgetPerRun: 'Maximum na jedno spuštění',
    budgetMonthly: 'Maximum za měsíc',
    scheduleSave: 'Uložit plán',
    scheduleSaving: 'Ukládání…',
    scheduleSummary: 'Denně v {time} {zone} · až {perRun} za spuštění · až {monthly} za měsíc',
    oneRunPerDay:
      'Jedno spuštění denně. Opakované ani opakovaně spuštěné spuštění se nikdy nezapočítá dvakrát.',
    errTime: 'Zadejte čas, například 07:00.',
    errCredits: 'Zadejte celé číslo od {min} do {max}.',
    errOrder: 'Měsíční limit musí být alespoň stejně vysoký jako limit na jedno spuštění.',
    collecting: 'Probíhá denně v {time} · poslední načtení {last} · {rows} řádků',
    collectingNever: 'Probíhá denně v {time} · zatím nenačteno',
    awaitingData: 'Vyžádáno {date} · zatím se nesbírá',
    stopped: 'Zastaveno: {reason}',
    reconnect: 'Znovu připojit',
    skippedBudget: 'Dnes přeskočeno {skipped} z {total} kategorií: měsíční rozpočet byl vyčerpán.',
    gapMarker: 'Pro toto období nejsou k dispozici žádná data',
    gapBudget: 'Přeskočeno: měsíční rozpočet byl vyčerpán',
    gapUnsupported: 'Přeskočeno: zatím není podporováno',
    gapFailed: 'Přeskočeno: zdroj neodpověděl',
    chartLabel: 'Cena v čase. Přeskočené období znamená přerušení čáry, nikoli nulovou hodnotu.',
    firstRunTitle: 'První spuštění dokončeno',
    firstRun:
      'Načteno {read} zdrojů · {unsupported} zatím není podporováno · cena {cost} · další spuštění {next}',
    firstRunUnmeasured:
      'Načteno {read} zdrojů · {unsupported} zatím není podporováno · cena zatím nebyla změřena · další spuštění {next}',
    firstRunNoNext:
      'Načteno {read} zdrojů · {unsupported} zatím není podporováno · cena {cost} · žádné spuštění není naplánováno',
    lastRunNone: 'Zatím žádné spuštění. První proběhne v naplánovaný čas.',
    paused: 'Pozastaveno',
    pause: 'Pozastavit',
    resume: 'Pokračovat',
    historyFrom: 'Historie ze zdroje {source} od {since} (data podle zdroje)',
    historyImportedMark: 'Importováno',
    historyToday: 'Historie začíná dnes, {date}.',
    budgetSave: 'Uložit rozpočet',
    creditsUnit: 'kreditů',
    budgetInvalid: 'Tyto limity nelze uložit. Zkontrolujte je a zkuste to znovu.',
    budgetSummary: 'Až {perRun} za spuštění · až {monthly} za měsíc',
    budgetNone: 'Rozpočet zatím není nastaven.',
    budgetUnavailable: 'Rozpočet momentálně není k dispozici.',
    budgetDenied: 'Tento rozpočet nemůžete změnit.',
    budgetChanged: 'Rozpočet se od vašeho otevření změnil. Zkontrolujte limity a zkuste to znovu.',
    budgetUnknown:
      'Nepodařilo se potvrdit, zda byl rozpočet uložen. Zkuste to znovu; změna se nepoužije dvakrát.',
    scheduleFacts: 'Spouští se {schedule} ({zone})',
    scheduleNext: 'Další spuštění {next}',
    scheduleOn: 'Zapnuto',
    scheduleNotScheduled: 'Nenaplánováno',
    scheduleEnable: 'Zapnout',
    scheduleUnavailable: 'Plán momentálně není k dispozici.',
    scheduleDenied: 'Tento plán nemůžete změnit.',
    scheduleConflict: 'Plán se od vašeho otevření změnil. Zkontrolujte ho a zkuste to znovu.',
    scheduleUnknown:
      'Nepodařilo se ověřit, zda se plán změnil. Zkuste to znovu; změna se nepoužije dvakrát.',
    budgetHeading: 'Rozpočet',
    scheduleOnlyHeading: 'Plán',
    scheduleJobOrdinal: 'Plánované spuštění {n}',
    budgetUnconfirmed: 'Nepodařilo se ověřit, zda byl rozpočet uložen.',
    scheduleUnconfirmed: 'Nepodařilo se ověřit, zda se plán změnil.',
    scheduleTimeNote: 'Časy platí v časovém pásmu plánu.',
    scheduleWeekdays: 'Spouští se ve všední dny v {time}',
    scheduleWeekly: 'Spouští se každý týden: {days} v {time}',
    scheduleCustom: 'Spouští se podle vlastního plánu',
    scheduleThen: 'Poté {runs}',
    scheduleChange: 'Změnit plán',
    scheduleChoose: 'Zvolit jiný čas',
    pickerHeading: 'Kdy se spouští',
    presetEvery15: 'Každých 15 minut',
    presetEvery30: 'Každých 30 minut',
    presetHourly: 'Každou hodinu',
    presetDaily: 'Každý den',
    presetWeekdays: 'Všední dny',
    presetWeekly: 'Každý týden',
    presetCustom: 'Vlastní',
    pickerTime: 'Čas',
    pickerDay: 'Den',
    pickerCron: 'Výraz cron',
    pickerCronHelp:
      'Pět polí: minuta, hodina, den v měsíci, měsíc a den v týdnu, například 0 9 1 * * znamená 9:00 prvního dne každého měsíce.',
    pickerCustomPreview: 'Při uložení se plán zkontroluje a zobrazí se další spuštění.',
    pickerCancel: 'Zrušit',
    scheduleTooFrequent: 'Spuštění mohou probíhat nejčastěji každých 15 minut.',
    scheduleInvalidChoice: 'Tento plán nelze použít. Zkontrolujte výraz a časové pásmo.',
    scheduleBusy: 'Spuštění právě probíhá. Zkuste to znovu, až skončí.',
    runsHeading: 'Poslední spuštění',
    runsEmpty: 'Zatím žádná spuštění.',
    runsUnavailable: 'Poslední spuštění teď nejsou k dispozici.',
    runSucceeded: 'Úspěšné',
    runFailed: 'Neúspěšné',
    runUnknown: 'Nepotvrzeno',
    runManual: 'Spuštěno ručně',
    runScheduled: 'Naplánováno',
    runWebhook: 'Webhook',
    runEvent: 'Změna dat',
    runShowLog: 'Zobrazit log',
    runHideLog: 'Skrýt log',
    runLogEmpty: 'Nic nevypsala.',
    runLogDropped: '{n} dřívějších řádků nebylo zachováno.',
    runError: 'Chyba',
    runPrinted: 'Co vypsala',
    runInput: 'Vstup',
    runLogUnavailable: 'Log teď není k dispozici.',
    runFix: 'Opravit v chatu',
    runFixFailed: 'Chat aplikace se nepodařilo otevřít. Zkuste to znovu.',
    runsMore: 'Načíst další',
    fixScheduled: 'Naplánované spuštění této aplikace selhalo. Najdi příčinu a oprav to.',
    fixTriggered: 'Spuštění této aplikace selhalo. Najdi příčinu a oprav to.',
    fixUnknown:
      'Spuštění této aplikace skončilo bez potvrzeného výsledku. Zkontroluj, co se stalo, a oprav to.',
    fixRunLine: 'Funkce {name} · spuštění {id} · {time}',
    fixError: 'Chyba',
    fixPrinted: 'Posledních vypsaných řádků',
    fixInput: 'Její vstup',
  },
  da: {
    sourcesHeading: 'Hvad skal spores',
    sourcesNote:
      'Kilder kommer fra certificerede Tool Store-pakker, og manglende kilder tilføjes efter anmodning.',
    chooseSource: 'Vælg {name}',
    readsLine: 'Læser {fields}',
    partialLine: 'Læser {fields}. Endnu ikke: {gaps}.',
    awaitingLine: 'Understøttes ikke endnu. Manglende kilder tilføjes efter anmodning.',
    requestIt: 'Anmod om det',
    requestedLine: 'Anmodet {date} · vi fortæller dig, når det er klar.',
    needsConnection: 'Kræver {whose} {system}-forbindelse',
    connect: 'Forbind {system}',
    askOwner: 'Spørg {owner}',
    coverageSummary: '{n} kilder indsamles nu; {m} kommer med, når de understøttes.',
    coverageSummaryOne: '{n} kilde indsamles nu; {m} kommer med, når de understøttes.',
    scheduleHeading: 'Tidsplan og budget',
    scheduleTime: 'Kører hver dag kl.',
    scheduleDaily: 'Kører hver dag kl. {time}',
    scheduleEveryHour: 'Kører hver time',
    scheduleEveryMinutes: 'Kører hvert {minutes}. minut',
    scheduleTimezone: 'Tidszone',
    budgetPerRun: 'Maksimalt forbrug pr. kørsel',
    budgetMonthly: 'Maksimalt forbrug pr. måned',
    scheduleSave: 'Gem tidsplan',
    scheduleSaving: 'Gemmer…',
    scheduleSummary:
      'Dagligt kl. {time} {zone} · op til {perRun} pr. kørsel · op til {monthly} pr. måned',
    oneRunPerDay:
      'Én kørsel om dagen. En gentaget eller forsøgt igen-kørsel tæller aldrig dobbelt.',
    errTime: 'Angiv et tidspunkt som 07:00.',
    errCredits: 'Angiv et helt tal fra {min} til {max}.',
    errOrder: 'Månedsgrænsen skal mindst svare til grænsen for én kørsel.',
    collecting: 'Indsamler dagligt kl. {time} · senest læst {last} · {rows} rækker',
    collectingNever: 'Indsamler dagligt kl. {time} · ikke læst endnu',
    awaitingData: 'Anmodet {date} · indsamler ikke endnu',
    stopped: 'Stoppet: {reason}',
    reconnect: 'Opret forbindelse igen',
    skippedBudget: 'Sprang {skipped} af {total} kategorier over i dag: månedsbudgettet er nået.',
    gapMarker: 'Ingen data for denne periode',
    gapBudget: 'Sprunget over: månedsbudgettet er nået',
    gapUnsupported: 'Sprunget over: understøttes ikke endnu',
    gapFailed: 'Sprunget over: kilden svarede ikke',
    chartLabel: 'Pris over tid. En sprunget periode er et brud i linjen, ikke nul.',
    firstRunTitle: 'Første kørsel gennemført',
    firstRun:
      'Læste {read} kilder · {unsupported} understøttes ikke endnu · pris {cost} · næste kørsel {next}',
    firstRunUnmeasured:
      'Læste {read} kilder · {unsupported} understøttes ikke endnu · pris endnu ikke målt · næste kørsel {next}',
    firstRunNoNext:
      'Læste {read} kilder · {unsupported} understøttes ikke endnu · pris {cost} · ingen kørsel er planlagt',
    lastRunNone: 'Ingen kørsel endnu. Den første kører på det planlagte tidspunkt.',
    paused: 'Sat på pause',
    pause: 'Sæt på pause',
    resume: 'Genoptag',
    historyFrom: 'Historik fra {source} siden {since} (datoer fra kilden)',
    historyImportedMark: 'Importeret',
    historyToday: 'Historikken starter i dag, {date}.',
    budgetSave: 'Gem budget',
    creditsUnit: 'kreditter',
    budgetInvalid: 'Disse grænser kan ikke gemmes. Tjek dem, og prøv igen.',
    budgetSummary: 'Op til {perRun} pr. kørsel · op til {monthly} pr. måned',
    budgetNone: 'Der er ikke angivet noget budget endnu.',
    budgetUnavailable: 'Budgettet er ikke tilgængeligt lige nu.',
    budgetDenied: 'Du kan ikke ændre dette budget.',
    budgetChanged: 'Budgettet blev ændret, efter du åbnede dette. Tjek grænserne, og prøv igen.',
    budgetUnknown:
      'Vi kunne ikke bekræfte, om det blev gemt. Prøv igen; det bliver ikke anvendt to gange.',
    scheduleFacts: 'Kører {schedule} ({zone})',
    scheduleNext: 'Næste kørsel {next}',
    scheduleOn: 'Til',
    scheduleNotScheduled: 'Ikke planlagt',
    scheduleEnable: 'Slå til',
    scheduleUnavailable: 'Tidsplanen er ikke tilgængelig lige nu.',
    scheduleDenied: 'Du kan ikke ændre denne tidsplan.',
    scheduleConflict: 'Tidsplanen blev ændret, siden du åbnede den. Kontrollér den, og prøv igen.',
    scheduleUnknown:
      'Vi kunne ikke bekræfte, om tidsplanen blev ændret. Prøv igen; den bliver ikke anvendt to gange.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Tidsplan',
    scheduleJobOrdinal: 'Planlagt kørsel {n}',
    budgetUnconfirmed: 'Vi kunne ikke bekræfte, om det blev gemt.',
    scheduleUnconfirmed: 'Vi kunne ikke bekræfte, om tidsplanen blev ændret.',
    scheduleTimeNote: 'Tidspunkter angives i tidsplanens egen tidszone.',
    scheduleWeekdays: 'Kører på hverdage kl. {time}',
    scheduleWeekly: 'Kører hver {days} kl. {time}',
    scheduleCustom: 'Kører efter sin egen tidsplan',
    scheduleThen: 'Derefter {runs}',
    scheduleChange: 'Skift tidsplan',
    scheduleChoose: 'Vælg et andet tidspunkt',
    pickerHeading: 'Hvornår den kører',
    presetEvery15: 'Hvert 15. minut',
    presetEvery30: 'Hvert 30. minut',
    presetHourly: 'Hver time',
    presetDaily: 'Hver dag',
    presetWeekdays: 'Hverdage',
    presetWeekly: 'Hver uge',
    presetCustom: 'Brugerdefineret',
    pickerTime: 'Tidspunkt',
    pickerDay: 'Dag',
    pickerCron: 'Cron-udtryk',
    pickerCronHelp:
      'Fem felter: minut, time, dag i måneden, måned og ugedag, for eksempel 0 9 1 * * for kl. 09:00 den første i hver måned.',
    pickerCustomPreview: 'Ved gemning tjekkes denne tidsplan, og de næste kørsler vises.',
    pickerCancel: 'Annuller',
    scheduleTooFrequent: 'Kørsler kan højst ske hvert 15. minut.',
    scheduleInvalidChoice: 'Den tidsplan kan ikke bruges. Tjek udtrykket og tidszonen.',
    scheduleBusy: 'En kørsel er i gang. Prøv igen, når den er færdig.',
    runsHeading: 'Seneste kørsler',
    runsEmpty: 'Ingen kørsler endnu.',
    runsUnavailable: 'Seneste kørsler er ikke tilgængelige lige nu.',
    runSucceeded: 'Lykkedes',
    runFailed: 'Mislykkedes',
    runUnknown: 'Ubekræftet',
    runManual: 'Kørt manuelt',
    runScheduled: 'Planlagt',
    runWebhook: 'Webhook',
    runEvent: 'Dataændring',
    runShowLog: 'Vis log',
    runHideLog: 'Skjul log',
    runLogEmpty: 'Den udskrev ingenting.',
    runLogDropped: '{n} tidligere linjer blev ikke gemt.',
    runError: 'Fejl',
    runPrinted: 'Hvad den udskrev',
    runInput: 'Input',
    runLogUnavailable: 'Loggen er ikke tilgængelig lige nu.',
    runFix: 'Ret i chat',
    runFixFailed: 'Appens chat kunne ikke åbnes. Prøv igen.',
    runsMore: 'Indlæs flere',
    fixScheduled: 'Denne apps planlagte kørsel mislykkedes. Find årsagen, og ret den.',
    fixTriggered: 'Denne apps kørsel mislykkedes. Find årsagen, og ret den.',
    fixUnknown:
      'Denne apps kørsel sluttede uden et bekræftet resultat. Undersøg, hvad der skete, og ret det.',
    fixRunLine: 'Funktion {name} · kørsel {id} · {time}',
    fixError: 'Fejl',
    fixPrinted: 'De sidste linjer, den udskrev',
    fixInput: 'Dens input',
  },
  de: {
    sourcesHeading: 'Was verfolgt werden soll',
    sourcesNote:
      'Quellen stammen aus zertifizierten Tool-Store-Paketen. Fehlende Quellen werden auf Anfrage hinzugefügt.',
    chooseSource: '{name} auswählen',
    readsLine: 'Liest {fields}',
    partialLine: 'Liest {fields}. Noch nicht: {gaps}.',
    awaitingLine: 'Noch nicht unterstützt. Fehlende Quellen werden auf Anfrage hinzugefügt.',
    requestIt: 'Anfordern',
    requestedLine: 'Angefordert am {date} · wir informieren Sie, sobald es bereit ist.',
    needsConnection: 'Benötigt die Verbindung von {whose} zu {system}',
    connect: '{system} verbinden',
    askOwner: '{owner} fragen',
    coverageSummary:
      '{n} Quellen sammeln jetzt Daten; {m} kommen hinzu, sobald sie unterstützt werden.',
    coverageSummaryOne:
      '{n} Quelle sammelt jetzt Daten; {m} kommen hinzu, sobald sie unterstützt werden.',
    scheduleHeading: 'Zeitplan und Budget',
    scheduleTime: 'Täglich um',
    scheduleDaily: 'Läuft täglich um {time}',
    scheduleEveryHour: 'Läuft stündlich',
    scheduleEveryMinutes: 'Läuft alle {minutes} Minuten',
    scheduleTimezone: 'Zeitzone',
    budgetPerRun: 'Maximale Ausgaben pro Ausführung',
    budgetMonthly: 'Maximale Ausgaben pro Monat',
    scheduleSave: 'Zeitplan speichern',
    scheduleSaving: 'Wird gespeichert…',
    scheduleSummary:
      'Täglich um {time} {zone} · bis zu {perRun} pro Ausführung · bis zu {monthly} pro Monat',
    oneRunPerDay:
      'Eine Ausführung pro Tag. Eine wiederholte oder erneut gestartete Ausführung wird nie doppelt gezählt.',
    errTime: 'Geben Sie eine Uhrzeit wie 07:00 ein.',
    errCredits: 'Geben Sie eine ganze Zahl von {min} bis {max} ein.',
    errOrder: 'Das Monatslimit muss mindestens so hoch sein wie das Limit pro Ausführung.',
    collecting: 'Tägliche Erfassung um {time} · zuletzt gelesen {last} · {rows} Zeilen',
    collectingNever: 'Tägliche Erfassung um {time} · noch nicht gelesen',
    awaitingData: 'Angefordert am {date} · Erfassung noch nicht gestartet',
    stopped: 'Angehalten: {reason}',
    reconnect: 'Erneut verbinden',
    skippedBudget:
      'Heute wurden {skipped} von {total} Kategorien übersprungen: Das Monatsbudget ist ausgeschöpft.',
    gapMarker: 'Keine Daten für diesen Zeitraum',
    gapBudget: 'Übersprungen: Monatsbudget ausgeschöpft',
    gapUnsupported: 'Übersprungen: noch nicht unterstützt',
    gapFailed: 'Übersprungen: Die Quelle hat nicht geantwortet',
    chartLabel:
      'Preisverlauf. Ein übersprungener Zeitraum ist eine Unterbrechung der Linie, nicht null.',
    firstRunTitle: 'Erste Ausführung abgeschlossen',
    firstRun:
      '{read} Quellen gelesen · {unsupported} noch nicht unterstützt · Kosten {cost} · nächste Ausführung {next}',
    firstRunUnmeasured:
      '{read} Quellen gelesen · {unsupported} noch nicht unterstützt · Kosten noch nicht gemessen · nächste Ausführung {next}',
    firstRunNoNext:
      '{read} Quellen gelesen · {unsupported} noch nicht unterstützt · Kosten {cost} · keine Ausführung geplant',
    lastRunNone: 'Noch keine Ausführung. Die erste läuft zur geplanten Zeit.',
    paused: 'Pausiert',
    pause: 'Pausieren',
    resume: 'Fortsetzen',
    historyFrom: 'Verlauf von {source} seit {since} (Daten aus der Quelle)',
    historyImportedMark: 'Importiert',
    historyToday: 'Der Verlauf beginnt heute, am {date}.',
    budgetSave: 'Budget speichern',
    creditsUnit: 'Credits',
    budgetInvalid:
      'Diese Limits können nicht gespeichert werden. Prüfen Sie sie und versuchen Sie es erneut.',
    budgetSummary: 'Bis zu {perRun} pro Ausführung · bis zu {monthly} pro Monat',
    budgetNone: 'Noch kein Budget festgelegt.',
    budgetUnavailable: 'Das Budget ist derzeit nicht verfügbar.',
    budgetDenied: 'Sie können dieses Budget nicht ändern.',
    budgetChanged:
      'Das Budget wurde geändert, seit Sie diese Seite geöffnet haben. Prüfen Sie die Limits und versuchen Sie es erneut.',
    budgetUnknown:
      'Wir konnten nicht bestätigen, ob es gespeichert wurde. Versuchen Sie es erneut; es wird nicht doppelt angewendet.',
    scheduleFacts: 'Ausführungen {schedule} ({zone})',
    scheduleNext: 'Nächste Ausführung {next}',
    scheduleOn: 'Aktiv',
    scheduleNotScheduled: 'Nicht geplant',
    scheduleEnable: 'Einschalten',
    scheduleUnavailable: 'Der Zeitplan ist derzeit nicht verfügbar.',
    scheduleDenied: 'Sie können diesen Zeitplan nicht ändern.',
    scheduleConflict:
      'Der Zeitplan wurde geändert, seit Sie ihn geöffnet haben. Überprüfen Sie ihn und versuchen Sie es erneut.',
    scheduleUnknown:
      'Wir konnten nicht bestätigen, ob der Zeitplan geändert wurde. Versuchen Sie es erneut; er wird nicht doppelt angewendet.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Zeitplan',
    scheduleJobOrdinal: 'Geplante Ausführung {n}',
    budgetUnconfirmed: 'Wir konnten nicht bestätigen, ob es gespeichert wurde.',
    scheduleUnconfirmed: 'Wir konnten nicht bestätigen, ob der Zeitplan geändert wurde.',
    scheduleTimeNote: 'Die Zeiten gelten in der eigenen Zeitzone des Zeitplans.',
    scheduleWeekdays: 'Läuft werktags um {time}',
    scheduleWeekly: 'Läuft jeden {days} um {time}',
    scheduleCustom: 'Läuft nach eigenem Zeitplan',
    scheduleThen: 'Danach {runs}',
    scheduleChange: 'Zeitplan ändern',
    scheduleChoose: 'Andere Zeit wählen',
    pickerHeading: 'Wann es läuft',
    presetEvery15: 'Alle 15 Minuten',
    presetEvery30: 'Alle 30 Minuten',
    presetHourly: 'Jede Stunde',
    presetDaily: 'Jeden Tag',
    presetWeekdays: 'Werktags',
    presetWeekly: 'Jede Woche',
    presetCustom: 'Benutzerdefiniert',
    pickerTime: 'Uhrzeit',
    pickerDay: 'Tag',
    pickerCron: 'Cron-Ausdruck',
    pickerCronHelp:
      'Fünf Felder: Minute, Stunde, Tag des Monats, Monat und Wochentag, z. B. 0 9 1 * * für 09:00 Uhr am Ersten jedes Monats.',
    pickerCustomPreview:
      'Beim Speichern wird dieser Zeitplan geprüft und die nächsten Ausführungen werden angezeigt.',
    pickerCancel: 'Abbrechen',
    scheduleTooFrequent: 'Ausführungen sind höchstens alle 15 Minuten möglich.',
    scheduleInvalidChoice:
      'Dieser Zeitplan kann nicht verwendet werden. Prüfen Sie den Ausdruck und die Zeitzone.',
    scheduleBusy: 'Eine Ausführung läuft gerade. Versuchen Sie es nach ihrem Ende erneut.',
    runsHeading: 'Letzte Ausführungen',
    runsEmpty: 'Noch keine Ausführungen.',
    runsUnavailable: 'Letzte Ausführungen sind derzeit nicht verfügbar.',
    runSucceeded: 'Erfolgreich',
    runFailed: 'Fehlgeschlagen',
    runUnknown: 'Nicht bestätigt',
    runManual: 'Manuell ausgeführt',
    runScheduled: 'Geplant',
    runWebhook: 'Webhook',
    runEvent: 'Datenänderung',
    runShowLog: 'Protokoll anzeigen',
    runHideLog: 'Protokoll ausblenden',
    runLogEmpty: 'Es wurde nichts ausgegeben.',
    runLogDropped: '{n} frühere Zeilen wurden nicht gespeichert.',
    runError: 'Fehler',
    runPrinted: 'Was ausgegeben wurde',
    runInput: 'Eingabe',
    runLogUnavailable: 'Das Protokoll ist derzeit nicht verfügbar.',
    runFix: 'Im Chat beheben',
    runFixFailed: 'Der Chat der App konnte nicht geöffnet werden. Versuchen Sie es erneut.',
    runsMore: 'Mehr laden',
    fixScheduled:
      'Der geplante Lauf dieser App ist fehlgeschlagen. Bitte finden Sie die Ursache und beheben Sie sie.',
    fixTriggered:
      'Der Lauf dieser App ist fehlgeschlagen. Bitte finden Sie die Ursache und beheben Sie sie.',
    fixUnknown:
      'Der Lauf dieser App ist ohne bestätigtes Ergebnis zu Ende gegangen. Bitte prüfen Sie, was passiert ist, und beheben Sie es.',
    fixRunLine: 'Funktion {name} · Lauf {id} · {time}',
    fixError: 'Fehler',
    fixPrinted: 'Die letzten ausgegebenen Zeilen',
    fixInput: 'Die Eingabe',
  },
  el: {
    sourcesHeading: 'Τι θα παρακολουθείται',
    sourcesNote:
      'Οι πηγές προέρχονται από πιστοποιημένα πακέτα του Tool Store και όσες λείπουν προστίθενται κατόπιν αιτήματος.',
    chooseSource: 'Επιλέξτε {name}',
    readsLine: 'Διαβάζει {fields}',
    partialLine: 'Διαβάζει {fields}. Όχι ακόμη: {gaps}.',
    awaitingLine: 'Δεν υποστηρίζεται ακόμη. Οι πηγές που λείπουν προστίθενται κατόπιν αιτήματος.',
    requestIt: 'Ζητήστε το',
    requestedLine: 'Ζητήθηκε στις {date} · θα σας ενημερώσουμε όταν είναι έτοιμο.',
    needsConnection: 'Χρειάζεται τη σύνδεση {system} που ανήκει σε {whose}',
    connect: 'Σύνδεση {system}',
    askOwner: 'Ρωτήστε τον/την {owner}',
    coverageSummary: '{n} πηγές συλλέγουν δεδομένα τώρα· {m} θα προστεθούν όταν υποστηρίζονται.',
    coverageSummaryOne: '{n} πηγή συλλέγει δεδομένα τώρα· {m} θα προστεθούν όταν υποστηρίζονται.',
    scheduleHeading: 'Προγραμματισμός και προϋπολογισμός',
    scheduleTime: 'Εκτελείται καθημερινά στις',
    scheduleDaily: 'Εκτελείται καθημερινά στις {time}',
    scheduleEveryHour: 'Εκτελείται κάθε ώρα',
    scheduleEveryMinutes: 'Εκτελείται κάθε {minutes} λεπτά',
    scheduleTimezone: 'Ζώνη ώρας',
    budgetPerRun: 'Μέγιστη δαπάνη ανά εκτέλεση',
    budgetMonthly: 'Μέγιστη μηνιαία δαπάνη',
    scheduleSave: 'Αποθήκευση προγράμματος',
    scheduleSaving: 'Αποθήκευση…',
    scheduleSummary:
      'Καθημερινά στις {time} {zone} · έως {perRun} ανά εκτέλεση · έως {monthly} τον μήνα',
    oneRunPerDay:
      'Μία εκτέλεση την ημέρα. Μια επαναλαμβανόμενη ή επαναληφθείσα εκτέλεση δεν μετρά ποτέ δύο φορές.',
    errTime: 'Εισαγάγετε ώρα όπως 07:00.',
    errCredits: 'Εισαγάγετε έναν ακέραιο αριθμό από {min} έως {max}.',
    errOrder: 'Το μηνιαίο όριο πρέπει να είναι τουλάχιστον ίσο με το όριο μίας εκτέλεσης.',
    collecting: 'Συλλογή καθημερινά στις {time} · τελευταία ανάγνωση {last} · {rows} γραμμές',
    collectingNever: 'Συλλογή καθημερινά στις {time} · δεν έχει γίνει ακόμη ανάγνωση',
    awaitingData: 'Ζητήθηκε στις {date} · δεν συλλέγονται ακόμη δεδομένα',
    stopped: 'Διακόπηκε: {reason}',
    reconnect: 'Επανασύνδεση',
    skippedBudget:
      'Παραλείφθηκαν {skipped} από {total} κατηγορίες σήμερα: ο μηνιαίος προϋπολογισμός εξαντλήθηκε.',
    gapMarker: 'Δεν υπάρχουν δεδομένα για αυτή την περίοδο',
    gapBudget: 'Παραλείφθηκε: ο μηνιαίος προϋπολογισμός εξαντλήθηκε',
    gapUnsupported: 'Παραλείφθηκε: δεν υποστηρίζεται ακόμη',
    gapFailed: 'Παραλείφθηκε: η πηγή δεν απάντησε',
    chartLabel:
      'Τιμή με την πάροδο του χρόνου. Μια παραλειφθείσα περίοδος είναι διακοπή στη γραμμή, όχι μηδενική τιμή.',
    firstRunTitle: 'Η πρώτη εκτέλεση ολοκληρώθηκε',
    firstRun:
      'Αναγνώστηκαν {read} πηγές · {unsupported} δεν υποστηρίζονται ακόμη · κόστος {cost} · επόμενη εκτέλεση {next}',
    firstRunUnmeasured:
      'Αναγνώστηκαν {read} πηγές · {unsupported} δεν υποστηρίζονται ακόμη · το κόστος δεν έχει μετρηθεί ακόμη · επόμενη εκτέλεση {next}',
    firstRunNoNext:
      'Αναγνώστηκαν {read} πηγές · {unsupported} δεν υποστηρίζονται ακόμη · κόστος {cost} · δεν έχει προγραμματιστεί εκτέλεση',
    lastRunNone: 'Δεν έχει γίνει ακόμη εκτέλεση. Η πρώτη θα γίνει την προγραμματισμένη ώρα.',
    paused: 'Σε παύση',
    pause: 'Παύση',
    resume: 'Συνέχιση',
    historyFrom: 'Ιστορικό από {source} από {since} (ημερομηνίες από την πηγή)',
    historyImportedMark: 'Εισήχθη',
    historyToday: 'Το ιστορικό ξεκινά σήμερα, {date}.',
    budgetSave: 'Αποθήκευση προϋπολογισμού',
    creditsUnit: 'πιστώσεις',
    budgetInvalid: 'Αυτά τα όρια δεν μπορούν να αποθηκευτούν. Ελέγξτε τα και δοκιμάστε ξανά.',
    budgetSummary: 'Έως {perRun} ανά εκτέλεση · έως {monthly} τον μήνα',
    budgetNone: 'Δεν έχει οριστεί ακόμη προϋπολογισμός.',
    budgetUnavailable: 'Ο προϋπολογισμός δεν είναι διαθέσιμος αυτή τη στιγμή.',
    budgetDenied: 'Δεν μπορείτε να αλλάξετε αυτόν τον προϋπολογισμό.',
    budgetChanged:
      'Ο προϋπολογισμός άλλαξε από τότε που ανοίξατε αυτή τη σελίδα. Ελέγξτε τα όρια και προσπαθήστε ξανά.',
    budgetUnknown:
      'Δεν μπορέσαμε να επιβεβαιώσουμε αν αποθηκεύτηκε. Προσπαθήστε ξανά· δεν θα εφαρμοστεί δύο φορές.',
    scheduleFacts: 'Εκτελείται {schedule} ({zone})',
    scheduleNext: 'Επόμενη εκτέλεση {next}',
    scheduleOn: 'Ενεργό',
    scheduleNotScheduled: 'Δεν έχει προγραμματιστεί',
    scheduleEnable: 'Ενεργοποίηση',
    scheduleUnavailable: 'Το πρόγραμμα δεν είναι διαθέσιμο αυτήν τη στιγμή.',
    scheduleDenied: 'Δεν μπορείτε να αλλάξετε αυτό το πρόγραμμα.',
    scheduleConflict:
      'Το πρόγραμμα άλλαξε από τότε που το ανοίξατε. Ελέγξτε το και δοκιμάστε ξανά.',
    scheduleUnknown:
      'Δεν μπορέσαμε να επιβεβαιώσουμε αν το πρόγραμμα άλλαξε. Δοκιμάστε ξανά· δεν θα εφαρμοστεί δύο φορές.',
    budgetHeading: 'Προϋπολογισμός',
    scheduleOnlyHeading: 'Πρόγραμμα',
    scheduleJobOrdinal: 'Προγραμματισμένη εκτέλεση {n}',
    budgetUnconfirmed: 'Δεν μπορέσαμε να επιβεβαιώσουμε αν αποθηκεύτηκε.',
    scheduleUnconfirmed: 'Δεν μπορέσαμε να επιβεβαιώσουμε αν το πρόγραμμα άλλαξε.',
    scheduleTimeNote: 'Οι ώρες ακολουθούν τη ζώνη ώρας του προγράμματος.',
    scheduleWeekdays: 'Εκτελείται τις καθημερινές στις {time}',
    scheduleWeekly: 'Εκτελείται εβδομαδιαία: {days} στις {time}',
    scheduleCustom: 'Εκτελείται με δικό του πρόγραμμα',
    scheduleThen: 'Έπειτα {runs}',
    scheduleChange: 'Αλλαγή προγράμματος',
    scheduleChoose: 'Επιλογή άλλης ώρας',
    pickerHeading: 'Πότε εκτελείται',
    presetEvery15: 'Κάθε 15 λεπτά',
    presetEvery30: 'Κάθε 30 λεπτά',
    presetHourly: 'Κάθε ώρα',
    presetDaily: 'Κάθε μέρα',
    presetWeekdays: 'Καθημερινές',
    presetWeekly: 'Κάθε εβδομάδα',
    presetCustom: 'Προσαρμοσμένο',
    pickerTime: 'Ώρα',
    pickerDay: 'Ημέρα',
    pickerCron: 'Έκφραση cron',
    pickerCronHelp:
      'Πέντε πεδία: λεπτό, ώρα, ημέρα του μήνα, μήνας και ημέρα της εβδομάδας, για παράδειγμα 0 9 1 * * για τις 09:00 την πρώτη κάθε μήνα.',
    pickerCustomPreview:
      'Η αποθήκευση ελέγχει αυτό το πρόγραμμα και δείχνει τις επόμενες εκτελέσεις του.',
    pickerCancel: 'Ακύρωση',
    scheduleTooFrequent: 'Οι εκτελέσεις μπορούν να γίνονται το πολύ κάθε 15 λεπτά.',
    scheduleInvalidChoice:
      'Αυτό το πρόγραμμα δεν μπορεί να χρησιμοποιηθεί. Ελέγξτε την έκφραση και τη ζώνη ώρας.',
    scheduleBusy: 'Μια εκτέλεση βρίσκεται σε εξέλιξη. Δοκιμάστε ξανά όταν ολοκληρωθεί.',
    runsHeading: 'Πρόσφατες εκτελέσεις',
    runsEmpty: 'Δεν υπάρχουν ακόμη εκτελέσεις.',
    runsUnavailable: 'Οι πρόσφατες εκτελέσεις δεν είναι διαθέσιμες αυτή τη στιγμή.',
    runSucceeded: 'Επιτυχία',
    runFailed: 'Αποτυχία',
    runUnknown: 'Ανεπιβεβαίωτη',
    runManual: 'Χειροκίνητη εκτέλεση',
    runScheduled: 'Προγραμματισμένη',
    runWebhook: 'Webhook',
    runEvent: 'Αλλαγή δεδομένων',
    runShowLog: 'Εμφάνιση καταγραφής',
    runHideLog: 'Απόκρυψη καταγραφής',
    runLogEmpty: 'Δεν εκτύπωσε τίποτα.',
    runLogDropped: '{n} προηγούμενες γραμμές δεν διατηρήθηκαν.',
    runError: 'Σφάλμα',
    runPrinted: 'Τι εκτύπωσε',
    runInput: 'Είσοδος',
    runLogUnavailable: 'Η καταγραφή δεν είναι διαθέσιμη αυτή τη στιγμή.',
    runFix: 'Διόρθωση στη συνομιλία',
    runFixFailed: 'Δεν ήταν δυνατό το άνοιγμα της συνομιλίας της Εφαρμογής. Δοκιμάστε ξανά.',
    runsMore: 'Φόρτωση περισσότερων',
    fixScheduled:
      'Η προγραμματισμένη εκτέλεση αυτής της Εφαρμογής απέτυχε. Βρείτε την αιτία και διορθώστε την.',
    fixTriggered: 'Η εκτέλεση αυτής της Εφαρμογής απέτυχε. Βρείτε την αιτία και διορθώστε την.',
    fixUnknown:
      'Η εκτέλεση αυτής της Εφαρμογής ολοκληρώθηκε χωρίς επιβεβαιωμένο αποτέλεσμα. Ελέγξτε τι συνέβη και διορθώστε το.',
    fixRunLine: 'Λειτουργία {name} · εκτέλεση {id} · {time}',
    fixError: 'Σφάλμα',
    fixPrinted: 'Οι τελευταίες γραμμές που εκτύπωσε',
    fixInput: 'Η είσοδός της',
  },
  es: {
    sourcesHeading: 'Qué seguir',
    sourcesNote:
      'Las fuentes proceden de paquetes certificados de Tool Store; las que falten se añadirán previa solicitud.',
    chooseSource: 'Elegir {name}',
    readsLine: 'Lee {fields}',
    partialLine: 'Lee {fields}. Aún no: {gaps}.',
    awaitingLine: 'Todavía no es compatible. Las fuentes que falten se añadirán previa solicitud.',
    requestIt: 'Solicitar',
    requestedLine: 'Solicitado el {date} · te avisaremos cuando esté listo.',
    needsConnection: 'Necesita una conexión de {system} de {whose}',
    connect: 'Conectar {system}',
    askOwner: 'Preguntar a {owner}',
    coverageSummary: 'Ahora se recopilan {n} fuentes; {m} se añadirán cuando sean compatibles.',
    coverageSummaryOne: 'Ahora se recopila {n} fuente; {m} se añadirán cuando sean compatibles.',
    scheduleHeading: 'Programación y presupuesto',
    scheduleTime: 'Se ejecuta todos los días a las',
    scheduleDaily: 'Se ejecuta todos los días a las {time}',
    scheduleEveryHour: 'Se ejecuta cada hora',
    scheduleEveryMinutes: 'Se ejecuta cada {minutes} minutos',
    scheduleTimezone: 'Zona horaria',
    budgetPerRun: 'Máximo para gastar en una ejecución',
    budgetMonthly: 'Máximo para gastar en un mes',
    scheduleSave: 'Guardar programación',
    scheduleSaving: 'Guardando…',
    scheduleSummary:
      'Diariamente a las {time} {zone} · hasta {perRun} por ejecución · hasta {monthly} al mes',
    oneRunPerDay:
      'Una ejecución al día. Una ejecución repetida o reintentada nunca cuenta dos veces.',
    errTime: 'Introduce una hora como 07:00.',
    errCredits: 'Introduce un número entero de {min} a {max}.',
    errOrder: 'El límite mensual debe ser como mínimo igual al límite de una ejecución.',
    collecting: 'Recopilando diariamente a las {time} · última lectura {last} · {rows} filas',
    collectingNever: 'Recopilando diariamente a las {time} · aún no se ha leído',
    awaitingData: 'Solicitado el {date} · aún no se recopila',
    stopped: 'Detenido: {reason}',
    reconnect: 'Volver a conectar',
    skippedBudget:
      'Hoy se han omitido {skipped} de {total} categorías: se ha alcanzado el presupuesto mensual.',
    gapMarker: 'No hay datos para este periodo',
    gapBudget: 'Omitido: se ha alcanzado el presupuesto mensual',
    gapUnsupported: 'Omitido: todavía no es compatible',
    gapFailed: 'Omitido: la fuente no respondió',
    chartLabel:
      'Precio a lo largo del tiempo. Un periodo omitido crea una interrupción en la línea, no un valor cero.',
    firstRunTitle: 'Primera ejecución completada',
    firstRun:
      'Se han leído {read} fuentes · {unsupported} todavía no son compatibles · coste {cost} · próxima ejecución {next}',
    firstRunUnmeasured:
      'Se han leído {read} fuentes · {unsupported} todavía no son compatibles · coste aún no medido · próxima ejecución {next}',
    firstRunNoNext:
      'Se han leído {read} fuentes · {unsupported} todavía no son compatibles · coste {cost} · no hay ninguna ejecución programada',
    lastRunNone: 'Aún no hay ninguna ejecución. La primera se realizará a la hora programada.',
    paused: 'En pausa',
    pause: 'Pausar',
    resume: 'Reanudar',
    historyFrom: 'Historial de {source} desde {since} (fechas de la fuente)',
    historyImportedMark: 'Importado',
    historyToday: 'El historial comienza hoy, {date}.',
    budgetSave: 'Guardar presupuesto',
    creditsUnit: 'créditos',
    budgetInvalid: 'Estos límites no se pueden guardar. Revísalos e inténtalo de nuevo.',
    budgetSummary: 'Hasta {perRun} por ejecución · hasta {monthly} al mes',
    budgetNone: 'Aún no se ha establecido ningún presupuesto.',
    budgetUnavailable: 'El presupuesto no está disponible en este momento.',
    budgetDenied: 'No puedes cambiar este presupuesto.',
    budgetChanged:
      'El presupuesto ha cambiado desde que abriste esto. Comprueba los límites e inténtalo de nuevo.',
    budgetUnknown:
      'No pudimos confirmar si se ha guardado. Inténtalo de nuevo; no se aplicará dos veces.',
    scheduleFacts: 'Se ejecuta {schedule} ({zone})',
    scheduleNext: 'Próxima ejecución {next}',
    scheduleOn: 'Activado',
    scheduleNotScheduled: 'No programado',
    scheduleEnable: 'Activar',
    scheduleUnavailable: 'El calendario no está disponible en este momento.',
    scheduleDenied: 'No puedes cambiar este calendario.',
    scheduleConflict:
      'El calendario ha cambiado desde que lo abriste. Compruébalo e inténtalo de nuevo.',
    scheduleUnknown:
      'No hemos podido confirmar si el calendario ha cambiado. Inténtalo de nuevo; no se aplicará dos veces.',
    budgetHeading: 'Presupuesto',
    scheduleOnlyHeading: 'Calendario',
    scheduleJobOrdinal: 'Ejecución programada {n}',
    budgetUnconfirmed: 'No hemos podido confirmar si se ha guardado.',
    scheduleUnconfirmed: 'No hemos podido confirmar si el calendario ha cambiado.',
    scheduleTimeNote: 'Las horas están en la zona horaria de la propia programación.',
    scheduleWeekdays: 'Se ejecuta los días laborables a las {time}',
    scheduleWeekly: 'Se ejecuta cada {days} a las {time}',
    scheduleCustom: 'Se ejecuta con su propia programación',
    scheduleThen: 'Después {runs}',
    scheduleChange: 'Cambiar programación',
    scheduleChoose: 'Elegir otra hora',
    pickerHeading: 'Cuándo se ejecuta',
    presetEvery15: 'Cada 15 minutos',
    presetEvery30: 'Cada 30 minutos',
    presetHourly: 'Cada hora',
    presetDaily: 'Cada día',
    presetWeekdays: 'Días laborables',
    presetWeekly: 'Cada semana',
    presetCustom: 'Personalizado',
    pickerTime: 'Hora',
    pickerDay: 'Día',
    pickerCron: 'Expresión cron',
    pickerCronHelp:
      'Cinco campos: minuto, hora, día del mes, mes y día de la semana, por ejemplo 0 9 1 * * para las 09:00 del primer día de cada mes.',
    pickerCustomPreview:
      'Al guardar se comprueba esta programación y se muestran sus próximas ejecuciones.',
    pickerCancel: 'Cancelar',
    scheduleTooFrequent: 'Las ejecuciones pueden ser como máximo cada 15 minutos.',
    scheduleInvalidChoice:
      'No se puede usar esa programación. Comprueba la expresión y la zona horaria.',
    scheduleBusy: 'Hay una ejecución en curso. Inténtalo de nuevo cuando termine.',
    runsHeading: 'Ejecuciones recientes',
    runsEmpty: 'Aún no hay ejecuciones.',
    runsUnavailable: 'Las ejecuciones recientes no están disponibles en este momento.',
    runSucceeded: 'Correcta',
    runFailed: 'Fallida',
    runUnknown: 'Sin confirmar',
    runManual: 'Manual',
    runScheduled: 'Programada',
    runWebhook: 'Webhook',
    runEvent: 'Cambio de datos',
    runShowLog: 'Mostrar registro',
    runHideLog: 'Ocultar registro',
    runLogEmpty: 'No imprimió nada.',
    runLogDropped: 'No se conservaron {n} líneas anteriores.',
    runError: 'Error',
    runPrinted: 'Lo que imprimió',
    runInput: 'Entrada',
    runLogUnavailable: 'El registro no está disponible en este momento.',
    runFix: 'Corregir en el chat',
    runFixFailed: 'No se pudo abrir el chat de la app. Inténtalo de nuevo.',
    runsMore: 'Cargar más',
    fixScheduled: 'Falló la ejecución programada de esta app. Busca la causa y corrígela.',
    fixTriggered: 'Falló una ejecución de esta app. Busca la causa y corrígela.',
    fixUnknown:
      'Una ejecución de esta app terminó sin un resultado confirmado. Comprueba qué pasó y corrígelo.',
    fixRunLine: 'Función {name} · ejecución {id} · {time}',
    fixError: 'Error',
    fixPrinted: 'Las últimas líneas que imprimió',
    fixInput: 'Su entrada',
  },
  et: {
    sourcesHeading: 'Mida jälgida',
    sourcesNote:
      'Allikad pärinevad sertifitseeritud tööriistapoe pakettidest ja puuduvad allikad lisatakse taotluse alusel.',
    chooseSource: 'Vali {name}',
    readsLine: 'Loeb: {fields}',
    partialLine: 'Loeb: {fields}. Veel puuduvad: {gaps}.',
    awaitingLine: 'Veel ei toetata. Puuduvad allikad lisatakse taotluse alusel.',
    requestIt: 'Taotle seda',
    requestedLine: 'Taotletud {date} · anname teada, kui see on valmis.',
    needsConnection: 'Vajab kasutaja {whose} {system} ühendust',
    connect: 'Ühenda {system}',
    askOwner: 'Küsi omanikult {owner}',
    coverageSummary: 'Praegu kogutakse {n} allikast; {m} lisanduvad, kui neid toetama hakatakse.',
    coverageSummaryOne: 'Praegu kogutakse {n} allikast; {m} lisandub, kui seda toetama hakatakse.',
    scheduleHeading: 'Ajakava ja eelarve',
    scheduleTime: 'Käivitub iga päev kell',
    scheduleDaily: 'Käivitub iga päev kell {time}',
    scheduleEveryHour: 'Käivitub iga tund',
    scheduleEveryMinutes: 'Käivitub iga {minutes} minuti järel',
    scheduleTimezone: 'Ajavöönd',
    budgetPerRun: 'Maksimaalne kulu ühe käivituse kohta',
    budgetMonthly: 'Maksimaalne kulu kuus',
    scheduleSave: 'Salvesta ajakava',
    scheduleSaving: 'Salvestamine…',
    scheduleSummary:
      'Iga päev kell {time} {zone} · kuni {perRun} käivituse kohta · kuni {monthly} kuus',
    oneRunPerDay:
      'Üks käivitus päevas. Korduv või uuesti proovitud käivitus ei lähe kunagi kaks korda arvesse.',
    errTime: 'Sisesta kellaaeg kujul 07:00.',
    errCredits: 'Sisesta täisarv vahemikus {min} kuni {max}.',
    errOrder: 'Kuu limiit peab olema vähemalt ühe käivituse limiidi suurune.',
    collecting: 'Kogumine iga päev kell {time} · viimati loetud {last} · {rows} rida',
    collectingNever: 'Kogumine iga päev kell {time} · veel lugemata',
    awaitingData: 'Taotletud {date} · kogumine pole veel alanud',
    stopped: 'Peatatud: {reason}',
    reconnect: 'Ühenda uuesti',
    skippedBudget: 'Täna jäeti vahele {skipped} kategooriat {total}-st: kuu eelarve on täis.',
    gapMarker: 'Selle perioodi andmed puuduvad',
    gapBudget: 'Vahele jäetud: kuu eelarve on täis',
    gapUnsupported: 'Vahele jäetud: veel ei toetata',
    gapFailed: 'Vahele jäetud: allikas ei vastanud',
    chartLabel: 'Hind ajas. Vahele jäetud periood katkestab joone, mitte ei tähista nulli.',
    firstRunTitle: 'Esimene käivitus tehtud',
    firstRun:
      'Loetud {read} allikat · {unsupported} pole veel toetatud · kulu {cost} · järgmine käivitus {next}',
    firstRunUnmeasured:
      'Loetud {read} allikat · {unsupported} pole veel toetatud · kulu pole veel mõõdetud · järgmine käivitus {next}',
    firstRunNoNext:
      'Loetud {read} allikat · {unsupported} pole veel toetatud · kulu {cost} · käivitust pole ajastatud',
    lastRunNone: 'Käivitust pole veel toimunud. Esimene käivitus toimub ajastatud ajal.',
    paused: 'Peatatud',
    pause: 'Peata',
    resume: 'Jätka',
    historyFrom: 'Ajalugu allikast {source} alates {since} (kuupäevad pärinevad allikast)',
    historyImportedMark: 'Imporditud',
    historyToday: 'Ajalugu algab täna, {date}.',
    budgetSave: 'Salvesta eelarve',
    creditsUnit: 'krediiti',
    budgetInvalid: 'Neid piiranguid ei saa salvestada. Kontrolli neid ja proovi uuesti.',
    budgetSummary: 'Kuni {perRun} käivituse kohta · kuni {monthly} kuus',
    budgetNone: 'Eelarvet pole veel määratud.',
    budgetUnavailable: 'Eelarve pole praegu saadaval.',
    budgetDenied: 'Sa ei saa seda eelarvet muuta.',
    budgetChanged: 'Eelarve muutus pärast selle avamist. Kontrolli limiite ja proovi uuesti.',
    budgetUnknown:
      'Me ei saanud kinnitada, kas see salvestati. Proovi uuesti; seda ei rakendata kaks korda.',
    scheduleFacts: 'Käivitub {schedule} ({zone})',
    scheduleNext: 'Järgmine käivitus {next}',
    scheduleOn: 'Sees',
    scheduleNotScheduled: 'Pole ajastatud',
    scheduleEnable: 'Lülita sisse',
    scheduleUnavailable: 'Ajakava pole praegu saadaval.',
    scheduleDenied: 'Te ei saa seda ajakava muuta.',
    scheduleConflict: 'Ajakava muutus pärast selle avamist. Kontrollige seda ja proovige uuesti.',
    scheduleUnknown:
      'Me ei saanud kinnitada, kas ajakava muutus. Proovige uuesti; seda ei rakendata kaks korda.',
    budgetHeading: 'Eelarve',
    scheduleOnlyHeading: 'Ajakava',
    scheduleJobOrdinal: 'Ajastatud käivitus {n}',
    budgetUnconfirmed: 'Me ei saanud kinnitada, kas see salvestati.',
    scheduleUnconfirmed: 'Me ei saanud kinnitada, kas ajakava muutus.',
    scheduleTimeNote: 'Kellaajad on ajakava enda ajavööndis.',
    scheduleWeekdays: 'Käivitub tööpäevadel kell {time}',
    scheduleWeekly: 'Käivitub iga {days} kell {time}',
    scheduleCustom: 'Käivitub oma ajakava järgi',
    scheduleThen: 'Seejärel {runs}',
    scheduleChange: 'Muuda ajakava',
    scheduleChoose: 'Vali muu kellaaeg',
    pickerHeading: 'Millal see käivitub',
    presetEvery15: 'Iga 15 minuti järel',
    presetEvery30: 'Iga 30 minuti järel',
    presetHourly: 'Iga tund',
    presetDaily: 'Iga päev',
    presetWeekdays: 'Tööpäevadel',
    presetWeekly: 'Iga nädal',
    presetCustom: 'Kohandatud',
    pickerTime: 'Kellaaeg',
    pickerDay: 'Päev',
    pickerCron: 'Cron-avaldis',
    pickerCronHelp:
      'Viis välja: minut, tund, kuu päev, kuu ja nädalapäev, näiteks 0 9 1 * * tähendab kella 09:00 iga kuu esimesel päeval.',
    pickerCustomPreview:
      'Salvestamisel kontrollitakse seda ajakava ja kuvatakse selle järgmised käivitused.',
    pickerCancel: 'Tühista',
    scheduleTooFrequent: 'Käivitusi võib olla kõige rohkem iga 15 minuti järel.',
    scheduleInvalidChoice: 'Seda ajakava ei saa kasutada. Kontrolli avaldist ja ajavööndit.',
    scheduleBusy: 'Käivitus on pooleli. Proovi uuesti, kui see on lõppenud.',
    runsHeading: 'Hiljutised käivitused',
    runsEmpty: 'Käivitusi veel pole.',
    runsUnavailable: 'Hiljutised käivitused pole praegu saadaval.',
    runSucceeded: 'Õnnestus',
    runFailed: 'Ebaõnnestus',
    runUnknown: 'Kinnitamata',
    runManual: 'Käivitatud käsitsi',
    runScheduled: 'Ajastatud',
    runWebhook: 'Veebikonks',
    runEvent: 'Andmete muutus',
    runShowLog: 'Näita logi',
    runHideLog: 'Peida logi',
    runLogEmpty: 'See ei väljastanud midagi.',
    runLogDropped: '{n} varasemat rida ei säilitatud.',
    runError: 'Viga',
    runPrinted: 'Mida see väljastas',
    runInput: 'Sisend',
    runLogUnavailable: 'Logi pole praegu saadaval.',
    runFix: 'Paranda vestluses',
    runFixFailed: 'Rakenduse vestlust ei saanud avada. Proovi uuesti.',
    runsMore: 'Laadi veel',
    fixScheduled: 'Selle rakenduse ajastatud käivitus ebaõnnestus. Leia põhjus ja paranda see.',
    fixTriggered: 'Selle rakenduse käivitus ebaõnnestus. Leia põhjus ja paranda see.',
    fixUnknown:
      'Selle rakenduse käivitus lõppes kinnitatud tulemuseta. Uuri, mis juhtus, ja paranda see.',
    fixRunLine: 'Funktsioon {name} · käivitus {id} · {time}',
    fixError: 'Viga',
    fixPrinted: 'Viimased väljastatud read',
    fixInput: 'Selle sisend',
  },
  fi: {
    sourcesHeading: 'Mitä seurataan',
    sourcesNote:
      'Lähteet ovat peräisin sertifioiduista Tool Store -paketeista, ja puuttuvat lähteet lisätään pyynnöstä.',
    chooseSource: 'Valitse {name}',
    readsLine: 'Lukee: {fields}',
    partialLine: 'Lukee: {fields}. Ei vielä: {gaps}.',
    awaitingLine: 'Ei vielä tuettu. Puuttuvat lähteet lisätään pyynnöstä.',
    requestIt: 'Pyydä sitä',
    requestedLine: 'Pyydetty {date} · ilmoitamme, kun se on valmis.',
    needsConnection: 'Tarvitsee {whose} {system}-yhteyden',
    connect: 'Yhdistä {system}',
    askOwner: 'Kysy omistajalta {owner}',
    coverageSummary: '{n} lähdettä kerää tietoja nyt; {m} liittyy mukaan, kun niitä tuetaan.',
    coverageSummaryOne: '{n} lähde kerää tietoja nyt; {m} liittyy mukaan, kun niitä tuetaan.',
    scheduleHeading: 'Aikataulu ja budjetti',
    scheduleTime: 'Suoritetaan päivittäin klo',
    scheduleDaily: 'Suoritetaan päivittäin klo {time}',
    scheduleEveryHour: 'Suoritetaan tunnin välein',
    scheduleEveryMinutes: 'Suoritetaan {minutes} minuutin välein',
    scheduleTimezone: 'Aikavyöhyke',
    budgetPerRun: 'Enimmäiskulutus yhdellä suorituskerralla',
    budgetMonthly: 'Enimmäiskulutus kuukaudessa',
    scheduleSave: 'Tallenna aikataulu',
    scheduleSaving: 'Tallennetaan…',
    scheduleSummary:
      'Päivittäin klo {time} {zone} · enintään {perRun} suorituskertaa kohden · enintään {monthly} kuukaudessa',
    oneRunPerDay:
      'Yksi suoritus päivässä. Toistettua tai uudelleen yritettyä suoritusta ei lasketa kahdesti.',
    errTime: 'Anna aika muodossa 07:00.',
    errCredits: 'Anna kokonaisluku väliltä {min}–{max}.',
    errOrder: 'Kuukausirajan on oltava vähintään yhden suorituskerran rajan suuruinen.',
    collecting: 'Kerätään päivittäin klo {time} · viimeisin luku {last} · {rows} riviä',
    collectingNever: 'Kerätään päivittäin klo {time} · ei luettu vielä',
    awaitingData: 'Pyydetty {date} · tietoja ei vielä kerätä',
    stopped: 'Pysäytetty: {reason}',
    reconnect: 'Yhdistä uudelleen',
    skippedBudget: 'Tänään ohitettiin {skipped}/{total} luokkaa: kuukausibudjetti on käytetty.',
    gapMarker: 'Ei tietoja tältä ajalta',
    gapBudget: 'Ohitettu: kuukausibudjetti käytetty',
    gapUnsupported: 'Ohitettu: ei vielä tuettu',
    gapFailed: 'Ohitettu: lähde ei vastannut',
    chartLabel: 'Hinta ajan mittaan. Ohitettu ajanjakso näkyy viivan katkeamisena, ei nollana.',
    firstRunTitle: 'Ensimmäinen suoritus valmis',
    firstRun:
      'Luettu {read} lähdettä · {unsupported} ei vielä tuettu · kustannus {cost} · seuraava suoritus {next}',
    firstRunUnmeasured:
      'Luettu {read} lähdettä · {unsupported} ei vielä tuettu · kustannusta ei ole vielä mitattu · seuraava suoritus {next}',
    firstRunNoNext:
      'Luettu {read} lähdettä · {unsupported} ei vielä tuettu · kustannus {cost} · suoritusta ei ole ajoitettu',
    lastRunNone: 'Suoritusta ei ole vielä tehty. Ensimmäinen suoritetaan aikataulun mukaisesti.',
    paused: 'Keskeytetty',
    pause: 'Keskeytä',
    resume: 'Jatka',
    historyFrom: 'Lähteen {source} historia alkaen {since} (päivämäärät ovat lähteestä)',
    historyImportedMark: 'Tuotu',
    historyToday: 'Historia alkaa tänään, {date}.',
    budgetSave: 'Tallenna budjetti',
    creditsUnit: 'krediittiä',
    budgetInvalid: 'Näitä rajoja ei voi tallentaa. Tarkista ne ja yritä uudelleen.',
    budgetSummary: 'Enintään {perRun} suorituskertaa kohden · enintään {monthly} kuukaudessa',
    budgetNone: 'Budjettia ei ole vielä määritetty.',
    budgetUnavailable: 'Budjetti ei ole tällä hetkellä saatavilla.',
    budgetDenied: 'Et voi muuttaa tätä budjettia.',
    budgetChanged:
      'Budjetti muuttui sen jälkeen, kun avasit tämän. Tarkista rajat ja yritä uudelleen.',
    budgetUnknown:
      'Emme voineet vahvistaa, tallennettiinko se. Yritä uudelleen; sitä ei oteta käyttöön kahdesti.',
    scheduleFacts: 'Suoritetaan: {schedule} ({zone})',
    scheduleNext: 'Seuraava suoritus {next}',
    scheduleOn: 'Käytössä',
    scheduleNotScheduled: 'Ei ajoitettu',
    scheduleEnable: 'Ota käyttöön',
    scheduleUnavailable: 'Aikataulu ei ole juuri nyt käytettävissä.',
    scheduleDenied: 'Et voi muuttaa tätä aikataulua.',
    scheduleConflict:
      'Aikataulu muuttui sen jälkeen, kun avasit tämän. Tarkista se ja yritä uudelleen.',
    scheduleUnknown:
      'Emme voineet vahvistaa, muuttuiko aikataulu. Yritä uudelleen; muutosta ei oteta käyttöön kahdesti.',
    budgetHeading: 'Budjetti',
    scheduleOnlyHeading: 'Aikataulu',
    scheduleJobOrdinal: 'Ajastettu suoritus {n}',
    budgetUnconfirmed: 'Emme voineet vahvistaa, tallennettiinko se.',
    scheduleUnconfirmed: 'Emme voineet vahvistaa, muuttuiko aikataulu.',
    scheduleTimeNote: 'Ajat ovat aikataulun omassa aikavyöhykkeessä.',
    scheduleWeekdays: 'Suoritetaan arkisin klo {time}',
    scheduleWeekly: 'Suoritetaan joka {days} klo {time}',
    scheduleCustom: 'Suoritetaan oman aikataulunsa mukaan',
    scheduleThen: 'Sen jälkeen {runs}',
    scheduleChange: 'Muuta aikataulua',
    scheduleChoose: 'Valitse toinen aika',
    pickerHeading: 'Milloin se suoritetaan',
    presetEvery15: '15 minuutin välein',
    presetEvery30: '30 minuutin välein',
    presetHourly: 'Tunnin välein',
    presetDaily: 'Päivittäin',
    presetWeekdays: 'Arkisin',
    presetWeekly: 'Viikoittain',
    presetCustom: 'Mukautettu',
    pickerTime: 'Aika',
    pickerDay: 'Päivä',
    pickerCron: 'Cron-lauseke',
    pickerCronHelp:
      'Viisi kenttää: minuutti, tunti, kuukauden päivä, kuukausi ja viikonpäivä, esimerkiksi 0 9 1 * * tarkoittaa klo 09:00 kunkin kuukauden ensimmäisenä päivänä.',
    pickerCustomPreview: 'Tallennus tarkistaa tämän aikataulun ja näyttää seuraavat suoritukset.',
    pickerCancel: 'Peruuta',
    scheduleTooFrequent: 'Suorituksia voi olla enintään 15 minuutin välein.',
    scheduleInvalidChoice: 'Tätä aikataulua ei voi käyttää. Tarkista lauseke ja aikavyöhyke.',
    scheduleBusy: 'Suoritus on käynnissä. Yritä uudelleen, kun se on valmis.',
    runsHeading: 'Viimeisimmät suoritukset',
    runsEmpty: 'Ei vielä suorituksia.',
    runsUnavailable: 'Viimeisimmät suoritukset eivät ole juuri nyt käytettävissä.',
    runSucceeded: 'Onnistui',
    runFailed: 'Epäonnistui',
    runUnknown: 'Vahvistamaton',
    runManual: 'Suoritettu käsin',
    runScheduled: 'Ajastettu',
    runWebhook: 'Webhook',
    runEvent: 'Tietojen muutos',
    runShowLog: 'Näytä loki',
    runHideLog: 'Piilota loki',
    runLogEmpty: 'Se ei tulostanut mitään.',
    runLogDropped: '{n} aiempaa riviä ei säilytetty.',
    runError: 'Virhe',
    runPrinted: 'Mitä se tulosti',
    runInput: 'Syöte',
    runLogUnavailable: 'Loki ei ole juuri nyt käytettävissä.',
    runFix: 'Korjaa keskustelussa',
    runFixFailed: 'Sovelluksen keskustelua ei voitu avata. Yritä uudelleen.',
    runsMore: 'Lataa lisää',
    fixScheduled: 'Tämän sovelluksen ajastettu suoritus epäonnistui. Selvitä syy ja korjaa se.',
    fixTriggered: 'Tämän sovelluksen suoritus epäonnistui. Selvitä syy ja korjaa se.',
    fixUnknown:
      'Tämän sovelluksen suoritus päättyi ilman vahvistettua tulosta. Selvitä, mitä tapahtui, ja korjaa se.',
    fixRunLine: 'Funktio {name} · suoritus {id} · {time}',
    fixError: 'Virhe',
    fixPrinted: 'Viimeiset tulostetut rivit',
    fixInput: 'Sen syöte',
  },
  fr: {
    sourcesHeading: 'Éléments à suivre',
    sourcesNote:
      'Les sources proviennent de packs certifiés de la boutique d’outils, et les sources manquantes sont ajoutées sur demande.',
    chooseSource: 'Choisir {name}',
    readsLine: 'Lit {fields}',
    partialLine: 'Lit {fields}. Pas encore disponibles : {gaps}.',
    awaitingLine: 'Pas encore pris en charge. Les sources manquantes sont ajoutées sur demande.',
    requestIt: 'Demander',
    requestedLine: 'Demandé le {date} · nous vous informerons quand ce sera prêt.',
    needsConnection: 'Nécessite la connexion {system} de {whose}',
    connect: 'Connecter {system}',
    askOwner: 'Demander à {owner}',
    coverageSummary:
      '{n} sources collectent actuellement des données ; {m} seront ajoutées lorsqu’elles seront prises en charge.',
    coverageSummaryOne:
      '{n} source collecte actuellement des données ; {m} seront ajoutées lorsqu’elles seront prises en charge.',
    scheduleHeading: 'Planification et budget',
    scheduleTime: 'S’exécute chaque jour à',
    scheduleDaily: 'S’exécute chaque jour à {time}',
    scheduleEveryHour: 'S’exécute toutes les heures',
    scheduleEveryMinutes: 'S’exécute toutes les {minutes} minutes',
    scheduleTimezone: 'Fuseau horaire',
    budgetPerRun: 'Montant maximal à dépenser par exécution',
    budgetMonthly: 'Montant maximal à dépenser par mois',
    scheduleSave: 'Enregistrer la planification',
    scheduleSaving: 'Enregistrement…',
    scheduleSummary:
      'Chaque jour à {time} {zone} · jusqu’à {perRun} par exécution · jusqu’à {monthly} par mois',
    oneRunPerDay:
      'Une exécution par jour. Une exécution répétée ou relancée n’est jamais comptée deux fois.',
    errTime: 'Saisissez une heure comme 07:00.',
    errCredits: 'Saisissez un nombre entier de {min} à {max}.',
    errOrder: 'La limite mensuelle doit être au moins égale à la limite par exécution.',
    collecting: 'Collecte quotidienne à {time} · dernière lecture {last} · {rows} lignes',
    collectingNever: 'Collecte quotidienne à {time} · pas encore de lecture',
    awaitingData: 'Demandé le {date} · collecte pas encore commencée',
    stopped: 'Arrêté : {reason}',
    reconnect: 'Reconnecter',
    skippedBudget:
      '{skipped} catégories sur {total} ignorées aujourd’hui : le budget mensuel est atteint.',
    gapMarker: 'Aucune donnée pour cette période',
    gapBudget: 'Ignoré : budget mensuel atteint',
    gapUnsupported: 'Ignoré : pas encore pris en charge',
    gapFailed: 'Ignoré : la source n’a pas répondu',
    chartLabel:
      'Prix au fil du temps. Une période ignorée crée une interruption dans la ligne, et non une valeur nulle.',
    firstRunTitle: 'Première exécution terminée',
    firstRun:
      '{read} sources lues · {unsupported} pas encore prises en charge · coût {cost} · prochaine exécution {next}',
    firstRunUnmeasured:
      '{read} sources lues · {unsupported} pas encore prises en charge · coût pas encore mesuré · prochaine exécution {next}',
    firstRunNoNext:
      '{read} sources lues · {unsupported} pas encore prises en charge · coût {cost} · aucune exécution planifiée',
    lastRunNone: 'Aucune exécution pour le moment. La première aura lieu à l’heure prévue.',
    paused: 'En pause',
    pause: 'Mettre en pause',
    resume: 'Reprendre',
    historyFrom: 'Historique provenant de {source} depuis {since} (dates fournies par la source)',
    historyImportedMark: 'Importé',
    historyToday: 'L’historique commence aujourd’hui, le {date}.',
    budgetSave: 'Enregistrer le budget',
    creditsUnit: 'crédits',
    budgetInvalid: 'Ces limites ne peuvent pas être enregistrées. Vérifiez-les et réessayez.',
    budgetSummary: 'Jusqu’à {perRun} par exécution · jusqu’à {monthly} par mois',
    budgetNone: 'Aucun budget n’est encore défini.',
    budgetUnavailable: 'Le budget n’est pas disponible pour le moment.',
    budgetDenied: 'Vous ne pouvez pas modifier ce budget.',
    budgetChanged:
      'Le budget a changé depuis l’ouverture de cette page. Vérifiez les limites et réessayez.',
    budgetUnknown:
      'Nous n’avons pas pu confirmer son enregistrement. Réessayez ; il ne sera pas appliqué deux fois.',
    scheduleFacts: 'Exécutions {schedule} ({zone})',
    scheduleNext: 'Prochaine exécution {next}',
    scheduleOn: 'Activée',
    scheduleNotScheduled: 'Non planifiée',
    scheduleEnable: 'Activer',
    scheduleUnavailable: 'Le planning n’est pas disponible pour le moment.',
    scheduleDenied: 'Vous ne pouvez pas modifier ce planning.',
    scheduleConflict:
      'Le planning a changé depuis que vous l’avez ouvert. Vérifiez-le, puis réessayez.',
    scheduleUnknown:
      'Nous n’avons pas pu confirmer si le planning a changé. Réessayez ; il ne sera pas appliqué deux fois.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Planning',
    scheduleJobOrdinal: 'Exécution planifiée {n}',
    budgetUnconfirmed: 'Nous n’avons pas pu confirmer l’enregistrement.',
    scheduleUnconfirmed: 'Nous n’avons pas pu confirmer si le planning a changé.',
    scheduleTimeNote: 'Les heures sont exprimées dans le fuseau horaire propre au planning.',
    scheduleWeekdays: 'S’exécute en semaine à {time}',
    scheduleWeekly: 'S’exécute chaque {days} à {time}',
    scheduleCustom: 'S’exécute selon son propre planning',
    scheduleThen: 'Ensuite {runs}',
    scheduleChange: 'Modifier le planning',
    scheduleChoose: 'Choisir un autre horaire',
    pickerHeading: 'Quand elle s’exécute',
    presetEvery15: 'Toutes les 15 minutes',
    presetEvery30: 'Toutes les 30 minutes',
    presetHourly: 'Toutes les heures',
    presetDaily: 'Tous les jours',
    presetWeekdays: 'En semaine',
    presetWeekly: 'Toutes les semaines',
    presetCustom: 'Personnalisé',
    pickerTime: 'Heure',
    pickerDay: 'Jour',
    pickerCron: 'Expression cron',
    pickerCronHelp:
      'Cinq champs : minute, heure, jour du mois, mois et jour de la semaine, par exemple 0 9 1 * * pour 09:00 le premier de chaque mois.',
    pickerCustomPreview:
      'L’enregistrement vérifie ce planning et affiche ses prochaines exécutions.',
    pickerCancel: 'Annuler',
    scheduleTooFrequent: 'Les exécutions peuvent avoir lieu au plus toutes les 15 minutes.',
    scheduleInvalidChoice:
      'Ce planning n’est pas utilisable. Vérifiez l’expression et le fuseau horaire.',
    scheduleBusy: 'Une exécution est en cours. Réessayez quand elle sera terminée.',
    runsHeading: 'Exécutions récentes',
    runsEmpty: 'Aucune exécution pour le moment.',
    runsUnavailable: 'Les exécutions récentes ne sont pas disponibles pour le moment.',
    runSucceeded: 'Réussie',
    runFailed: 'Échec',
    runUnknown: 'Non confirmée',
    runManual: 'Lancée à la main',
    runScheduled: 'Planifiée',
    runWebhook: 'Webhook',
    runEvent: 'Changement de données',
    runShowLog: 'Afficher le journal',
    runHideLog: 'Masquer le journal',
    runLogEmpty: 'Rien n’a été affiché.',
    runLogDropped: '{n} lignes antérieures n’ont pas été conservées.',
    runError: 'Erreur',
    runPrinted: 'Ce qui a été affiché',
    runInput: 'Entrée',
    runLogUnavailable: 'Le journal n’est pas disponible pour le moment.',
    runFix: 'Corriger dans le chat',
    runFixFailed: 'Le chat de l’application n’a pas pu être ouvert. Réessayez.',
    runsMore: 'Charger plus',
    fixScheduled:
      'L’exécution planifiée de cette application a échoué. Trouvez la cause et corrigez-la.',
    fixTriggered: 'L’exécution de cette application a échoué. Trouvez la cause et corrigez-la.',
    fixUnknown:
      'L’exécution de cette application s’est terminée sans résultat confirmé. Vérifiez ce qui s’est passé et corrigez-le.',
    fixRunLine: 'Fonction {name} · exécution {id} · {time}',
    fixError: 'Erreur',
    fixPrinted: 'Les dernières lignes affichées',
    fixInput: 'Son entrée',
  },
  ga: {
    sourcesHeading: 'Cad atá le rianú',
    sourcesNote:
      'Tagann foinsí ó phacáistí deimhnithe Tool Store, agus cuirtear na cinn atá ar iarraidh leis ar iarratas.',
    chooseSource: 'Roghnaigh {name}',
    readsLine: 'Léann sé {fields}',
    partialLine: 'Léann sé {fields}. Níl siad seo ann fós: {gaps}.',
    awaitingLine: 'Ní thacaítear leis fós. Cuirtear na foinsí atá ar iarraidh leis ar iarratas.',
    requestIt: 'Iarr é',
    requestedLine: 'Iarrtha an {date} · cuirfimid in iúl duit nuair a bheidh sé réidh.',
    needsConnection: 'Teastaíonn nasc {system} {whose}',
    connect: 'Ceangail {system}',
    askOwner: 'Iarr ar {owner}',
    coverageSummary: 'Bailíonn {n} fhoinse anois; glacfaidh {m} eile páirt nuair a thacófar leo.',
    coverageSummaryOne:
      'Bailíonn {n} fhoinse anois; glacfaidh {m} eile páirt nuair a thacófar leo.',
    scheduleHeading: 'Sceideal agus buiséad',
    scheduleTime: 'Ritheann sé gach lá ag',
    scheduleDaily: 'Ritheann sé gach lá ag {time}',
    scheduleEveryHour: 'Ritheann sé gach uair an chloig',
    scheduleEveryMinutes: 'Ritheann sé gach {minutes} nóiméad',
    scheduleTimezone: 'Crios ama',
    budgetPerRun: 'Uasmhéid le caitheamh in aon rith amháin',
    budgetMonthly: 'Uasmhéid le caitheamh i mí',
    scheduleSave: 'Sábháil an sceideal',
    scheduleSaving: 'Á shábháil…',
    scheduleSummary:
      'Gach lá ag {time} {zone} · suas le {perRun} in aghaidh an rith · suas le {monthly} sa mhí',
    oneRunPerDay: 'Rith amháin gach lá. Ní áirítear rith athdhéanta ná atriallta faoi dhó riamh.',
    errTime: 'Cuir isteach am mar 07:00.',
    errCredits: 'Cuir isteach slánuimhir ó {min} go {max}.',
    errOrder:
      'Caithfidh an teorainn mhíosúil a bheith cothrom le teorainn aon rite amháin ar a laghad.',
    collecting: 'Á bhailiú gach lá ag {time} · an léamh deireanach {last} · {rows} sraith',
    collectingNever: 'Á bhailiú gach lá ag {time} · níor léadh fós é',
    awaitingData: 'Iarrtha an {date} · níl sé á bhailiú fós',
    stopped: 'Stoptha: {reason}',
    reconnect: 'Athcheangail',
    skippedBudget:
      'Scipeáladh {skipped} as {total} chatagóir inniu: tá an buiséad míosúil bainte amach.',
    gapMarker: 'Gan sonraí don tréimhse seo',
    gapBudget: 'Scipeáilte: tá an buiséad míosúil bainte amach',
    gapUnsupported: 'Scipeáilte: ní thacaítear leis fós',
    gapFailed: 'Scipeáilte: níor fhreagair an fhoinse',
    chartLabel: 'Praghas le himeacht ama. Is briseadh sa líne é tréimhse a scipeáladh, ní nialas.',
    firstRunTitle: 'An chéad rith críochnaithe',
    firstRun:
      'Léadh {read} fhoinse · ní thacaítear le {unsupported} fós · costas {cost} · an chéad rith eile {next}',
    firstRunUnmeasured:
      'Léadh {read} fhoinse · ní thacaítear le {unsupported} fós · níl an costas tomhaiste fós · an chéad rith eile {next}',
    firstRunNoNext:
      'Léadh {read} fhoinse · ní thacaítear le {unsupported} fós · costas {cost} · níl aon rith sceidealaithe',
    lastRunNone: 'Níl aon rith ann fós. Rithfidh an chéad cheann ag an am sceidealaithe.',
    paused: 'Curtha ar sos',
    pause: 'Cuir ar sos',
    resume: 'Atosaigh',
    historyFrom: 'Stair ó {source} ón {since} (dátaí ón bhfoinse)',
    historyImportedMark: 'Iompórtáilte',
    historyToday: 'Tosaíonn an stair inniu, {date}.',
    budgetSave: 'Sábháil an buiséad',
    creditsUnit: 'creidmheas',
    budgetInvalid:
      'Ní féidir na teorainneacha seo a shábháil. Seiceáil iad agus bain triail eile as.',
    budgetSummary: 'Suas le {perRun} in aghaidh an rith · suas le {monthly} sa mhí',
    budgetNone: 'Níl aon bhuiséad socraithe fós.',
    budgetUnavailable: 'Níl an buiséad ar fáil faoi láthair.',
    budgetDenied: 'Ní féidir leat an buiséad seo a athrú.',
    budgetChanged:
      "Athraíodh an buiséad ó d'oscail tú é seo. Seiceáil na teorainneacha agus bain triail eile as.",
    budgetUnknown:
      'Níorbh fhéidir linn a dheimhniú ar sábháladh é. Bain triail eile as; ní chuirfear i bhfeidhm faoi dhó é.',
    scheduleFacts: 'Ritheann sé {schedule} ({zone})',
    scheduleNext: 'An chéad rith eile {next}',
    scheduleOn: 'Cumasaithe',
    scheduleNotScheduled: 'Gan sceidealú',
    scheduleEnable: 'Cuir ar siúl',
    scheduleUnavailable: 'Níl an sceideal ar fáil faoi láthair.',
    scheduleDenied: 'Ní féidir leat an sceideal seo a athrú.',
    scheduleConflict: 'Athraíodh an sceideal ó d’oscail tú é. Seiceáil é agus bain triail eile as.',
    scheduleUnknown:
      'Ní rabhamar in ann a dheimhniú ar athraíodh an sceideal. Bain triail eile as; ní chuirfear i bhfeidhm é faoi dhó.',
    budgetHeading: 'Buiséad',
    scheduleOnlyHeading: 'Sceideal',
    scheduleJobOrdinal: 'Rith sceidealaithe {n}',
    budgetUnconfirmed: 'Ní rabhamar in ann a dheimhniú ar sábháladh é.',
    scheduleUnconfirmed: 'Ní rabhamar in ann a dheimhniú ar athraíodh an sceideal.',
    scheduleTimeNote: 'Tá na hamanna i gcrios ama an sceidil féin.',
    scheduleWeekdays: 'Ritheann sé laethanta seachtaine ag {time}',
    scheduleWeekly: 'Ritheann sé gach seachtain: {days} ag {time}',
    scheduleCustom: 'Ritheann sé ar a sceideal féin',
    scheduleThen: 'Ansin {runs}',
    scheduleChange: 'Athraigh an sceideal',
    scheduleChoose: 'Roghnaigh am eile',
    pickerHeading: 'Cathain a ritheann sé',
    presetEvery15: 'Gach 15 nóiméad',
    presetEvery30: 'Gach 30 nóiméad',
    presetHourly: 'Gach uair an chloig',
    presetDaily: 'Gach lá',
    presetWeekdays: 'Laethanta seachtaine',
    presetWeekly: 'Gach seachtain',
    presetCustom: 'Saincheaptha',
    pickerTime: 'Am',
    pickerDay: 'Lá',
    pickerCron: 'Slonn cron',
    pickerCronHelp:
      'Cúig réimse: nóiméad, uair, lá den mhí, mí agus lá den tseachtain, mar shampla 0 9 1 * * do 09:00 ar an gcéad lá de gach mí.',
    pickerCustomPreview:
      'Seiceálann an sábháil an sceideal seo agus taispeánann sí na rith eile atá le teacht.',
    pickerCancel: 'Cealaigh',
    scheduleTooFrequent: 'Ní féidir rith a bheith níos minicí ná gach 15 nóiméad.',
    scheduleInvalidChoice:
      'Ní féidir an sceideal sin a úsáid. Seiceáil an slonn agus an crios ama.',
    scheduleBusy: 'Tá rith ar siúl. Bain triail eile as nuair a bheidh sé críochnaithe.',
    runsHeading: 'Rith le déanaí',
    runsEmpty: 'Gan aon rith fós.',
    runsUnavailable: 'Níl na rith le déanaí ar fáil faoi láthair.',
    runSucceeded: 'D’éirigh leis',
    runFailed: 'Theip air',
    runUnknown: 'Gan deimhniú',
    runManual: 'Rite de láimh',
    runScheduled: 'Sceidealaithe',
    runWebhook: 'Webhook',
    runEvent: 'Athrú sonraí',
    runShowLog: 'Taispeáin an log',
    runHideLog: 'Folaigh an log',
    runLogEmpty: 'Níor phriontáil sé tada.',
    runLogDropped: 'Níor coinníodh {n} líne roimhe seo.',
    runError: 'Earráid',
    runPrinted: 'An méid a phriontáil sé',
    runInput: 'Ionchur',
    runLogUnavailable: 'Níl an log ar fáil faoi láthair.',
    runFix: 'Deisigh sa chomhrá',
    runFixFailed: 'Níorbh fhéidir comhrá na haipe a oscailt. Bain triail eile as.',
    runsMore: 'Lódáil tuilleadh',
    fixScheduled: 'Theip ar rith sceidealaithe na haipe seo. Aimsigh an chúis agus deisigh é.',
    fixTriggered: 'Theip ar rith na haipe seo. Aimsigh an chúis agus deisigh é.',
    fixUnknown:
      'Chríochnaigh rith na haipe seo gan toradh deimhnithe. Seiceáil cad a tharla agus deisigh é.',
    fixRunLine: 'Feidhm {name} · rith {id} · {time}',
    fixError: 'Earráid',
    fixPrinted: 'Na línte deireanacha a phriontáil sé',
    fixInput: 'A ionchur',
  },
  hr: {
    sourcesHeading: 'Što pratiti',
    sourcesNote:
      'Izvori dolaze iz certificiranih paketa Tool Storea, a nedostajući se dodaju na zahtjev.',
    chooseSource: 'Odaberite {name}',
    readsLine: 'Čita {fields}',
    partialLine: 'Čita {fields}. Još nije dostupno: {gaps}.',
    awaitingLine: 'Još nije podržano. Nedostajući se izvori dodaju na zahtjev.',
    requestIt: 'Zatraži',
    requestedLine: 'Zatraženo {date} · obavijestit ćemo vas kada bude spremno.',
    needsConnection: 'Potrebna je veza {whose} sa sustavom {system}',
    connect: 'Poveži {system}',
    askOwner: 'Pitaj {owner}',
    coverageSummary: 'Trenutačno se prikuplja {n} izvora; {m} će se pridružiti kada budu podržani.',
    coverageSummaryOne:
      'Trenutačno se prikuplja {n} izvor; {m} će se pridružiti kada budu podržani.',
    scheduleHeading: 'Raspored i proračun',
    scheduleTime: 'Pokreće se svaki dan u',
    scheduleDaily: 'Pokreće se svaki dan u {time}',
    scheduleEveryHour: 'Pokreće se svakog sata',
    scheduleEveryMinutes: 'Pokreće se svakih {minutes} minuta',
    scheduleTimezone: 'Vremenska zona',
    budgetPerRun: 'Najviše za jednu izvedbu',
    budgetMonthly: 'Najviše za jedan mjesec',
    scheduleSave: 'Spremi raspored',
    scheduleSaving: 'Spremanje…',
    scheduleSummary: 'Svakodnevno u {time} {zone} · do {perRun} po izvedbi · do {monthly} mjesečno',
    oneRunPerDay:
      'Jedna izvedba dnevno. Ponovljena izvedba ili ponovni pokušaj nikad se ne računa dvaput.',
    errTime: 'Unesite vrijeme poput 07:00.',
    errCredits: 'Unesite cijeli broj od {min} do {max}.',
    errOrder: 'Mjesečno ograničenje mora biti barem jednako ograničenju za jednu izvedbu.',
    collecting: 'Prikupljanje svakog dana u {time} · zadnje čitanje {last} · {rows} redaka',
    collectingNever: 'Prikupljanje svakog dana u {time} · još nije čitano',
    awaitingData: 'Zatraženo {date} · prikupljanje još nije pokrenuto',
    stopped: 'Zaustavljeno: {reason}',
    reconnect: 'Ponovno poveži',
    skippedBudget:
      'Danas je preskočeno {skipped} od ukupno {total} kategorija: mjesečni je proračun dosegnut.',
    gapMarker: 'Nema podataka za ovo razdoblje',
    gapBudget: 'Preskočeno: mjesečni je proračun dosegnut',
    gapUnsupported: 'Preskočeno: još nije podržano',
    gapFailed: 'Preskočeno: izvor nije odgovorio',
    chartLabel: 'Cijena tijekom vremena. Preskočeno razdoblje prekid je na liniji, a ne nula.',
    firstRunTitle: 'Prva izvedba dovršena',
    firstRun:
      'Pročitano izvora: {read} · još nije podržano: {unsupported} · trošak {cost} · sljedeća izvedba {next}',
    firstRunUnmeasured:
      'Pročitano izvora: {read} · još nije podržano: {unsupported} · trošak još nije izmjeren · sljedeća izvedba {next}',
    firstRunNoNext:
      'Pročitano izvora: {read} · još nije podržano: {unsupported} · trošak {cost} · nije zakazana nijedna izvedba',
    lastRunNone: 'Još nema izvedbi. Prva će se pokrenuti u zakazano vrijeme.',
    paused: 'Pauzirano',
    pause: 'Pauziraj',
    resume: 'Nastavi',
    historyFrom: 'Povijest iz izvora {source} od {since} (datumi iz izvora)',
    historyImportedMark: 'Uvezeno',
    historyToday: 'Povijest počinje danas, {date}.',
    budgetSave: 'Spremi proračun',
    creditsUnit: 'kredita',
    budgetInvalid: 'Ta se ograničenja ne mogu spremiti. Provjerite ih i pokušajte ponovno.',
    budgetSummary: 'Do {perRun} po izvedbi · do {monthly} mjesečno',
    budgetNone: 'Proračun još nije postavljen.',
    budgetUnavailable: 'Proračun trenutačno nije dostupan.',
    budgetDenied: 'Ne možete promijeniti ovaj proračun.',
    budgetChanged:
      'Proračun se promijenio otkad ste otvorili ovu stranicu. Provjerite ograničenja i pokušajte ponovno.',
    budgetUnknown:
      'Nismo mogli potvrditi je li spremljeno. Pokušajte ponovno; neće se primijeniti dvaput.',
    scheduleFacts: 'Pokreće se {schedule} ({zone})',
    scheduleNext: 'Sljedeća izvedba {next}',
    scheduleOn: 'Uključeno',
    scheduleNotScheduled: 'Nije zakazano',
    scheduleEnable: 'Uključi',
    scheduleUnavailable: 'Raspored trenutačno nije dostupan.',
    scheduleDenied: 'Ne možete promijeniti ovaj raspored.',
    scheduleConflict:
      'Raspored se promijenio otkad ste ga otvorili. Provjerite ga i pokušajte ponovno.',
    scheduleUnknown:
      'Nismo mogli potvrditi je li se raspored promijenio. Pokušajte ponovno; promjena se neće primijeniti dvaput.',
    budgetHeading: 'Proračun',
    scheduleOnlyHeading: 'Raspored',
    scheduleJobOrdinal: 'Planirano pokretanje {n}',
    budgetUnconfirmed: 'Nismo mogli potvrditi je li spremljeno.',
    scheduleUnconfirmed: 'Nismo mogli potvrditi je li se raspored promijenio.',
    scheduleTimeNote: 'Vremena vrijede u vremenskoj zoni rasporeda.',
    scheduleWeekdays: 'Pokreće se radnim danom u {time}',
    scheduleWeekly: 'Pokreće se svaki tjedan: {days} u {time}',
    scheduleCustom: 'Pokreće se prema vlastitom rasporedu',
    scheduleThen: 'Zatim {runs}',
    scheduleChange: 'Promijeni raspored',
    scheduleChoose: 'Odaberi drugo vrijeme',
    pickerHeading: 'Kada se pokreće',
    presetEvery15: 'Svakih 15 minuta',
    presetEvery30: 'Svakih 30 minuta',
    presetHourly: 'Svakog sata',
    presetDaily: 'Svaki dan',
    presetWeekdays: 'Radni dani',
    presetWeekly: 'Svaki tjedan',
    presetCustom: 'Prilagođeno',
    pickerTime: 'Vrijeme',
    pickerDay: 'Dan',
    pickerCron: 'Izraz cron',
    pickerCronHelp:
      'Pet polja: minuta, sat, dan u mjesecu, mjesec i dan u tjednu, na primjer 0 9 1 * * znači u 9:00 prvog dana svakog mjeseca.',
    pickerCustomPreview: 'Pri spremanju provjerava se raspored i prikazuju sljedeće izvedbe.',
    pickerCancel: 'Odustani',
    scheduleTooFrequent: 'Izvedbe mogu biti najviše svakih 15 minuta.',
    scheduleInvalidChoice:
      'Taj se raspored ne može upotrijebiti. Provjerite izraz i vremensku zonu.',
    scheduleBusy: 'Izvedba je u tijeku. Pokušajte ponovno kad završi.',
    runsHeading: 'Posljednje izvedbe',
    runsEmpty: 'Još nema izvedbi.',
    runsUnavailable: 'Posljednje izvedbe trenutačno nisu dostupne.',
    runSucceeded: 'Uspjelo',
    runFailed: 'Nije uspjelo',
    runUnknown: 'Nepotvrđeno',
    runManual: 'Ručno pokrenuto',
    runScheduled: 'Zakazano',
    runWebhook: 'Webhook',
    runEvent: 'Promjena podataka',
    runShowLog: 'Prikaži zapisnik',
    runHideLog: 'Sakrij zapisnik',
    runLogEmpty: 'Nije ispisala ništa.',
    runLogDropped: 'Ranijih redaka koji nisu sačuvani: {n}.',
    runError: 'Pogreška',
    runPrinted: 'Što je ispisala',
    runInput: 'Ulaz',
    runLogUnavailable: 'Zapisnik trenutačno nije dostupan.',
    runFix: 'Popravi u chatu',
    runFixFailed: 'Chat aplikacije nije bilo moguće otvoriti. Pokušajte ponovno.',
    runsMore: 'Učitaj više',
    fixScheduled: 'Zakazana izvedba ove aplikacije nije uspjela. Pronađi uzrok i popravi.',
    fixTriggered: 'Izvedba ove aplikacije nije uspjela. Pronađi uzrok i popravi.',
    fixUnknown:
      'Izvedba ove aplikacije završila je bez potvrđenog rezultata. Provjeri što se dogodilo i popravi.',
    fixRunLine: 'Funkcija {name} · izvedba {id} · {time}',
    fixError: 'Pogreška',
    fixPrinted: 'Zadnji ispisani redci',
    fixInput: 'Njezin ulaz',
  },
  hu: {
    sourcesHeading: 'Mit kövessünk nyomon',
    sourcesNote:
      'A források hitelesített Tool Store-csomagokból származnak, a hiányzókat pedig kérésre hozzáadjuk.',
    chooseSource: '{name} kiválasztása',
    readsLine: 'Ezeket olvassa: {fields}',
    partialLine: 'Ezeket olvassa: {fields}. Még nem: {gaps}.',
    awaitingLine: 'Még nem támogatott. A hiányzó forrásokat kérésre hozzáadjuk.',
    requestIt: 'Kérés',
    requestedLine: 'Kérve: {date} · értesítjük, amikor elkészül.',
    needsConnection: '{whose} {system}-kapcsolata szükséges',
    connect: '{system} csatlakoztatása',
    askOwner: 'Kérés a tulajdonostól: {owner}',
    coverageSummary: 'Jelenleg {n} forrás gyűjt adatot; {m} csatlakozik, amikor támogatottá válik.',
    coverageSummaryOne:
      'Jelenleg {n} forrás gyűjt adatot; {m} csatlakozik, amikor támogatottá válik.',
    scheduleHeading: 'Ütemezés és költségkeret',
    scheduleTime: 'Futtatás naponta ekkor:',
    scheduleDaily: 'Futtatás naponta ekkor: {time}',
    scheduleEveryHour: 'Futtatás óránként',
    scheduleEveryMinutes: 'Futtatás {minutes} percenként',
    scheduleTimezone: 'Időzóna',
    budgetPerRun: 'Egy futtatásra fordítható legfeljebb',
    budgetMonthly: 'Egy hónapban elkölthető legfeljebb',
    scheduleSave: 'Ütemezés mentése',
    scheduleSaving: 'Mentés…',
    scheduleSummary:
      'Naponta ekkor: {time} {zone} · futtatásonként legfeljebb {perRun} · havonta legfeljebb {monthly}',
    oneRunPerDay:
      'Naponta egy futtatás. Egy ismételt vagy újrapróbált futtatás soha nem számít kétszer.',
    errTime: 'Adjon meg egy időpontot, például: 07:00.',
    errCredits: 'Adjon meg egy egész számot {min} és {max} között.',
    errOrder: 'A havi korlátnak legalább egy futtatás korlátjával azonosnak kell lennie.',
    collecting: 'Napi adatgyűjtés ekkor: {time} · utolsó olvasás: {last} · {rows} sor',
    collectingNever: 'Napi adatgyűjtés ekkor: {time} · még nem történt olvasás',
    awaitingData: 'Kérve: {date} · még nincs adatgyűjtés',
    stopped: 'Leállítva: {reason}',
    reconnect: 'Újracsatlakozás',
    skippedBudget: 'Ma {skipped}/{total} kategória kihagyva: a havi költségkeret betelt.',
    gapMarker: 'Nincs adat erre az időszakra',
    gapBudget: 'Kihagyva: a havi költségkeret betelt',
    gapUnsupported: 'Kihagyva: még nem támogatott',
    gapFailed: 'Kihagyva: a forrás nem válaszolt',
    chartLabel: 'Ár az idő függvényében. A kihagyott időszak törést jelent a vonalban, nem nullát.',
    firstRunTitle: 'Az első futtatás kész',
    firstRun:
      'Beolvasott források: {read} · még nem támogatott: {unsupported} · költség: {cost} · következő futtatás: {next}',
    firstRunUnmeasured:
      'Beolvasott források: {read} · még nem támogatott: {unsupported} · költség: még nincs mérve · következő futtatás: {next}',
    firstRunNoNext:
      'Beolvasott források: {read} · még nem támogatott: {unsupported} · költség: {cost} · nincs ütemezett futtatás',
    lastRunNone: 'Még nem volt futtatás. Az első a beütemezett időpontban indul.',
    paused: 'Szüneteltetve',
    pause: 'Szüneteltetés',
    resume: 'Folytatás',
    historyFrom:
      'Előzmények innen: {source}, ettől kezdve: {since} (a dátumok a forrásból származnak)',
    historyImportedMark: 'Importálva',
    historyToday: 'Az előzmények ma, {date} kezdődnek.',
    budgetSave: 'Költségkeret mentése',
    creditsUnit: 'kredit',
    budgetInvalid: 'Ezek a korlátok nem menthetők. Ellenőrizze őket, és próbálja újra.',
    budgetSummary: 'Futtatásonként legfeljebb {perRun} · havonta legfeljebb {monthly}',
    budgetNone: 'Még nincs beállítva költségkeret.',
    budgetUnavailable: 'A költségkeret jelenleg nem érhető el.',
    budgetDenied: 'Nem módosíthatja ezt a költségkeretet.',
    budgetChanged:
      'A költségkeret megváltozott az oldal megnyitása óta. Ellenőrizze a korlátokat, majd próbálja újra.',
    budgetUnknown:
      'Nem sikerült megerősíteni, hogy mentve lett-e. Próbálja újra; nem lesz kétszer alkalmazva.',
    scheduleFacts: 'Futtatás: {schedule} ({zone})',
    scheduleNext: 'Következő futtatás: {next}',
    scheduleOn: 'Bekapcsolva',
    scheduleNotScheduled: 'Nincs ütemezve',
    scheduleEnable: 'Bekapcsolás',
    scheduleUnavailable: 'Az ütemezés jelenleg nem érhető el.',
    scheduleDenied: 'Nem módosíthatja ezt az ütemezést.',
    scheduleConflict:
      'Az ütemezés megváltozott, mióta megnyitotta. Ellenőrizze, majd próbálja újra.',
    scheduleUnknown:
      'Nem sikerült megerősítenünk, hogy az ütemezés megváltozott-e. Próbálja újra; a módosítás nem lesz kétszer alkalmazva.',
    budgetHeading: 'Keret',
    scheduleOnlyHeading: 'Ütemezés',
    scheduleJobOrdinal: '{n}. ütemezett futtatás',
    budgetUnconfirmed: 'Nem sikerült megerősítenünk, hogy a mentés megtörtént-e.',
    scheduleUnconfirmed: 'Nem sikerült megerősítenünk, hogy az ütemezés megváltozott-e.',
    scheduleTimeNote: 'Az időpontok az ütemezés saját időzónájában értendők.',
    scheduleWeekdays: 'Futtatás hétköznapokon ekkor: {time}',
    scheduleWeekly: 'Futtatás hetente: {days} ekkor: {time}',
    scheduleCustom: 'Egyéni ütemezés szerint fut',
    scheduleThen: 'Ezután {runs}',
    scheduleChange: 'Ütemezés módosítása',
    scheduleChoose: 'Másik időpont választása',
    pickerHeading: 'Mikor fut',
    presetEvery15: '15 percenként',
    presetEvery30: '30 percenként',
    presetHourly: 'Óránként',
    presetDaily: 'Naponta',
    presetWeekdays: 'Hétköznapokon',
    presetWeekly: 'Hetente',
    presetCustom: 'Egyéni',
    pickerTime: 'Időpont',
    pickerDay: 'Nap',
    pickerCron: 'Cron-kifejezés',
    pickerCronHelp:
      'Öt mező: perc, óra, a hónap napja, hónap és a hét napja, például a 0 9 1 * * minden hónap első napján 9:00-t jelent.',
    pickerCustomPreview:
      'Mentéskor ellenőrizzük az ütemezést, és megmutatjuk a következő futtatásokat.',
    pickerCancel: 'Mégse',
    scheduleTooFrequent: 'A futtatások legfeljebb 15 percenként történhetnek.',
    scheduleInvalidChoice:
      'Ez az ütemezés nem használható. Ellenőrizd a kifejezést és az időzónát.',
    scheduleBusy: 'Futtatás van folyamatban. Próbáld újra, ha befejeződött.',
    runsHeading: 'Legutóbbi futtatások',
    runsEmpty: 'Még nincs futtatás.',
    runsUnavailable: 'A legutóbbi futtatások jelenleg nem érhetők el.',
    runSucceeded: 'Sikeres',
    runFailed: 'Sikertelen',
    runUnknown: 'Nem megerősített',
    runManual: 'Kézzel indítva',
    runScheduled: 'Ütemezett',
    runWebhook: 'Webhook',
    runEvent: 'Adatváltozás',
    runShowLog: 'Napló megjelenítése',
    runHideLog: 'Napló elrejtése',
    runLogEmpty: 'Nem írt ki semmit.',
    runLogDropped: '{n} korábbi sor nem lett megőrizve.',
    runError: 'Hiba',
    runPrinted: 'Amit kiírt',
    runInput: 'Bemenet',
    runLogUnavailable: 'A napló jelenleg nem érhető el.',
    runFix: 'Javítás a csevegésben',
    runFixFailed: 'Az alkalmazás csevegését nem sikerült megnyitni. Próbáld újra.',
    runsMore: 'Továbbiak betöltése',
    fixScheduled:
      'Ennek az alkalmazásnak az ütemezett futtatása sikertelen volt. Keresd meg az okot, és javítsd ki.',
    fixTriggered:
      'Ennek az alkalmazásnak a futtatása sikertelen volt. Keresd meg az okot, és javítsd ki.',
    fixUnknown:
      'Ennek az alkalmazásnak a futtatása megerősített eredmény nélkül ért véget. Nézd meg, mi történt, és javítsd ki.',
    fixRunLine: 'Függvény: {name} · futtatás: {id} · {time}',
    fixError: 'Hiba',
    fixPrinted: 'Az utolsó kiírt sorok',
    fixInput: 'A bemenete',
  },
  it: {
    sourcesHeading: 'Cosa monitorare',
    sourcesNote:
      'Le fonti provengono dai pacchetti certificati del Tool Store; quelle mancanti vengono aggiunte su richiesta.',
    chooseSource: 'Scegli {name}',
    readsLine: 'Legge {fields}',
    partialLine: 'Legge {fields}. Non ancora: {gaps}.',
    awaitingLine: 'Non ancora supportato. Le fonti mancanti vengono aggiunte su richiesta.',
    requestIt: 'Richiedila',
    requestedLine: 'Richiesta il {date} · ti avviseremo quando sarà pronta.',
    needsConnection: 'Richiede la connessione {system} di {whose}',
    connect: 'Collega {system}',
    askOwner: 'Chiedi a {owner}',
    coverageSummary:
      '{n} fonti raccolgono dati ora; altre {m} verranno aggiunte quando saranno supportate.',
    coverageSummaryOne:
      '{n} fonte raccoglie dati ora; altre {m} verranno aggiunte quando saranno supportate.',
    scheduleHeading: 'Pianificazione e budget',
    scheduleTime: 'Esegue ogni giorno alle',
    scheduleDaily: 'Esegue ogni giorno alle {time}',
    scheduleEveryHour: 'Esegue ogni ora',
    scheduleEveryMinutes: 'Esegue ogni {minutes} minuti',
    scheduleTimezone: 'Fuso orario',
    budgetPerRun: "Importo massimo per un'esecuzione",
    budgetMonthly: 'Importo massimo mensile',
    scheduleSave: 'Salva pianificazione',
    scheduleSaving: 'Salvataggio…',
    scheduleSummary:
      'Ogni giorno alle {time} {zone} · fino a {perRun} per esecuzione · fino a {monthly} al mese',
    oneRunPerDay:
      "Un'esecuzione al giorno. Un'esecuzione ripetuta o ritentata non viene mai conteggiata due volte.",
    errTime: 'Inserisci un orario come 07:00.',
    errCredits: 'Inserisci un numero intero da {min} a {max}.',
    errOrder: 'Il limite mensile deve essere almeno pari al limite per una singola esecuzione.',
    collecting: 'Raccolta giornaliera alle {time} · ultima lettura {last} · {rows} righe',
    collectingNever: 'Raccolta giornaliera alle {time} · non ancora letta',
    awaitingData: 'Richiesta il {date} · raccolta non ancora iniziata',
    stopped: 'Interrotta: {reason}',
    reconnect: 'Riconnetti',
    skippedBudget:
      'Oggi sono state saltate {skipped} categorie su {total}: il budget mensile è stato raggiunto.',
    gapMarker: 'Nessun dato per questo periodo',
    gapBudget: 'Saltato: budget mensile raggiunto',
    gapUnsupported: 'Saltato: non ancora supportato',
    gapFailed: 'Saltato: la fonte non ha risposto',
    chartLabel: 'Prezzo nel tempo. Un periodo saltato interrompe la linea, non equivale a zero.',
    firstRunTitle: 'Prima esecuzione completata',
    firstRun:
      'Lette {read} fonti · {unsupported} non ancora supportate · costo {cost} · prossima esecuzione {next}',
    firstRunUnmeasured:
      'Lette {read} fonti · {unsupported} non ancora supportate · costo non ancora misurato · prossima esecuzione {next}',
    firstRunNoNext:
      'Lette {read} fonti · {unsupported} non ancora supportate · costo {cost} · nessuna esecuzione pianificata',
    lastRunNone: "Nessuna esecuzione. La prima verrà eseguita all'orario pianificato.",
    paused: 'In pausa',
    pause: 'Metti in pausa',
    resume: 'Riprendi',
    historyFrom: 'Cronologia da {source} dal {since} (date della fonte)',
    historyImportedMark: 'Importata',
    historyToday: 'La cronologia inizia oggi, {date}.',
    budgetSave: 'Salva budget',
    creditsUnit: 'crediti',
    budgetInvalid: 'Questi limiti non possono essere salvati. Controllali e riprova.',
    budgetSummary: 'Fino a {perRun} per esecuzione · fino a {monthly} al mese',
    budgetNone: 'Non è ancora stato impostato alcun budget.',
    budgetUnavailable: 'Il budget non è al momento disponibile.',
    budgetDenied: 'Non puoi modificare questo budget.',
    budgetChanged:
      'Il budget è cambiato da quando hai aperto questa pagina. Controlla i limiti e riprova.',
    budgetUnknown:
      'Non abbiamo potuto confermare se è stato salvato. Riprova; non verrà applicato due volte.',
    scheduleFacts: 'Esegue {schedule} ({zone})',
    scheduleNext: 'Prossima esecuzione {next}',
    scheduleOn: 'Attivata',
    scheduleNotScheduled: 'Non pianificata',
    scheduleEnable: 'Attiva',
    scheduleUnavailable: 'La pianificazione non è disponibile al momento.',
    scheduleDenied: 'Non puoi modificare questa pianificazione.',
    scheduleConflict: 'La pianificazione è cambiata da quando l’hai aperta. Controllala e riprova.',
    scheduleUnknown:
      'Non abbiamo potuto confermare se la pianificazione è cambiata. Riprova: non verrà applicata due volte.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Pianificazione',
    scheduleJobOrdinal: 'Esecuzione pianificata {n}',
    budgetUnconfirmed: 'Non abbiamo potuto confermare che sia stata salvata.',
    scheduleUnconfirmed: 'Non abbiamo potuto confermare se la pianificazione è cambiata.',
    scheduleTimeNote: 'Gli orari sono nel fuso orario della pianificazione stessa.',
    scheduleWeekdays: 'Si esegue nei giorni feriali alle {time}',
    scheduleWeekly: 'Si esegue ogni {days} alle {time}',
    scheduleCustom: 'Si esegue con una pianificazione propria',
    scheduleThen: 'Poi {runs}',
    scheduleChange: 'Cambia pianificazione',
    scheduleChoose: 'Scegli un altro orario',
    pickerHeading: 'Quando si esegue',
    presetEvery15: 'Ogni 15 minuti',
    presetEvery30: 'Ogni 30 minuti',
    presetHourly: 'Ogni ora',
    presetDaily: 'Ogni giorno',
    presetWeekdays: 'Giorni feriali',
    presetWeekly: 'Ogni settimana',
    presetCustom: 'Personalizzata',
    pickerTime: 'Ora',
    pickerDay: 'Giorno',
    pickerCron: 'Espressione cron',
    pickerCronHelp:
      'Cinque campi: minuto, ora, giorno del mese, mese e giorno della settimana, ad esempio 0 9 1 * * per le 09:00 del primo giorno di ogni mese.',
    pickerCustomPreview:
      'Il salvataggio controlla questa pianificazione e mostra le prossime esecuzioni.',
    pickerCancel: 'Annulla',
    scheduleTooFrequent: 'Le esecuzioni possono avvenire al massimo ogni 15 minuti.',
    scheduleInvalidChoice:
      'Questa pianificazione non può essere usata. Controlla l’espressione e il fuso orario.',
    scheduleBusy: 'Un’esecuzione è in corso. Riprova quando sarà terminata.',
    runsHeading: 'Esecuzioni recenti',
    runsEmpty: 'Ancora nessuna esecuzione.',
    runsUnavailable: 'Le esecuzioni recenti non sono disponibili al momento.',
    runSucceeded: 'Riuscita',
    runFailed: 'Non riuscita',
    runUnknown: 'Non confermata',
    runManual: 'Manuale',
    runScheduled: 'Pianificata',
    runWebhook: 'Webhook',
    runEvent: 'Modifica dei dati',
    runShowLog: 'Mostra log',
    runHideLog: 'Nascondi log',
    runLogEmpty: 'Non ha stampato nulla.',
    runLogDropped: '{n} righe precedenti non sono state conservate.',
    runError: 'Errore',
    runPrinted: 'Cosa ha stampato',
    runInput: 'Input',
    runLogUnavailable: 'Il log non è disponibile al momento.',
    runFix: 'Correggi nella chat',
    runFixFailed: 'Non è stato possibile aprire la chat dell’app. Riprova.',
    runsMore: 'Carica altro',
    fixScheduled:
      'L’esecuzione pianificata di questa app non è riuscita. Trova la causa e correggila.',
    fixTriggered: 'Un’esecuzione di questa app non è riuscita. Trova la causa e correggila.',
    fixUnknown:
      'Un’esecuzione di questa app si è conclusa senza un risultato confermato. Controlla cosa è successo e correggilo.',
    fixRunLine: 'Funzione {name} · esecuzione {id} · {time}',
    fixError: 'Errore',
    fixPrinted: 'Le ultime righe stampate',
    fixInput: 'Il suo input',
  },
  lt: {
    sourcesHeading: 'Ką stebėti',
    sourcesNote:
      'Šaltiniai gaunami iš sertifikuotų Tool Store paketų, o trūkstami pridedami pagal užklausą.',
    chooseSource: 'Pasirinkite {name}',
    readsLine: 'Nuskaito {fields}',
    partialLine: 'Nuskaito {fields}. Dar ne: {gaps}.',
    awaitingLine: 'Dar nepalaikoma. Trūkstami šaltiniai pridedami pagal užklausą.',
    requestIt: 'Pateikti užklausą',
    requestedLine: 'Užklausa pateikta {date} · pranešime, kai bus paruošta.',
    needsConnection: 'Reikia {whose} {system} ryšio',
    connect: 'Prijungti {system}',
    askOwner: 'Paklausti {owner}',
    coverageSummary: 'Dabar renkami {n} šaltiniai; {m} bus įtraukti, kai taps palaikomi.',
    coverageSummaryOne: 'Dabar renkamas {n} šaltinis; {m} bus įtraukti, kai taps palaikomi.',
    scheduleHeading: 'Tvarkaraštis ir biudžetas',
    scheduleTime: 'Vykdoma kasdien',
    scheduleDaily: 'Vykdoma kasdien {time}',
    scheduleEveryHour: 'Vykdoma kas valandą',
    scheduleEveryMinutes: 'Vykdoma kas {minutes} min.',
    scheduleTimezone: 'Laiko juosta',
    budgetPerRun: 'Daugiausia išleisti vienam vykdymui',
    budgetMonthly: 'Daugiausia išleisti per mėnesį',
    scheduleSave: 'Išsaugoti tvarkaraštį',
    scheduleSaving: 'Išsaugoma…',
    scheduleSummary:
      'Kasdien {time} {zone} · iki {perRun} vienam vykdymui · iki {monthly} per mėnesį',
    oneRunPerDay:
      'Vienas vykdymas per dieną. Pakartotas ar iš naujo paleistas vykdymas niekada neskaičiuojamas du kartus.',
    errTime: 'Įveskite laiką, pvz., 07:00.',
    errCredits: 'Įveskite sveikąjį skaičių nuo {min} iki {max}.',
    errOrder: 'Mėnesio limitas turi būti ne mažesnis už vieno vykdymo limitą.',
    collecting: 'Renkama kasdien {time} · paskutinį kartą nuskaityta {last} · {rows} eilučių',
    collectingNever: 'Renkama kasdien {time} · dar nenuskaityta',
    awaitingData: 'Užklausa pateikta {date} · dar nerenkama',
    stopped: 'Sustabdyta: {reason}',
    reconnect: 'Prisijungti iš naujo',
    skippedBudget:
      'Šiandien praleistos {skipped} iš {total} kategorijų: pasiektas mėnesio biudžetas.',
    gapMarker: 'Šiuo laikotarpiu duomenų nėra',
    gapBudget: 'Praleista: pasiektas mėnesio biudžetas',
    gapUnsupported: 'Praleista: dar nepalaikoma',
    gapFailed: 'Praleista: šaltinis neatsakė',
    chartLabel:
      'Kainos kitimas laikui bėgant. Praleistas laikotarpis reiškia linijos pertrūkį, o ne nulinę reikšmę.',
    firstRunTitle: 'Pirmasis vykdymas baigtas',
    firstRun:
      'Nuskaityti {read} šaltiniai · {unsupported} dar nepalaikomi · kaina {cost} · kitas vykdymas {next}',
    firstRunUnmeasured:
      'Nuskaityti {read} šaltiniai · {unsupported} dar nepalaikomi · kaina dar neišmatuota · kitas vykdymas {next}',
    firstRunNoNext:
      'Nuskaityti {read} šaltiniai · {unsupported} dar nepalaikomi · kaina {cost} · vykdymas nesuplanuotas',
    lastRunNone: 'Vykdymų dar nebuvo. Pirmasis bus atliktas suplanuotu laiku.',
    paused: 'Pristabdyta',
    pause: 'Pristabdyti',
    resume: 'Tęsti',
    historyFrom: 'Istorija iš {source} nuo {since} (datos nurodytos šaltinyje)',
    historyImportedMark: 'Importuota',
    historyToday: 'Istorija prasideda šiandien, {date}.',
    budgetSave: 'Išsaugoti biudžetą',
    creditsUnit: 'kreditų',
    budgetInvalid: 'Šių ribų negalima išsaugoti. Patikrinkite jas ir bandykite dar kartą.',
    budgetSummary: 'Iki {perRun} vienam vykdymui · iki {monthly} per mėnesį',
    budgetNone: 'Biudžetas dar nenustatytas.',
    budgetUnavailable: 'Biudžetas šiuo metu nepasiekiamas.',
    budgetDenied: 'Negalite keisti šio biudžeto.',
    budgetChanged:
      'Biudžetas pasikeitė nuo tada, kai jį atidarėte. Patikrinkite limitus ir bandykite dar kartą.',
    budgetUnknown:
      'Nepavyko patvirtinti, ar jis išsaugotas. Bandykite dar kartą; jis nebus pritaikytas du kartus.',
    scheduleFacts: 'Vykdoma {schedule} ({zone})',
    scheduleNext: 'Kitas vykdymas {next}',
    scheduleOn: 'Įjungta',
    scheduleNotScheduled: 'Nes suplanuota',
    scheduleEnable: 'Įjungti',
    scheduleUnavailable: 'Tvarkaraštis šiuo metu nepasiekiamas.',
    scheduleDenied: 'Negalite keisti šio tvarkaraščio.',
    scheduleConflict:
      'Tvarkaraštis pasikeitė nuo tada, kai jį atidarėte. Patikrinkite ir bandykite dar kartą.',
    scheduleUnknown:
      'Nepavyko patvirtinti, ar tvarkaraštis pasikeitė. Bandykite dar kartą; pakeitimas nebus pritaikytas du kartus.',
    budgetHeading: 'Biudžetas',
    scheduleOnlyHeading: 'Tvarkaraštis',
    scheduleJobOrdinal: 'Suplanuotas paleidimas {n}',
    budgetUnconfirmed: 'Nepavyko patvirtinti, ar jis išsaugotas.',
    scheduleUnconfirmed: 'Nepavyko patvirtinti, ar tvarkaraštis pasikeitė.',
    scheduleTimeNote: 'Laikai nurodyti paties tvarkaraščio laiko juosta.',
    scheduleWeekdays: 'Veikia darbo dienomis {time}',
    scheduleWeekly: 'Veikia kas savaitę: {days} {time}',
    scheduleCustom: 'Veikia pagal savo tvarkaraštį',
    scheduleThen: 'Tada {runs}',
    scheduleChange: 'Keisti tvarkaraštį',
    scheduleChoose: 'Pasirinkti kitą laiką',
    pickerHeading: 'Kada veikia',
    presetEvery15: 'Kas 15 minučių',
    presetEvery30: 'Kas 30 minučių',
    presetHourly: 'Kas valandą',
    presetDaily: 'Kasdien',
    presetWeekdays: 'Darbo dienomis',
    presetWeekly: 'Kas savaitę',
    presetCustom: 'Pasirinktinis',
    pickerTime: 'Laikas',
    pickerDay: 'Diena',
    pickerCron: 'Cron išraiška',
    pickerCronHelp:
      'Penki laukai: minutė, valanda, mėnesio diena, mėnuo ir savaitės diena, pavyzdžiui, 0 9 1 * * reiškia 09:00 pirmą kiekvieno mėnesio dieną.',
    pickerCustomPreview: 'Išsaugant šis tvarkaraštis patikrinamas ir parodomi kiti jo paleidimai.',
    pickerCancel: 'Atšaukti',
    scheduleTooFrequent: 'Paleidimai gali vykti ne dažniau kaip kas 15 minučių.',
    scheduleInvalidChoice:
      'Šio tvarkaraščio negalima naudoti. Patikrinkite išraišką ir laiko juostą.',
    scheduleBusy: 'Vyksta paleidimas. Bandykite dar kartą, kai jis baigsis.',
    runsHeading: 'Naujausi paleidimai',
    runsEmpty: 'Paleidimų dar nėra.',
    runsUnavailable: 'Naujausi paleidimai šiuo metu nepasiekiami.',
    runSucceeded: 'Pavyko',
    runFailed: 'Nepavyko',
    runUnknown: 'Nepatvirtinta',
    runManual: 'Paleista rankiniu būdu',
    runScheduled: 'Pagal tvarkaraštį',
    runWebhook: 'Webhook',
    runEvent: 'Duomenų pakeitimas',
    runShowLog: 'Rodyti žurnalą',
    runHideLog: 'Slėpti žurnalą',
    runLogEmpty: 'Nieko neišspausdino.',
    runLogDropped: 'Ankstesnės eilutės neišsaugotos: {n}.',
    runError: 'Klaida',
    runPrinted: 'Ką išspausdino',
    runInput: 'Įvestis',
    runLogUnavailable: 'Žurnalas šiuo metu nepasiekiamas.',
    runFix: 'Taisyti pokalbyje',
    runFixFailed: 'Nepavyko atidaryti programos pokalbio. Bandykite dar kartą.',
    runsMore: 'Įkelti daugiau',
    fixScheduled:
      'Šios programos suplanuotas paleidimas nepavyko. Raskite priežastį ir ištaisykite.',
    fixTriggered: 'Šios programos paleidimas nepavyko. Raskite priežastį ir ištaisykite.',
    fixUnknown:
      'Šios programos paleidimas baigėsi be patvirtinto rezultato. Patikrinkite, kas įvyko, ir ištaisykite.',
    fixRunLine: 'Funkcija {name} · paleidimas {id} · {time}',
    fixError: 'Klaida',
    fixPrinted: 'Paskutinės išspausdintos eilutės',
    fixInput: 'Jos įvestis',
  },
  lv: {
    sourcesHeading: 'Ko izsekot',
    sourcesNote:
      'Avoti tiek iegūti no sertificētām Tool Store pakotnēm, un trūkstošie avoti tiek pievienoti pēc pieprasījuma.',
    chooseSource: 'Izvēlēties {name}',
    readsLine: 'Nolasa {fields}',
    partialLine: 'Nolasa {fields}. Vēl nav: {gaps}.',
    awaitingLine: 'Vēl netiek atbalstīts. Trūkstošie avoti tiek pievienoti pēc pieprasījuma.',
    requestIt: 'Pieprasīt',
    requestedLine: 'Pieprasīts {date} · paziņosim, kad tas būs gatavs.',
    needsConnection: 'Nepieciešams {whose} {system} savienojums',
    connect: 'Savienot {system}',
    askOwner: 'Jautāt {owner}',
    coverageSummary: '{n} avoti pašlaik apkopo datus; {m} tiks pievienoti, kad tos atbalstīs.',
    coverageSummaryOne: '{n} avots pašlaik apkopo datus; {m} tiks pievienoti, kad tos atbalstīs.',
    scheduleHeading: 'Grafiks un budžets',
    scheduleTime: 'Darbojas katru dienu plkst.',
    scheduleDaily: 'Darbojas katru dienu plkst. {time}',
    scheduleEveryHour: 'Darbojas katru stundu',
    scheduleEveryMinutes: 'Darbojas ik pēc {minutes} minūtēm',
    scheduleTimezone: 'Laika josla',
    budgetPerRun: 'Maksimālā summa vienai palaišanai',
    budgetMonthly: 'Maksimālā summa mēnesī',
    scheduleSave: 'Saglabāt grafiku',
    scheduleSaving: 'Notiek saglabāšana…',
    scheduleSummary:
      'Katru dienu plkst. {time} {zone} · līdz {perRun} vienā palaišanas reizē · līdz {monthly} mēnesī',
    oneRunPerDay:
      'Viena palaišanas reize dienā. Atkārtota vai atkārtoti mēģināta palaišanas reize netiek ieskaitīta divreiz.',
    errTime: 'Ievadiet laiku, piemēram, 07:00.',
    errCredits: 'Ievadiet veselu skaitli no {min} līdz {max}.',
    errOrder: 'Mēneša ierobežojumam jābūt vismaz vienas palaišanas reizes ierobežojuma apmērā.',
    collecting: 'Apkopo katru dienu plkst. {time} · pēdējā nolasīšana {last} · {rows} rindas',
    collectingNever: 'Apkopo katru dienu plkst. {time} · vēl nav nolasīts',
    awaitingData: 'Pieprasīts {date} · vēl netiek apkopots',
    stopped: 'Apturēts: {reason}',
    reconnect: 'Atkārtoti izveidot savienojumu',
    skippedBudget: 'Šodien izlaistas {skipped} no {total} kategorijām: sasniegts mēneša budžets.',
    gapMarker: 'Šajā periodā datu nav',
    gapBudget: 'Izlaists: sasniegts mēneša budžets',
    gapUnsupported: 'Izlaists: vēl netiek atbalstīts',
    gapFailed: 'Izlaists: avots neatbildēja',
    chartLabel: 'Cena laika gaitā. Izlaists periods grafikā ir pārtraukums, nevis nulle.',
    firstRunTitle: 'Pirmā palaišanas reize pabeigta',
    firstRun:
      'Nolasīti {read} avoti · {unsupported} vēl netiek atbalstīti · izmaksas {cost} · nākamā palaišanas reize {next}',
    firstRunUnmeasured:
      'Nolasīti {read} avoti · {unsupported} vēl netiek atbalstīti · izmaksas vēl nav izmērītas · nākamā palaišanas reize {next}',
    firstRunNoNext:
      'Nolasīti {read} avoti · {unsupported} vēl netiek atbalstīti · izmaksas {cost} · palaišanas reize nav ieplānota',
    lastRunNone: 'Vēl nav bijusi neviena palaišanas reize. Pirmā tiks veikta ieplānotajā laikā.',
    paused: 'Apturēts',
    pause: 'Apturēt',
    resume: 'Atsākt',
    historyFrom: 'Vēsture no {source} kopš {since} (datumi no avota)',
    historyImportedMark: 'Importēts',
    historyToday: 'Vēsture sākas šodien, {date}.',
    budgetSave: 'Saglabāt budžetu',
    creditsUnit: 'kredīti',
    budgetInvalid: 'Šos ierobežojumus nevar saglabāt. Pārbaudiet tos un mēģiniet vēlreiz.',
    budgetSummary: 'Līdz {perRun} vienā palaišanas reizē · līdz {monthly} mēnesī',
    budgetNone: 'Budžets vēl nav iestatīts.',
    budgetUnavailable: 'Budžets pašlaik nav pieejams.',
    budgetDenied: 'Jums nav atļauts mainīt šo budžetu.',
    budgetChanged:
      'Budžets ir mainījies kopš šīs lapas atvēršanas. Pārbaudiet ierobežojumus un mēģiniet vēlreiz.',
    budgetUnknown:
      'Nevarējām apstiprināt, vai tas tika saglabāts. Mēģiniet vēlreiz; tas netiks lietots divreiz.',
    scheduleFacts: 'Darbojas {schedule} ({zone})',
    scheduleNext: 'Nākamā palaišanas reize {next}',
    scheduleOn: 'Ieslēgts',
    scheduleNotScheduled: 'Nav ieplānots',
    scheduleEnable: 'Ieslēgt',
    scheduleUnavailable: 'Grafiks pašlaik nav pieejams.',
    scheduleDenied: 'Jūs nevarat mainīt šo grafiku.',
    scheduleConflict: 'Kopš tā atvēršanas grafiks ir mainījies. Pārbaudiet to un mēģiniet vēlreiz.',
    scheduleUnknown:
      'Neizdevās apstiprināt, vai grafiks ir mainījies. Mēģiniet vēlreiz; izmaiņas netiks lietotas atkārtoti.',
    budgetHeading: 'Budžets',
    scheduleOnlyHeading: 'Grafiks',
    scheduleJobOrdinal: 'Ieplānota palaišana {n}',
    budgetUnconfirmed: 'Neizdevās apstiprināt, vai tas tika saglabāts.',
    scheduleUnconfirmed: 'Neizdevās apstiprināt, vai grafiks ir mainījies.',
    scheduleTimeNote: 'Laiki ir paša grafika laika joslā.',
    scheduleWeekdays: 'Darbojas darbdienās plkst. {time}',
    scheduleWeekly: 'Darbojas reizi nedēļā: {days} plkst. {time}',
    scheduleCustom: 'Darbojas pēc sava grafika',
    scheduleThen: 'Pēc tam {runs}',
    scheduleChange: 'Mainīt grafiku',
    scheduleChoose: 'Izvēlēties citu laiku',
    pickerHeading: 'Kad tas darbojas',
    presetEvery15: 'Ik pēc 15 minūtēm',
    presetEvery30: 'Ik pēc 30 minūtēm',
    presetHourly: 'Katru stundu',
    presetDaily: 'Katru dienu',
    presetWeekdays: 'Darbdienās',
    presetWeekly: 'Katru nedēļu',
    presetCustom: 'Pielāgots',
    pickerTime: 'Laiks',
    pickerDay: 'Diena',
    pickerCron: 'Cron izteiksme',
    pickerCronHelp:
      'Pieci lauki: minūte, stunda, mēneša diena, mēnesis un nedēļas diena, piemēram, 0 9 1 * * nozīmē plkst. 09:00 katra mēneša pirmajā dienā.',
    pickerCustomPreview:
      'Saglabājot šis grafiks tiek pārbaudīts un tiek parādītas nākamās palaišanas reizes.',
    pickerCancel: 'Atcelt',
    scheduleTooFrequent: 'Palaišana var notikt ne biežāk kā ik pēc 15 minūtēm.',
    scheduleInvalidChoice: 'Šo grafiku nevar izmantot. Pārbaudiet izteiksmi un laika joslu.',
    scheduleBusy: 'Palaišana notiek. Mēģiniet vēlreiz, kad tā būs beigusies.',
    runsHeading: 'Nesenās palaišanas reizes',
    runsEmpty: 'Palaišanas reižu vēl nav.',
    runsUnavailable: 'Nesenās palaišanas reizes šobrīd nav pieejamas.',
    runSucceeded: 'Veiksmīgi',
    runFailed: 'Neveiksmīgi',
    runUnknown: 'Nav apstiprināts',
    runManual: 'Palaists manuāli',
    runScheduled: 'Pēc grafika',
    runWebhook: 'Webhook',
    runEvent: 'Datu izmaiņas',
    runShowLog: 'Rādīt žurnālu',
    runHideLog: 'Slēpt žurnālu',
    runLogEmpty: 'Nekas netika izdrukāts.',
    runLogDropped: '{n} iepriekšējās rindas netika saglabātas.',
    runError: 'Kļūda',
    runPrinted: 'Kas izdrukāts',
    runInput: 'Ievade',
    runLogUnavailable: 'Žurnāls šobrīd nav pieejams.',
    runFix: 'Labot tērzēšanā',
    runFixFailed: 'Neizdevās atvērt lietotnes tērzēšanu. Mēģiniet vēlreiz.',
    runsMore: 'Ielādēt vairāk',
    fixScheduled:
      'Šīs lietotnes ieplānotā palaišana neizdevās. Lūdzu, atrodiet cēloni un izlabojiet to.',
    fixTriggered: 'Šīs lietotnes palaišana neizdevās. Lūdzu, atrodiet cēloni un izlabojiet to.',
    fixUnknown:
      'Šīs lietotnes palaišana beidzās bez apstiprināta rezultāta. Lūdzu, pārbaudiet, kas notika, un izlabojiet to.',
    fixRunLine: 'Funkcija {name} · palaišana {id} · {time}',
    fixError: 'Kļūda',
    fixPrinted: 'Pēdējās izdrukātās rindas',
    fixInput: 'Tās ievade',
  },
  mt: {
    sourcesHeading: 'X’għandu jiġi segwit',
    sourcesNote:
      'Is-sorsi jiġu minn pakketti ċċertifikati tat-Tool Store, u dawk neqsin jiżdiedu fuq talba.',
    chooseSource: 'Agħżel {name}',
    readsLine: 'Jaqra {fields}',
    partialLine: 'Jaqra {fields}. Għadhom neqsin: {gaps}.',
    awaitingLine: 'Għadu mhux appoġġjat. Is-sorsi neqsin jiżdiedu fuq talba.',
    requestIt: 'Itlobh',
    requestedLine: 'Intalab {date} · ngħarrfuk meta jkun lest.',
    needsConnection: 'Jeħtieġ konnessjoni ta’ {whose} ma’ {system}',
    connect: 'Qabbad ma’ {system}',
    askOwner: 'Staqsi lil {owner}',
    coverageSummary: '{n} sorsi jinġabru issa; {m} se jiżdiedu meta jkunu appoġġjati.',
    coverageSummaryOne: 'Issa jinġabru {n} sorsi; {m} se jingħaqdu meta jiġu appoġġjati.',
    scheduleHeading: 'Skeda u baġit',
    scheduleTime: 'Jitħaddem kuljum fis-',
    scheduleDaily: 'Jitħaddem kuljum fis-{time}',
    scheduleEveryHour: 'Jitħaddem kull siegħa',
    scheduleEveryMinutes: 'Jitħaddem kull {minutes} minuta',
    scheduleTimezone: 'Żona tal-ħin',
    budgetPerRun: 'L-aktar li jista’ jintefaq f’tħaddim wieħed',
    budgetMonthly: 'L-aktar li jista’ jintefaq f’xahar',
    scheduleSave: 'Issejvja l-iskeda',
    scheduleSaving: 'Qed tiġi ssejvjata…',
    scheduleSummary:
      'Kuljum fis-{time} {zone} · sa {perRun} għal kull tħaddim · sa {monthly} fix-xahar',
    oneRunPerDay:
      'Tħaddim wieħed kuljum. Tħaddim ripetut jew ippruvat mill-ġdid qatt ma jingħadd darbtejn.',
    errTime: 'Daħħal ħin bħal 07:00.',
    errCredits: 'Daħħal numru sħiħ minn {min} sa {max}.',
    errOrder: 'Il-limitu ta’ kull xahar irid ikun tal-inqas daqs il-limitu għal tħaddim wieħed.',
    collecting: 'Qed jinġabar kuljum fis-{time} · l-aħħar qari {last} · {rows} ringiela',
    collectingNever: 'Qed jinġabar kuljum fis-{time} · għadu ma nqara xejn',
    awaitingData: 'Intalab {date} · għadu ma beda jinġabar',
    stopped: 'Imwaqqaf: {reason}',
    reconnect: 'Erġa’ qabbad',
    skippedBudget:
      'Illum inqabżu {skipped} minn {total} kategoriji: intlaħaq il-baġit ta’ kull xahar.',
    gapMarker: 'M’hemmx dejta għal dan il-perjodu',
    gapBudget: 'Inqabeż: intlaħaq il-baġit ta’ kull xahar',
    gapUnsupported: 'Inqabeż: għadu mhux appoġġjat',
    gapFailed: 'Inqabeż: is-sors ma weġibx',
    chartLabel: 'Prezz maż-żmien. Perjodu maqbuż huwa waqfa fil-linja, mhux żero.',
    firstRunTitle: 'L-ewwel tħaddim lest',
    firstRun:
      'Inqraw {read} sorsi · {unsupported} għadhom mhux appoġġjati · spiża {cost} · it-tħaddim li jmiss {next}',
    firstRunUnmeasured:
      'Inqraw {read} sorsi · {unsupported} għadhom mhux appoġġjati · l-ispiża għadha ma tkejjlitx · it-tħaddim li jmiss {next}',
    firstRunNoNext:
      'Inqraw {read} sorsi · {unsupported} għadhom mhux appoġġjati · spiża {cost} · m’hemm l-ebda tħaddim skedat',
    lastRunNone: 'Għadu ma sar l-ebda tħaddim. L-ewwel wieħed isir fil-ħin skedat.',
    paused: 'Imwaqqaf temporanjament',
    pause: 'Waqqaf temporanjament',
    resume: 'Kompli',
    historyFrom: 'Storja minn {source} minn {since} (id-dati huma mis-sors)',
    historyImportedMark: 'Importat',
    historyToday: 'L-istorja tibda llum, {date}.',
    budgetSave: 'Issejvja l-baġit',
    creditsUnit: 'krediti',
    budgetInvalid: 'Dawn il-limiti ma jistgħux jiġu ssejvjati. Iċċekkjahom u erġa’ pprova.',
    budgetSummary: 'Sa {perRun} għal kull tħaddim · sa {monthly} fix-xahar',
    budgetNone: 'Għadu ma ġie stabbilit l-ebda baġit.',
    budgetUnavailable: 'Il-baġit bħalissa mhux disponibbli.',
    budgetDenied: 'Ma tistax tibdel dan il-baġit.',
    budgetChanged: 'Il-baġit inbidel minn meta ftaħt dan. Iċċekkja l-limiti u erġa’ pprova.',
    budgetUnknown:
      'Ma stajniex nikkonfermaw jekk ġiex issejvjat. Erġa’ pprova; mhux se jiġi applikat darbtejn.',
    scheduleFacts: 'Jitħaddem {schedule} ({zone})',
    scheduleNext: 'It-tħaddim li jmiss {next}',
    scheduleOn: 'Mixgħul',
    scheduleNotScheduled: 'Mhux skedat',
    scheduleEnable: 'Ixgħel',
    scheduleUnavailable: 'L-iskeda mhijiex disponibbli bħalissa.',
    scheduleDenied: 'Ma tistax tibdel din l-iskeda.',
    scheduleConflict: 'L-iskeda nbidlet minn meta ftaħtha. Iċċekkjaha u erġa’ pprova.',
    scheduleUnknown:
      'Ma stajniex nikkonfermaw jekk l-iskeda nbidlitx. Erġa’ pprova; mhux se tiġi applikata darbtejn.',
    budgetHeading: 'Baġit',
    scheduleOnlyHeading: 'Skeda',
    scheduleJobOrdinal: 'Tħaddim skedat {n}',
    budgetUnconfirmed: 'Ma stajniex nikkonfermaw jekk ġiex salvat.',
    scheduleUnconfirmed: 'Ma stajniex nikkonfermaw jekk l-iskeda nbidlitx.',
    scheduleTimeNote: 'Il-ħinijiet huma fiż-żona tal-ħin tal-iskeda nnifisha.',
    scheduleWeekdays: 'Taħdem fil-jiem tax-xogħol fis-{time}',
    scheduleWeekly: 'Taħdem kull {days} fis-{time}',
    scheduleCustom: 'Taħdem fuq skeda tagħha stess',
    scheduleThen: 'Imbagħad {runs}',
    scheduleChange: 'Biddel l-iskeda',
    scheduleChoose: 'Agħżel ħin ieħor',
    pickerHeading: 'Meta taħdem',
    presetEvery15: 'Kull 15-il minuta',
    presetEvery30: 'Kull 30 minuta',
    presetHourly: 'Kull siegħa',
    presetDaily: 'Kuljum',
    presetWeekdays: 'Jiem tax-xogħol',
    presetWeekly: 'Kull ġimgħa',
    presetCustom: 'Personalizzata',
    pickerTime: 'Ħin',
    pickerDay: 'Jum',
    pickerCron: 'Espressjoni cron',
    pickerCronHelp:
      'Ħames oqsma: minuta, siegħa, jum tax-xahar, xahar u jum tal-ġimgħa, pereżempju 0 9 1 * * għad-09:00 fl-ewwel jum ta’ kull xahar.',
    pickerCustomPreview:
      'Meta taħżen, din l-iskeda tiġi ċċekkjata u jintwerew it-tħaddim li jmiss.',
    pickerCancel: 'Ikkanċella',
    scheduleTooFrequent: 'It-tħaddim jista’ jkun l-aktar kull 15-il minuta.',
    scheduleInvalidChoice:
      'Din l-iskeda ma tistax tintuża. Iċċekkja l-espressjoni u ż-żona tal-ħin.',
    scheduleBusy: 'Tħaddim għaddej. Erġa’ pprova meta jintemm.',
    runsHeading: 'Tħaddim riċenti',
    runsEmpty: 'Għad m’hemm l-ebda tħaddim.',
    runsUnavailable: 'It-tħaddim riċenti mhuwiex disponibbli bħalissa.',
    runSucceeded: 'Irnexxa',
    runFailed: 'Falla',
    runUnknown: 'Mhux ikkonfermat',
    runManual: 'Bl-idejn',
    runScheduled: 'Skedat',
    runWebhook: 'Webhook',
    runEvent: 'Bidla fid-dejta',
    runShowLog: 'Uri r-reġistru',
    runHideLog: 'Aħbi r-reġistru',
    runLogEmpty: 'Ma stampa xejn.',
    runLogDropped: '{n} linji preċedenti ma nżammux.',
    runError: 'Żball',
    runPrinted: 'Dak li stampa',
    runInput: 'Input',
    runLogUnavailable: 'Ir-reġistru mhuwiex disponibbli bħalissa.',
    runFix: 'Sewwi fiċ-chat',
    runFixFailed: 'Iċ-chat tal-app ma setax jinfetaħ. Erġa’ pprova.',
    runsMore: 'Ittella’ aktar',
    fixScheduled: 'It-tħaddim skedat ta’ din l-app falla. Sib il-kawża u sewwiha.',
    fixTriggered: 'Tħaddim ta’ din l-app falla. Sib il-kawża u sewwiha.',
    fixUnknown:
      'Tħaddim ta’ din l-app intemm mingħajr riżultat ikkonfermat. Iċċekkja x’ġara u sewwih.',
    fixRunLine: 'Funzjoni {name} · tħaddim {id} · {time}',
    fixError: 'Żball',
    fixPrinted: 'L-aħħar linji li stampa',
    fixInput: 'L-input tagħha',
  },
  nl: {
    sourcesHeading: 'Wat moet worden bijgehouden',
    sourcesNote:
      'Bronnen zijn afkomstig uit gecertificeerde Tool Store-pakketten. Ontbrekende bronnen worden op verzoek toegevoegd.',
    chooseSource: 'Kies {name}',
    readsLine: 'Leest {fields}',
    partialLine: 'Leest {fields}. Nog niet: {gaps}.',
    awaitingLine: 'Nog niet ondersteund. Ontbrekende bronnen worden op verzoek toegevoegd.',
    requestIt: 'Aanvragen',
    requestedLine: 'Aangevraagd op {date} · we laten je weten wanneer het klaar is.',
    needsConnection: 'Heeft de verbinding van {whose} met {system} nodig',
    connect: '{system} verbinden',
    askOwner: 'Vraag het aan {owner}',
    coverageSummary:
      '{n} bronnen verzamelen nu gegevens; {m} worden toegevoegd zodra ze worden ondersteund.',
    coverageSummaryOne:
      '{n} bron verzamelt nu gegevens; {m} worden toegevoegd zodra ze worden ondersteund.',
    scheduleHeading: 'Planning en budget',
    scheduleTime: 'Elke dag om',
    scheduleDaily: 'Wordt elke dag om {time} uitgevoerd',
    scheduleEveryHour: 'Wordt elk uur uitgevoerd',
    scheduleEveryMinutes: 'Wordt elke {minutes} minuten uitgevoerd',
    scheduleTimezone: 'Tijdzone',
    budgetPerRun: 'Maximaal uit te geven per uitvoering',
    budgetMonthly: 'Maximaal uit te geven per maand',
    scheduleSave: 'Planning opslaan',
    scheduleSaving: 'Opslaan…',
    scheduleSummary:
      'Dagelijks om {time} {zone} · maximaal {perRun} per uitvoering · maximaal {monthly} per maand',
    oneRunPerDay:
      'Eén uitvoering per dag. Een herhaalde of opnieuw uitgevoerde uitvoering telt nooit dubbel.',
    errTime: 'Voer een tijd in, zoals 07:00.',
    errCredits: 'Voer een heel getal in van {min} tot {max}.',
    errOrder: 'De maandelijkse limiet moet minstens gelijk zijn aan de limiet per uitvoering.',
    collecting: 'Dagelijks gegevens verzamelen om {time} · laatste uitlezing {last} · {rows} rijen',
    collectingNever: 'Dagelijks gegevens verzamelen om {time} · nog niet uitgelezen',
    awaitingData: 'Aangevraagd op {date} · nog geen gegevens verzameld',
    stopped: 'Gestopt: {reason}',
    reconnect: 'Opnieuw verbinden',
    skippedBudget:
      'Vandaag zijn {skipped} van {total} categorieën overgeslagen: het maandbudget is bereikt.',
    gapMarker: 'Geen gegevens voor deze periode',
    gapBudget: 'Overgeslagen: maandbudget bereikt',
    gapUnsupported: 'Overgeslagen: nog niet ondersteund',
    gapFailed: 'Overgeslagen: de bron reageerde niet',
    chartLabel:
      'Prijs in de loop van de tijd. Een overgeslagen periode is een onderbreking in de lijn, geen nulwaarde.',
    firstRunTitle: 'Eerste uitvoering voltooid',
    firstRun:
      'Gegevens gelezen uit {read} bronnen · {unsupported} nog niet ondersteund · kosten {cost} · volgende uitvoering {next}',
    firstRunUnmeasured:
      'Gegevens gelezen uit {read} bronnen · {unsupported} nog niet ondersteund · kosten nog niet gemeten · volgende uitvoering {next}',
    firstRunNoNext:
      'Gegevens gelezen uit {read} bronnen · {unsupported} nog niet ondersteund · kosten {cost} · er is geen uitvoering gepland',
    lastRunNone: 'Nog geen uitvoering. De eerste uitvoering vindt plaats op het geplande tijdstip.',
    paused: 'Gepauzeerd',
    pause: 'Pauzeren',
    resume: 'Hervatten',
    historyFrom: 'Geschiedenis van {source} sinds {since} (datums uit de bron)',
    historyImportedMark: 'Geïmporteerd',
    historyToday: 'De geschiedenis begint vandaag, {date}.',
    budgetSave: 'Budget opslaan',
    creditsUnit: 'credits',
    budgetInvalid:
      'Deze limieten kunnen niet worden opgeslagen. Controleer ze en probeer het opnieuw.',
    budgetSummary: 'Maximaal {perRun} per uitvoering · maximaal {monthly} per maand',
    budgetNone: 'Er is nog geen budget ingesteld.',
    budgetUnavailable: 'Het budget is momenteel niet beschikbaar.',
    budgetDenied: 'Je kunt dit budget niet wijzigen.',
    budgetChanged:
      'Het budget is gewijzigd sinds je dit hebt geopend. Controleer de limieten en probeer het opnieuw.',
    budgetUnknown:
      'We konden niet bevestigen of het is opgeslagen. Probeer het opnieuw; het wordt niet twee keer toegepast.',
    scheduleFacts: 'Uitvoeringen {schedule} ({zone})',
    scheduleNext: 'Volgende uitvoering {next}',
    scheduleOn: 'Aan',
    scheduleNotScheduled: 'Niet gepland',
    scheduleEnable: 'Inschakelen',
    scheduleUnavailable: 'De planning is momenteel niet beschikbaar.',
    scheduleDenied: 'Je kunt deze planning niet wijzigen.',
    scheduleConflict:
      'De planning is gewijzigd nadat je deze had geopend. Controleer de planning en probeer het opnieuw.',
    scheduleUnknown:
      'We konden niet bevestigen of de planning is gewijzigd. Probeer het opnieuw; de wijziging wordt niet dubbel toegepast.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Planning',
    scheduleJobOrdinal: 'Geplande uitvoering {n}',
    budgetUnconfirmed: 'We konden niet bevestigen of het budget is opgeslagen.',
    scheduleUnconfirmed: 'We konden niet bevestigen of de planning is gewijzigd.',
    scheduleTimeNote: 'Tijden gelden in de eigen tijdzone van de planning.',
    scheduleWeekdays: 'Wordt op werkdagen om {time} uitgevoerd',
    scheduleWeekly: 'Wordt elke {days} om {time} uitgevoerd',
    scheduleCustom: 'Wordt volgens een eigen planning uitgevoerd',
    scheduleThen: 'Daarna {runs}',
    scheduleChange: 'Planning wijzigen',
    scheduleChoose: 'Kies een ander tijdstip',
    pickerHeading: 'Wanneer het wordt uitgevoerd',
    presetEvery15: 'Elke 15 minuten',
    presetEvery30: 'Elke 30 minuten',
    presetHourly: 'Elk uur',
    presetDaily: 'Elke dag',
    presetWeekdays: 'Werkdagen',
    presetWeekly: 'Elke week',
    presetCustom: 'Aangepast',
    pickerTime: 'Tijd',
    pickerDay: 'Dag',
    pickerCron: 'Cron-expressie',
    pickerCronHelp:
      'Vijf velden: minuut, uur, dag van de maand, maand en dag van de week, bijvoorbeeld 0 9 1 * * voor 09:00 op de eerste van elke maand.',
    pickerCustomPreview:
      'Bij het opslaan wordt deze planning gecontroleerd en worden de volgende uitvoeringen getoond.',
    pickerCancel: 'Annuleren',
    scheduleTooFrequent: 'Uitvoeringen kunnen hooguit elke 15 minuten plaatsvinden.',
    scheduleInvalidChoice:
      'Die planning kan niet worden gebruikt. Controleer de expressie en de tijdzone.',
    scheduleBusy: 'Er is een uitvoering bezig. Probeer het opnieuw als die klaar is.',
    runsHeading: 'Recente uitvoeringen',
    runsEmpty: 'Nog geen uitvoeringen.',
    runsUnavailable: 'Recente uitvoeringen zijn momenteel niet beschikbaar.',
    runSucceeded: 'Geslaagd',
    runFailed: 'Mislukt',
    runUnknown: 'Niet bevestigd',
    runManual: 'Handmatig uitgevoerd',
    runScheduled: 'Gepland',
    runWebhook: 'Webhook',
    runEvent: 'Gegevenswijziging',
    runShowLog: 'Logboek tonen',
    runHideLog: 'Logboek verbergen',
    runLogEmpty: 'Er is niets afgedrukt.',
    runLogDropped: '{n} eerdere regels zijn niet bewaard.',
    runError: 'Fout',
    runPrinted: 'Wat er is afgedrukt',
    runInput: 'Invoer',
    runLogUnavailable: 'Het logboek is momenteel niet beschikbaar.',
    runFix: 'Herstellen in chat',
    runFixFailed: 'De chat van de app kon niet worden geopend. Probeer het opnieuw.',
    runsMore: 'Meer laden',
    fixScheduled: 'De geplande uitvoering van deze app is mislukt. Zoek de oorzaak en los het op.',
    fixTriggered: 'De uitvoering van deze app is mislukt. Zoek de oorzaak en los het op.',
    fixUnknown:
      'De uitvoering van deze app eindigde zonder bevestigd resultaat. Controleer wat er is gebeurd en los het op.',
    fixRunLine: 'Functie {name} · uitvoering {id} · {time}',
    fixError: 'Fout',
    fixPrinted: 'De laatste regels die zijn afgedrukt',
    fixInput: 'De invoer',
  },
  pl: {
    sourcesHeading: 'Co śledzić',
    sourcesNote:
      'Źródła pochodzą z certyfikowanych pakietów Tool Store, a brakujące są dodawane na żądanie.',
    chooseSource: 'Wybierz {name}',
    readsLine: 'Odczytuje: {fields}',
    partialLine: 'Odczytuje: {fields}. Brakuje jeszcze: {gaps}.',
    awaitingLine: 'Jeszcze nieobsługiwane. Brakujące źródła są dodawane na żądanie.',
    requestIt: 'Poproś o dodanie',
    requestedLine: 'Poproszono {date} · powiadomimy Cię, gdy będzie gotowe.',
    needsConnection: 'Wymaga połączenia {whose} z systemem {system}',
    connect: 'Połącz z systemem {system}',
    askOwner: 'Poproś {owner}',
    coverageSummary: 'Obecnie zbieranych jest {n} źródeł; {m} dołączy, gdy będą obsługiwane.',
    coverageSummaryOne: 'Obecnie zbierane jest {n} źródło; {m} dołączy, gdy będą obsługiwane.',
    scheduleHeading: 'Harmonogram i budżet',
    scheduleTime: 'Uruchamia się codziennie o',
    scheduleDaily: 'Uruchamia się codziennie o {time}',
    scheduleEveryHour: 'Uruchamia się co godzinę',
    scheduleEveryMinutes: 'Uruchamia się co {minutes} minut',
    scheduleTimezone: 'Strefa czasowa',
    budgetPerRun: 'Maksymalny koszt jednego uruchomienia',
    budgetMonthly: 'Maksymalny koszt w miesiącu',
    scheduleSave: 'Zapisz harmonogram',
    scheduleSaving: 'Zapisywanie…',
    scheduleSummary:
      'Codziennie o {time} {zone} · maks. {perRun} za uruchomienie · maks. {monthly} miesięcznie',
    oneRunPerDay:
      'Jedno uruchomienie dziennie. Powtórzone lub ponowione uruchomienie nigdy nie jest liczone podwójnie.',
    errTime: 'Wpisz godzinę, np. 07:00.',
    errCredits: 'Wpisz liczbę całkowitą od {min} do {max}.',
    errOrder: 'Limit miesięczny musi być co najmniej równy limitowi jednego uruchomienia.',
    collecting: 'Zbieranie codziennie o {time} · ostatni odczyt {last} · {rows} wierszy',
    collectingNever: 'Zbieranie codziennie o {time} · jeszcze nie odczytano',
    awaitingData: 'Poproszono {date} · zbieranie jeszcze się nie rozpoczęło',
    stopped: 'Zatrzymano: {reason}',
    reconnect: 'Połącz ponownie',
    skippedBudget:
      'Dziś pominięto {skipped} z {total} kategorii: miesięczny budżet został wyczerpany.',
    gapMarker: 'Brak danych za ten okres',
    gapBudget: 'Pominięto: miesięczny budżet został wyczerpany',
    gapUnsupported: 'Pominięto: jeszcze nieobsługiwane',
    gapFailed: 'Pominięto: źródło nie odpowiedziało',
    chartLabel: 'Cena w czasie. Pominięty okres oznacza przerwę na wykresie, a nie zero.',
    firstRunTitle: 'Pierwsze uruchomienie zakończone',
    firstRun:
      'Odczytano {read} źródeł · {unsupported} jeszcze nieobsługiwanych · koszt {cost} · następne uruchomienie {next}',
    firstRunUnmeasured:
      'Odczytano {read} źródeł · {unsupported} jeszcze nieobsługiwanych · koszt jeszcze niezmierzony · następne uruchomienie {next}',
    firstRunNoNext:
      'Odczytano {read} źródeł · {unsupported} jeszcze nieobsługiwanych · koszt {cost} · nie zaplanowano kolejnego uruchomienia',
    lastRunNone:
      'Nie wykonano jeszcze żadnego uruchomienia. Pierwsze odbędzie się o zaplanowanej godzinie.',
    paused: 'Wstrzymano',
    pause: 'Wstrzymaj',
    resume: 'Wznów',
    historyFrom: 'Historia z {source} od {since} (daty pochodzą ze źródła)',
    historyImportedMark: 'Zaimportowano',
    historyToday: 'Historia zaczyna się dziś, {date}.',
    budgetSave: 'Zapisz budżet',
    creditsUnit: 'kredytów',
    budgetInvalid: 'Nie można zapisać tych limitów. Sprawdź je i spróbuj ponownie.',
    budgetSummary: 'Maks. {perRun} za uruchomienie · maks. {monthly} miesięcznie',
    budgetNone: 'Nie ustawiono jeszcze budżetu.',
    budgetUnavailable: 'Budżet jest obecnie niedostępny.',
    budgetDenied: 'Nie możesz zmienić tego budżetu.',
    budgetChanged:
      'Budżet zmienił się od czasu otwarcia tej strony. Sprawdź limity i spróbuj ponownie.',
    budgetUnknown:
      'Nie udało się potwierdzić, czy budżet został zapisany. Spróbuj ponownie; nie zostanie zastosowany dwukrotnie.',
    scheduleFacts: 'Uruchamia się {schedule} ({zone})',
    scheduleNext: 'Następne uruchomienie {next}',
    scheduleOn: 'Włączony',
    scheduleNotScheduled: 'Nie zaplanowano',
    scheduleEnable: 'Włącz',
    scheduleUnavailable: 'Harmonogram jest teraz niedostępny.',
    scheduleDenied: 'Nie możesz zmienić tego harmonogramu.',
    scheduleConflict:
      'Harmonogram zmienił się od czasu jego otwarcia. Sprawdź go i spróbuj ponownie.',
    scheduleUnknown:
      'Nie udało się potwierdzić, czy harmonogram się zmienił. Spróbuj ponownie — zmiana nie zostanie zastosowana dwukrotnie.',
    budgetHeading: 'Budżet',
    scheduleOnlyHeading: 'Harmonogram',
    scheduleJobOrdinal: 'Zaplanowane uruchomienie {n}',
    budgetUnconfirmed: 'Nie udało się potwierdzić, czy zostało zapisane.',
    scheduleUnconfirmed: 'Nie udało się potwierdzić, czy harmonogram się zmienił.',
    scheduleTimeNote: 'Godziny podano w strefie czasowej harmonogramu.',
    scheduleWeekdays: 'Uruchamia się w dni robocze o {time}',
    scheduleWeekly: 'Uruchamia się co tydzień: {days}, o {time}',
    scheduleCustom: 'Uruchamia się według własnego harmonogramu',
    scheduleThen: 'Następnie {runs}',
    scheduleChange: 'Zmień harmonogram',
    scheduleChoose: 'Wybierz inną godzinę',
    pickerHeading: 'Kiedy się uruchamia',
    presetEvery15: 'Co 15 minut',
    presetEvery30: 'Co 30 minut',
    presetHourly: 'Co godzinę',
    presetDaily: 'Codziennie',
    presetWeekdays: 'Dni robocze',
    presetWeekly: 'Co tydzień',
    presetCustom: 'Własny',
    pickerTime: 'Godzina',
    pickerDay: 'Dzień',
    pickerCron: 'Wyrażenie cron',
    pickerCronHelp:
      'Pięć pól: minuta, godzina, dzień miesiąca, miesiąc i dzień tygodnia, na przykład 0 9 1 * * oznacza godzinę 9:00 pierwszego dnia każdego miesiąca.',
    pickerCustomPreview: 'Zapisanie sprawdza ten harmonogram i pokazuje kolejne uruchomienia.',
    pickerCancel: 'Anuluj',
    scheduleTooFrequent: 'Uruchomienia mogą następować najczęściej co 15 minut.',
    scheduleInvalidChoice: 'Tego harmonogramu nie można użyć. Sprawdź wyrażenie i strefę czasową.',
    scheduleBusy: 'Trwa uruchomienie. Spróbuj ponownie, gdy się zakończy.',
    runsHeading: 'Ostatnie uruchomienia',
    runsEmpty: 'Brak uruchomień.',
    runsUnavailable: 'Ostatnie uruchomienia są teraz niedostępne.',
    runSucceeded: 'Powodzenie',
    runFailed: 'Niepowodzenie',
    runUnknown: 'Niepotwierdzone',
    runManual: 'Ręcznie',
    runScheduled: 'Zaplanowane',
    runWebhook: 'Webhook',
    runEvent: 'Zmiana danych',
    runShowLog: 'Pokaż log',
    runHideLog: 'Ukryj log',
    runLogEmpty: 'Nic nie wypisano.',
    runLogDropped: 'Nie zachowano {n} wcześniejszych wierszy.',
    runError: 'Błąd',
    runPrinted: 'Co wypisano',
    runInput: 'Dane wejściowe',
    runLogUnavailable: 'Log jest teraz niedostępny.',
    runFix: 'Napraw na czacie',
    runFixFailed: 'Nie udało się otworzyć czatu aplikacji. Spróbuj ponownie.',
    runsMore: 'Wczytaj więcej',
    fixScheduled:
      'Zaplanowane uruchomienie tej aplikacji nie powiodło się. Znajdź przyczynę i napraw to.',
    fixTriggered: 'Uruchomienie tej aplikacji nie powiodło się. Znajdź przyczynę i napraw to.',
    fixUnknown:
      'Uruchomienie tej aplikacji zakończyło się bez potwierdzonego wyniku. Sprawdź, co się stało, i napraw to.',
    fixRunLine: 'Funkcja {name} · uruchomienie {id} · {time}',
    fixError: 'Błąd',
    fixPrinted: 'Ostatnie wypisane wiersze',
    fixInput: 'Jej dane wejściowe',
  },
  pt: {
    sourcesHeading: 'O que acompanhar',
    sourcesNote:
      'As fontes vêm de pacotes certificados da Loja de ferramentas, e as que estiverem faltando são adicionadas mediante solicitação.',
    chooseSource: 'Escolher {name}',
    readsLine: 'Lê {fields}',
    partialLine: 'Lê {fields}. Ainda não: {gaps}.',
    awaitingLine:
      'Ainda não compatível. As fontes que estiverem faltando são adicionadas mediante solicitação.',
    requestIt: 'Solicitar',
    requestedLine: 'Solicitado em {date} · avisaremos quando estiver pronto.',
    needsConnection: 'Precisa da conexão de {system} de {whose}',
    connect: 'Conectar {system}',
    askOwner: 'Pedir a {owner}',
    coverageSummary: '{n} fontes coletam dados agora; {m} serão incluídas quando houver suporte.',
    coverageSummaryOne: '{n} fonte coleta dados agora; {m} será incluída quando houver suporte.',
    scheduleHeading: 'Programação e orçamento',
    scheduleTime: 'Executa todos os dias às',
    scheduleDaily: 'Executa todos os dias às {time}',
    scheduleEveryHour: 'Executa a cada hora',
    scheduleEveryMinutes: 'Executa a cada {minutes} minutos',
    scheduleTimezone: 'Fuso horário',
    budgetPerRun: 'Máximo a gastar em uma execução',
    budgetMonthly: 'Máximo a gastar em um mês',
    scheduleSave: 'Salvar programação',
    scheduleSaving: 'Salvando…',
    scheduleSummary:
      'Diariamente às {time} {zone} · até {perRun} por execução · até {monthly} por mês',
    oneRunPerDay:
      'Uma execução por dia. Uma execução repetida ou refeita nunca é contabilizada duas vezes.',
    errTime: 'Insira um horário, como 07:00.',
    errCredits: 'Introduza um número inteiro de {min} a {max}.',
    errOrder: 'O limite mensal deve ser pelo menos igual ao limite de uma execução.',
    collecting: 'Coletando diariamente às {time} · última leitura {last} · {rows} linhas',
    collectingNever: 'Coletando diariamente às {time} · ainda não lido',
    awaitingData: 'Solicitado em {date} · ainda não coletando',
    stopped: 'Parado: {reason}',
    reconnect: 'Reconectar',
    skippedBudget:
      '{skipped} de {total} categorias ignoradas hoje: o orçamento mensal foi atingido.',
    gapMarker: 'Sem dados para este período',
    gapBudget: 'Ignorado: orçamento mensal atingido',
    gapUnsupported: 'Ignorado: ainda não compatível',
    gapFailed: 'Ignorado: a fonte não respondeu',
    chartLabel:
      'Preço ao longo do tempo. Um período ignorado é uma interrupção na linha, não zero.',
    firstRunTitle: 'Primeira execução concluída',
    firstRun:
      '{read} fontes lidas · {unsupported} ainda não compatíveis · custo {cost} · próxima execução {next}',
    firstRunUnmeasured:
      '{read} fontes lidas · {unsupported} ainda não compatíveis · custo ainda não medido · próxima execução {next}',
    firstRunNoNext:
      '{read} fontes lidas · {unsupported} ainda não compatíveis · custo {cost} · nenhuma execução programada',
    lastRunNone:
      'Ainda não houve nenhuma execução. A primeira será realizada no horário programado.',
    paused: 'Pausado',
    pause: 'Pausar',
    resume: 'Retomar',
    historyFrom: 'Histórico de {source} desde {since} (datas da fonte)',
    historyImportedMark: 'Importado',
    historyToday: 'O histórico começa hoje, {date}.',
    budgetSave: 'Salvar orçamento',
    creditsUnit: 'créditos',
    budgetInvalid: 'Não é possível guardar estes limites. Verifique-os e tente novamente.',
    budgetSummary: 'Até {perRun} por execução · até {monthly} por mês',
    budgetNone: 'Nenhum orçamento definido ainda.',
    budgetUnavailable: 'O orçamento não está disponível no momento.',
    budgetDenied: 'Você não pode alterar este orçamento.',
    budgetChanged:
      'O orçamento foi alterado desde que você abriu esta tela. Verifique os limites e tente novamente.',
    budgetUnknown:
      'Não foi possível confirmar se foi salvo. Tente novamente; ele não será aplicado duas vezes.',
    scheduleFacts: 'Executa {schedule} ({zone})',
    scheduleNext: 'Próxima execução {next}',
    scheduleOn: 'Ativada',
    scheduleNotScheduled: 'Não programada',
    scheduleEnable: 'Ativar',
    scheduleUnavailable: 'A agenda não está disponível no momento.',
    scheduleDenied: 'Você não pode alterar esta agenda.',
    scheduleConflict: 'A agenda mudou desde que você abriu isto. Verifique e tente novamente.',
    scheduleUnknown:
      'Não foi possível confirmar se a agenda mudou. Tente novamente; ela não será aplicada duas vezes.',
    budgetHeading: 'Orçamento',
    scheduleOnlyHeading: 'Agenda',
    scheduleJobOrdinal: 'Execução agendada {n}',
    budgetUnconfirmed: 'Não foi possível confirmar se isso foi salvo.',
    scheduleUnconfirmed: 'Não foi possível confirmar se a agenda mudou.',
    scheduleTimeNote: 'Os horários estão no fuso horário do próprio agendamento.',
    scheduleWeekdays: 'Executa em dias úteis às {time}',
    scheduleWeekly: 'Executa toda {days} às {time}',
    scheduleCustom: 'Executa em um agendamento próprio',
    scheduleThen: 'Depois {runs}',
    scheduleChange: 'Alterar agendamento',
    scheduleChoose: 'Escolher outro horário',
    pickerHeading: 'Quando executa',
    presetEvery15: 'A cada 15 minutos',
    presetEvery30: 'A cada 30 minutos',
    presetHourly: 'A cada hora',
    presetDaily: 'Todos os dias',
    presetWeekdays: 'Dias úteis',
    presetWeekly: 'Toda semana',
    presetCustom: 'Personalizado',
    pickerTime: 'Horário',
    pickerDay: 'Dia',
    pickerCron: 'Expressão cron',
    pickerCronHelp:
      'Cinco campos: minuto, hora, dia do mês, mês e dia da semana, por exemplo 0 9 1 * * para as 09:00 do primeiro dia de cada mês.',
    pickerCustomPreview:
      'Ao salvar, este agendamento é verificado e as próximas execuções são exibidas.',
    pickerCancel: 'Cancelar',
    scheduleTooFrequent: 'As execuções podem ocorrer, no máximo, a cada 15 minutos.',
    scheduleInvalidChoice:
      'Esse agendamento não pode ser usado. Verifique a expressão e o fuso horário.',
    scheduleBusy: 'Há uma execução em andamento. Tente novamente quando terminar.',
    runsHeading: 'Execuções recentes',
    runsEmpty: 'Nenhuma execução ainda.',
    runsUnavailable: 'As execuções recentes não estão disponíveis no momento.',
    runSucceeded: 'Concluída',
    runFailed: 'Falhou',
    runUnknown: 'Não confirmada',
    runManual: 'Manual',
    runScheduled: 'Agendada',
    runWebhook: 'Webhook',
    runEvent: 'Alteração de dados',
    runShowLog: 'Mostrar registro',
    runHideLog: 'Ocultar registro',
    runLogEmpty: 'Não imprimiu nada.',
    runLogDropped: '{n} linhas anteriores não foram mantidas.',
    runError: 'Erro',
    runPrinted: 'O que imprimiu',
    runInput: 'Entrada',
    runLogUnavailable: 'O registro não está disponível no momento.',
    runFix: 'Corrigir no chat',
    runFixFailed: 'Não foi possível abrir o chat do aplicativo. Tente novamente.',
    runsMore: 'Carregar mais',
    fixScheduled: 'A execução agendada deste aplicativo falhou. Encontre a causa e corrija.',
    fixTriggered: 'Uma execução deste aplicativo falhou. Encontre a causa e corrija.',
    fixUnknown:
      'Uma execução deste aplicativo terminou sem um resultado confirmado. Verifique o que aconteceu e corrija.',
    fixRunLine: 'Função {name} · execução {id} · {time}',
    fixError: 'Erro',
    fixPrinted: 'As últimas linhas que imprimiu',
    fixInput: 'Sua entrada',
  },
  ro: {
    sourcesHeading: 'Ce trebuie urmărit',
    sourcesNote:
      'Sursele provin din pachete certificate din Tool Store, iar cele lipsă sunt adăugate la cerere.',
    chooseSource: 'Alege {name}',
    readsLine: 'Citește {fields}',
    partialLine: 'Citește {fields}. Încă lipsesc: {gaps}.',
    awaitingLine: 'Încă nu este acceptat. Sursele lipsă sunt adăugate la cerere.',
    requestIt: 'Solicită',
    requestedLine: 'Solicitat la {date} · te vom anunța când este gata.',
    needsConnection: 'Necesită conexiunea {system} a {whose}',
    connect: 'Conectează {system}',
    askOwner: 'Întreabă-l pe {owner}',
    coverageSummary: '{n} surse colectează acum; {m} se vor alătura când vor fi acceptate.',
    coverageSummaryOne: '{n} sursă colectează acum; {m} se vor alătura când vor fi acceptate.',
    scheduleHeading: 'Program și buget',
    scheduleTime: 'Rulează zilnic la',
    scheduleDaily: 'Rulează zilnic la {time}',
    scheduleEveryHour: 'Rulează în fiecare oră',
    scheduleEveryMinutes: 'Rulează la fiecare {minutes} minute',
    scheduleTimezone: 'Fus orar',
    budgetPerRun: 'Suma maximă pentru o rulare',
    budgetMonthly: 'Suma maximă de cheltuit într-o lună',
    scheduleSave: 'Salvează programul',
    scheduleSaving: 'Se salvează…',
    scheduleSummary:
      'Zilnic la {time} {zone} · până la {perRun} per rulare · până la {monthly} pe lună',
    oneRunPerDay: 'O rulare pe zi. O rulare repetată sau reîncercată nu este numărată de două ori.',
    errTime: 'Introdu o oră precum 07:00.',
    errCredits: 'Introduceți un număr întreg între {min} și {max}.',
    errOrder: 'Limita lunară trebuie să fie cel puțin egală cu limita pentru o rulare.',
    collecting: 'Se colectează zilnic la {time} · ultima citire {last} · {rows} rânduri',
    collectingNever: 'Se colectează zilnic la {time} · încă nu s-a citit',
    awaitingData: 'Solicitat la {date} · încă nu se colectează',
    stopped: 'Oprit: {reason}',
    reconnect: 'Reconectează',
    skippedBudget:
      'Astăzi au fost omise {skipped} din {total} categorii: bugetul lunar a fost atins.',
    gapMarker: 'Nu există date pentru această perioadă',
    gapBudget: 'Omise: bugetul lunar a fost atins',
    gapUnsupported: 'Omise: încă nu sunt acceptate',
    gapFailed: 'Omise: sursa nu a răspuns',
    chartLabel: 'Prețul în timp. O perioadă omisă este o întrerupere a liniei, nu valoarea zero.',
    firstRunTitle: 'Prima rulare finalizată',
    firstRun:
      'Au fost citite {read} surse · {unsupported} încă nu sunt acceptate · cost {cost} · următoarea rulare {next}',
    firstRunUnmeasured:
      'Au fost citite {read} surse · {unsupported} încă nu sunt acceptate · costul nu este încă măsurat · următoarea rulare {next}',
    firstRunNoNext:
      'Au fost citite {read} surse · {unsupported} încă nu sunt acceptate · cost {cost} · nu este programată nicio rulare',
    lastRunNone: 'Nu există încă nicio rulare. Prima va avea loc la ora programată.',
    paused: 'În pauză',
    pause: 'Pune pe pauză',
    resume: 'Reia',
    historyFrom: 'Istoric din {source} de la {since} (datele provin din sursă)',
    historyImportedMark: 'Importat',
    historyToday: 'Istoricul începe astăzi, {date}.',
    budgetSave: 'Salvează bugetul',
    creditsUnit: 'credite',
    budgetInvalid: 'Aceste limite nu pot fi salvate. Verificați-le și încercați din nou.',
    budgetSummary: 'Până la {perRun} per rulare · până la {monthly} pe lună',
    budgetNone: 'Nu este setat încă niciun buget.',
    budgetUnavailable: 'Bugetul nu este disponibil momentan.',
    budgetDenied: 'Nu poți modifica acest buget.',
    budgetChanged:
      'Bugetul s-a schimbat de când ai deschis această pagină. Verifică limitele și încearcă din nou.',
    budgetUnknown:
      'Nu am putut confirma dacă a fost salvat. Încearcă din nou; nu va fi aplicat de două ori.',
    scheduleFacts: 'Rulează {schedule} ({zone})',
    scheduleNext: 'Următoarea rulare {next}',
    scheduleOn: 'Activat',
    scheduleNotScheduled: 'Nu este programat',
    scheduleEnable: 'Activează',
    scheduleUnavailable: 'Programul nu este disponibil momentan.',
    scheduleDenied: 'Nu poți modifica acest program.',
    scheduleConflict:
      'Programul s-a schimbat de când l-ai deschis. Verifică-l și încearcă din nou.',
    scheduleUnknown:
      'Nu am putut confirma dacă programul s-a schimbat. Încearcă din nou; nu va fi aplicat de două ori.',
    budgetHeading: 'Buget',
    scheduleOnlyHeading: 'Program',
    scheduleJobOrdinal: 'Rulare programată {n}',
    budgetUnconfirmed: 'Nu am putut confirma dacă a fost salvat.',
    scheduleUnconfirmed: 'Nu am putut confirma dacă programul s-a schimbat.',
    scheduleTimeNote: 'Orele sunt în fusul orar al programului.',
    scheduleWeekdays: 'Rulează în zilele lucrătoare la {time}',
    scheduleWeekly: 'Rulează în fiecare {days} la {time}',
    scheduleCustom: 'Rulează după propriul program',
    scheduleThen: 'Apoi {runs}',
    scheduleChange: 'Schimbă programul',
    scheduleChoose: 'Alege altă oră',
    pickerHeading: 'Când rulează',
    presetEvery15: 'La fiecare 15 minute',
    presetEvery30: 'La fiecare 30 de minute',
    presetHourly: 'În fiecare oră',
    presetDaily: 'În fiecare zi',
    presetWeekdays: 'Zile lucrătoare',
    presetWeekly: 'În fiecare săptămână',
    presetCustom: 'Personalizat',
    pickerTime: 'Ora',
    pickerDay: 'Ziua',
    pickerCron: 'Expresie cron',
    pickerCronHelp:
      'Cinci câmpuri: minut, oră, ziua lunii, luna și ziua săptămânii, de exemplu 0 9 1 * * pentru ora 09:00 în prima zi a fiecărei luni.',
    pickerCustomPreview: 'Salvarea verifică acest program și arată următoarele rulări.',
    pickerCancel: 'Anulează',
    scheduleTooFrequent: 'Rulările pot fi cel mult la fiecare 15 minute.',
    scheduleInvalidChoice: 'Acest program nu poate fi folosit. Verifică expresia și fusul orar.',
    scheduleBusy: 'O rulare este în curs. Încearcă din nou când se termină.',
    runsHeading: 'Rulări recente',
    runsEmpty: 'Nicio rulare încă.',
    runsUnavailable: 'Rulările recente nu sunt disponibile momentan.',
    runSucceeded: 'Reușită',
    runFailed: 'Eșuată',
    runUnknown: 'Neconfirmată',
    runManual: 'Manuală',
    runScheduled: 'Programată',
    runWebhook: 'Webhook',
    runEvent: 'Modificare de date',
    runShowLog: 'Arată jurnalul',
    runHideLog: 'Ascunde jurnalul',
    runLogEmpty: 'Nu a afișat nimic.',
    runLogDropped: '{n} linii anterioare nu au fost păstrate.',
    runError: 'Eroare',
    runPrinted: 'Ce a afișat',
    runInput: 'Intrare',
    runLogUnavailable: 'Jurnalul nu este disponibil momentan.',
    runFix: 'Remediază în chat',
    runFixFailed: 'Chatul aplicației nu a putut fi deschis. Încearcă din nou.',
    runsMore: 'Încarcă mai multe',
    fixScheduled: 'Rularea programată a acestei aplicații a eșuat. Găsește cauza și remediaz-o.',
    fixTriggered: 'O rulare a acestei aplicații a eșuat. Găsește cauza și remediaz-o.',
    fixUnknown:
      'O rulare a acestei aplicații s-a încheiat fără un rezultat confirmat. Verifică ce s-a întâmplat și remediază.',
    fixRunLine: 'Funcția {name} · rularea {id} · {time}',
    fixError: 'Eroare',
    fixPrinted: 'Ultimele linii afișate',
    fixInput: 'Intrarea ei',
  },
  sk: {
    sourcesHeading: 'Čo sledovať',
    sourcesNote:
      'Zdroje pochádzajú z certifikovaných balíkov Obchodu s nástrojmi a chýbajúce zdroje sa pridávajú na požiadanie.',
    chooseSource: 'Vybrať {name}',
    readsLine: 'Číta {fields}',
    partialLine: 'Číta {fields}. Zatiaľ nie: {gaps}.',
    awaitingLine: 'Zatiaľ nepodporované. Chýbajúce zdroje sa pridávajú na požiadanie.',
    requestIt: 'Požiadať o to',
    requestedLine: 'Vyžiadané {date} · oznámime vám, keď to bude pripravené.',
    needsConnection: 'Vyžaduje pripojenie {whose} {system}',
    connect: 'Pripojiť {system}',
    askOwner: 'Opýtať sa vlastníka {owner}',
    coverageSummary: '{n} zdrojov sa zhromažďuje teraz; {m} sa pridá, keď budú podporované.',
    coverageSummaryOne: '{n} zdroj sa zhromažďuje teraz; {m} sa pridá, keď budú podporované.',
    scheduleHeading: 'Plán a rozpočet',
    scheduleTime: 'Spúšťa sa každý deň o',
    scheduleDaily: 'Spúšťa sa každý deň o {time}',
    scheduleEveryHour: 'Spúšťa sa každú hodinu',
    scheduleEveryMinutes: 'Spúšťa sa každých {minutes} minút',
    scheduleTimezone: 'Časové pásmo',
    budgetPerRun: 'Maximum na jedno spustenie',
    budgetMonthly: 'Maximum na mesiac',
    scheduleSave: 'Uložiť plán',
    scheduleSaving: 'Ukladá sa…',
    scheduleSummary:
      'Denne o {time} {zone} · najviac {perRun} na jedno spustenie · najviac {monthly} mesačne',
    oneRunPerDay:
      'Jedno spustenie denne. Opakované ani zopakované spustenie sa nikdy nezapočíta dvakrát.',
    errTime: 'Zadajte čas, napríklad 07:00.',
    errCredits: 'Zadajte celé číslo od {min} do {max}.',
    errOrder: 'Mesačný limit musí byť aspoň taký vysoký ako limit na jedno spustenie.',
    collecting: 'Zhromažďuje sa denne o {time} · posledné čítanie {last} · {rows} riadkov',
    collectingNever: 'Zhromažďuje sa denne o {time} · zatiaľ sa nečítalo',
    awaitingData: 'Vyžiadané {date} · zatiaľ sa nezhromažďujú údaje',
    stopped: 'Zastavené: {reason}',
    reconnect: 'Znova pripojiť',
    skippedBudget:
      'Dnes sa preskočilo {skipped} z {total} kategórií: mesačný rozpočet bol vyčerpaný.',
    gapMarker: 'Za toto obdobie nie sú k dispozícii údaje',
    gapBudget: 'Preskočené: mesačný rozpočet bol vyčerpaný',
    gapUnsupported: 'Preskočené: zatiaľ nepodporované',
    gapFailed: 'Preskočené: zdroj neodpovedal',
    chartLabel: 'Cena v čase. Preskočené obdobie znamená prerušenie čiary, nie nulovú hodnotu.',
    firstRunTitle: 'Prvé spustenie dokončené',
    firstRun:
      'Prečítaných zdrojov: {read} · zatiaľ nepodporovaných: {unsupported} · cena {cost} · ďalšie spustenie {next}',
    firstRunUnmeasured:
      'Prečítaných zdrojov: {read} · zatiaľ nepodporovaných: {unsupported} · cena zatiaľ nezmeraná · ďalšie spustenie {next}',
    firstRunNoNext:
      'Prečítaných zdrojov: {read} · zatiaľ nepodporovaných: {unsupported} · cena {cost} · žiadne spustenie nie je naplánované',
    lastRunNone: 'Zatiaľ žiadne spustenie. Prvé prebehne v naplánovanom čase.',
    paused: 'Pozastavené',
    pause: 'Pozastaviť',
    resume: 'Pokračovať',
    historyFrom: 'História zo zdroja {source} od {since} (dátumy zo zdroja)',
    historyImportedMark: 'Importované',
    historyToday: 'História začína dnes, {date}.',
    budgetSave: 'Uložiť rozpočet',
    creditsUnit: 'kreditov',
    budgetInvalid: 'Tieto limity nie je možné uložiť. Skontrolujte ich a skúste to znova.',
    budgetSummary: 'Najviac {perRun} na jedno spustenie · najviac {monthly} mesačne',
    budgetNone: 'Rozpočet zatiaľ nie je nastavený.',
    budgetUnavailable: 'Rozpočet momentálne nie je k dispozícii.',
    budgetDenied: 'Tento rozpočet nemôžete zmeniť.',
    budgetChanged:
      'Rozpočet sa od otvorenia tejto stránky zmenil. Skontrolujte limity a skúste to znova.',
    budgetUnknown:
      'Nepodarilo sa nám potvrdiť, či sa rozpočet uložil. Skúste to znova; neuplatní sa dvakrát.',
    scheduleFacts: 'Spúšťa sa {schedule} ({zone})',
    scheduleNext: 'Ďalšie spustenie {next}',
    scheduleOn: 'Zapnuté',
    scheduleNotScheduled: 'Nenaplánované',
    scheduleEnable: 'Zapnúť',
    scheduleUnavailable: 'Plán momentálne nie je k dispozícii.',
    scheduleDenied: 'Tento plán nemôžete zmeniť.',
    scheduleConflict: 'Plán sa od vášho otvorenia zmenil. Skontrolujte ho a skúste znova.',
    scheduleUnknown:
      'Nepodarilo sa potvrdiť, či sa plán zmenil. Skúste znova; nepoužije sa dvakrát.',
    budgetHeading: 'Rozpočet',
    scheduleOnlyHeading: 'Plán',
    scheduleJobOrdinal: 'Plánované spustenie {n}',
    budgetUnconfirmed: 'Nepodarilo sa potvrdiť, či bol uložený.',
    scheduleUnconfirmed: 'Nepodarilo sa potvrdiť, či sa plán zmenil.',
    scheduleTimeNote: 'Časy platia v časovom pásme plánu.',
    scheduleWeekdays: 'Spúšťa sa vo všedné dni o {time}',
    scheduleWeekly: 'Spúšťa sa každý týždeň: {days} o {time}',
    scheduleCustom: 'Spúšťa sa podľa vlastného plánu',
    scheduleThen: 'Potom {runs}',
    scheduleChange: 'Zmeniť plán',
    scheduleChoose: 'Zvoliť iný čas',
    pickerHeading: 'Kedy sa spúšťa',
    presetEvery15: 'Každých 15 minút',
    presetEvery30: 'Každých 30 minút',
    presetHourly: 'Každú hodinu',
    presetDaily: 'Každý deň',
    presetWeekdays: 'Všedné dni',
    presetWeekly: 'Každý týždeň',
    presetCustom: 'Vlastné',
    pickerTime: 'Čas',
    pickerDay: 'Deň',
    pickerCron: 'Výraz cron',
    pickerCronHelp:
      'Päť polí: minúta, hodina, deň v mesiaci, mesiac a deň v týždni, napríklad 0 9 1 * * znamená 9:00 prvého dňa každého mesiaca.',
    pickerCustomPreview: 'Pri uložení sa plán skontroluje a zobrazia sa ďalšie spustenia.',
    pickerCancel: 'Zrušiť',
    scheduleTooFrequent: 'Spustenia môžu prebiehať najčastejšie každých 15 minút.',
    scheduleInvalidChoice: 'Tento plán nemožno použiť. Skontrolujte výraz a časové pásmo.',
    scheduleBusy: 'Spustenie práve prebieha. Skúste to znova, keď skončí.',
    runsHeading: 'Posledné spustenia',
    runsEmpty: 'Zatiaľ žiadne spustenia.',
    runsUnavailable: 'Posledné spustenia teraz nie sú k dispozícii.',
    runSucceeded: 'Úspešné',
    runFailed: 'Neúspešné',
    runUnknown: 'Nepotvrdené',
    runManual: 'Spustené ručne',
    runScheduled: 'Naplánované',
    runWebhook: 'Webhook',
    runEvent: 'Zmena údajov',
    runShowLog: 'Zobraziť log',
    runHideLog: 'Skryť log',
    runLogEmpty: 'Nič nevypísala.',
    runLogDropped: '{n} skorších riadkov sa nezachovalo.',
    runError: 'Chyba',
    runPrinted: 'Čo vypísala',
    runInput: 'Vstup',
    runLogUnavailable: 'Log teraz nie je k dispozícii.',
    runFix: 'Opraviť v chate',
    runFixFailed: 'Chat aplikácie sa nepodarilo otvoriť. Skúste to znova.',
    runsMore: 'Načítať ďalšie',
    fixScheduled: 'Naplánované spustenie tejto aplikácie zlyhalo. Nájdi príčinu a oprav to.',
    fixTriggered: 'Spustenie tejto aplikácie zlyhalo. Nájdi príčinu a oprav to.',
    fixUnknown:
      'Spustenie tejto aplikácie sa skončilo bez potvrdeného výsledku. Skontroluj, čo sa stalo, a oprav to.',
    fixRunLine: 'Funkcia {name} · spustenie {id} · {time}',
    fixError: 'Chyba',
    fixPrinted: 'Posledné vypísané riadky',
    fixInput: 'Jej vstup',
  },
  sl: {
    sourcesHeading: 'Kaj spremljati',
    sourcesNote:
      'Viri so iz certificiranih paketov v Trgovini orodij, manjkajoči pa se dodajo na zahtevo.',
    chooseSource: 'Izberite {name}',
    readsLine: 'Bere {fields}',
    partialLine: 'Bere {fields}. Še ni na voljo: {gaps}.',
    awaitingLine: 'Še ni podprto. Manjkajoči viri se dodajo na zahtevo.',
    requestIt: 'Zahtevaj',
    requestedLine: 'Zahtevano {date} · obvestili vas bomo, ko bo pripravljeno.',
    needsConnection: 'Potrebuje povezavo {whose} {system}',
    connect: 'Poveži {system}',
    askOwner: 'Vprašaj {owner}',
    coverageSummary: '{n} virov se zbira zdaj; {m} se jih bo pridružilo, ko bodo podprti.',
    coverageSummaryOne: '{n} vir se zbira zdaj; {m} se jih bo pridružilo, ko bodo podprti.',
    scheduleHeading: 'Urnik in proračun',
    scheduleTime: 'Zažene se vsak dan ob',
    scheduleDaily: 'Zažene se vsak dan ob {time}',
    scheduleEveryHour: 'Zažene se vsako uro',
    scheduleEveryMinutes: 'Zažene se vsakih {minutes} minut',
    scheduleTimezone: 'Časovni pas',
    budgetPerRun: 'Največja poraba za en zagon',
    budgetMonthly: 'Največja mesečna poraba',
    scheduleSave: 'Shrani urnik',
    scheduleSaving: 'Shranjevanje…',
    scheduleSummary: 'Vsak dan ob {time} {zone} · do {perRun} na zagon · do {monthly} na mesec',
    oneRunPerDay: 'En zagon na dan. Ponovljen ali znova izveden zagon se nikoli ne šteje dvakrat.',
    errTime: 'Vnesite čas v obliki 07:00.',
    errCredits: 'Vnesite celo število od {min} do {max}.',
    errOrder: 'Mesečna omejitev mora biti najmanj enaka omejitvi za en zagon.',
    collecting: 'Zbiranje vsak dan ob {time} · zadnje branje {last} · {rows} vrstic',
    collectingNever: 'Zbiranje vsak dan ob {time} · še ni prebrano',
    awaitingData: 'Zahtevano {date} · zbiranje se še ni začelo',
    stopped: 'Ustavljeno: {reason}',
    reconnect: 'Znova poveži',
    skippedBudget: 'Danes preskočeno {skipped} od {total} kategorij: mesečni proračun je dosežen.',
    gapMarker: 'Za to obdobje ni podatkov',
    gapBudget: 'Preskočeno: mesečni proračun je dosežen',
    gapUnsupported: 'Preskočeno: še ni podprto',
    gapFailed: 'Preskočeno: vir se ni odzval',
    chartLabel: 'Cena skozi čas. Preskočeno obdobje pomeni prekinitev črte, ne ničelne vrednosti.',
    firstRunTitle: 'Prvi zagon je končan',
    firstRun:
      'Prebranih virov: {read} · še ni podprto: {unsupported} · strošek {cost} · naslednji zagon {next}',
    firstRunUnmeasured:
      'Prebranih virov: {read} · še ni podprto: {unsupported} · strošek še ni izmerjen · naslednji zagon {next}',
    firstRunNoNext:
      'Prebranih virov: {read} · še ni podprto: {unsupported} · strošek {cost} · noben zagon ni načrtovan',
    lastRunNone: 'Zagona še ni bilo. Prvi bo izveden ob načrtovanem času.',
    paused: 'Začasno ustavljeno',
    pause: 'Začasno ustavi',
    resume: 'Nadaljuj',
    historyFrom: 'Zgodovina iz vira {source} od {since} (datumi iz vira)',
    historyImportedMark: 'Uvoženo',
    historyToday: 'Zgodovina se začne danes, {date}.',
    budgetSave: 'Shrani proračun',
    creditsUnit: 'kreditov',
    budgetInvalid: 'Teh omejitev ni mogoče shraniti. Preverite jih in poskusite znova.',
    budgetSummary: 'Do {perRun} na zagon · do {monthly} na mesec',
    budgetNone: 'Proračun še ni nastavljen.',
    budgetUnavailable: 'Proračun trenutno ni na voljo.',
    budgetDenied: 'Tega proračuna ne morete spremeniti.',
    budgetChanged:
      'Proračun se je od vašega odprtja spremenil. Preverite omejitve in poskusite znova.',
    budgetUnknown:
      'Ni bilo mogoče potrditi, ali je bil shranjen. Poskusite znova; ne bo uporabljen dvakrat.',
    scheduleFacts: 'Zažene se {schedule} ({zone})',
    scheduleNext: 'Naslednji zagon {next}',
    scheduleOn: 'Vklopljeno',
    scheduleNotScheduled: 'Ni načrtovano',
    scheduleEnable: 'Vklopi',
    scheduleUnavailable: 'Razpored trenutno ni na voljo.',
    scheduleDenied: 'Tega razporeda ne morete spremeniti.',
    scheduleConflict:
      'Razpored se je od vašega odprtja spremenil. Preverite ga in poskusite znova.',
    scheduleUnknown:
      'Nismo mogli potrditi, ali se je razpored spremenil. Poskusite znova; ne bo uporabljen dvakrat.',
    budgetHeading: 'Proračun',
    scheduleOnlyHeading: 'Razpored',
    scheduleJobOrdinal: 'Načrtovan zagon {n}',
    budgetUnconfirmed: 'Nismo mogli potrditi, ali je bilo shranjeno.',
    scheduleUnconfirmed: 'Nismo mogli potrditi, ali se je razpored spremenil.',
    scheduleTimeNote: 'Časi veljajo v časovnem pasu razporeda.',
    scheduleWeekdays: 'Zažene se ob delavnikih ob {time}',
    scheduleWeekly: 'Zažene se vsak teden: {days} ob {time}',
    scheduleCustom: 'Zažene se po lastnem razporedu',
    scheduleThen: 'Nato {runs}',
    scheduleChange: 'Spremeni razpored',
    scheduleChoose: 'Izberi drug čas',
    pickerHeading: 'Kdaj se zažene',
    presetEvery15: 'Vsakih 15 minut',
    presetEvery30: 'Vsakih 30 minut',
    presetHourly: 'Vsako uro',
    presetDaily: 'Vsak dan',
    presetWeekdays: 'Delavniki',
    presetWeekly: 'Vsak teden',
    presetCustom: 'Po meri',
    pickerTime: 'Čas',
    pickerDay: 'Dan',
    pickerCron: 'Izraz cron',
    pickerCronHelp:
      'Pet polj: minuta, ura, dan v mesecu, mesec in dan v tednu, na primer 0 9 1 * * pomeni ob 9.00 prvi dan vsakega meseca.',
    pickerCustomPreview: 'Pri shranjevanju se razpored preveri in prikažejo se naslednji zagoni.',
    pickerCancel: 'Prekliči',
    scheduleTooFrequent: 'Zagoni so lahko največ vsakih 15 minut.',
    scheduleInvalidChoice: 'Tega razporeda ni mogoče uporabiti. Preverite izraz in časovni pas.',
    scheduleBusy: 'Zagon poteka. Poskusite znova, ko se konča.',
    runsHeading: 'Zadnji zagoni',
    runsEmpty: 'Zagonov še ni.',
    runsUnavailable: 'Zadnji zagoni trenutno niso na voljo.',
    runSucceeded: 'Uspešno',
    runFailed: 'Neuspešno',
    runUnknown: 'Nepotrjeno',
    runManual: 'Ročni zagon',
    runScheduled: 'Načrtovano',
    runWebhook: 'Webhook',
    runEvent: 'Sprememba podatkov',
    runShowLog: 'Pokaži dnevnik',
    runHideLog: 'Skrij dnevnik',
    runLogEmpty: 'Ni izpisala ničesar.',
    runLogDropped: '{n} prejšnjih vrstic ni bilo ohranjenih.',
    runError: 'Napaka',
    runPrinted: 'Kaj je izpisala',
    runInput: 'Vhod',
    runLogUnavailable: 'Dnevnik trenutno ni na voljo.',
    runFix: 'Popravi v klepetu',
    runFixFailed: 'Klepeta aplikacije ni bilo mogoče odpreti. Poskusite znova.',
    runsMore: 'Naloži več',
    fixScheduled: 'Načrtovani zagon te aplikacije ni uspel. Poišči vzrok in popravi.',
    fixTriggered: 'Zagon te aplikacije ni uspel. Poišči vzrok in popravi.',
    fixUnknown:
      'Zagon te aplikacije se je končal brez potrjenega rezultata. Preveri, kaj se je zgodilo, in popravi.',
    fixRunLine: 'Funkcija {name} · zagon {id} · {time}',
    fixError: 'Napaka',
    fixPrinted: 'Zadnje izpisane vrstice',
    fixInput: 'Njen vhod',
  },
  sv: {
    sourcesHeading: 'Vad som ska följas',
    sourcesNote:
      'Källor kommer från certifierade Tool Store-paket, och saknade läggs till på begäran.',
    chooseSource: 'Välj {name}',
    readsLine: 'Läser {fields}',
    partialLine: 'Läser {fields}. Ännu inte: {gaps}.',
    awaitingLine: 'Stöds inte än. Saknade källor läggs till på begäran.',
    requestIt: 'Begär den',
    requestedLine: 'Begärd {date} · vi hör av oss när den är klar.',
    needsConnection: 'Behöver {whose} {system}-anslutning',
    connect: 'Anslut {system}',
    askOwner: 'Fråga {owner}',
    coverageSummary: '{n} källor samlar in nu; {m} ansluter när de stöds.',
    coverageSummaryOne: '{n} källa samlar in nu; {m} ansluter när de stöds.',
    scheduleHeading: 'Schema och budget',
    scheduleTime: 'Körs varje dag kl.',
    scheduleDaily: 'Körs varje dag kl. {time}',
    scheduleEveryHour: 'Körs varje timme',
    scheduleEveryMinutes: 'Körs var {minutes}:e minut',
    scheduleTimezone: 'Tidszon',
    budgetPerRun: 'Mest att spendera på en körning',
    budgetMonthly: 'Mest att spendera på en månad',
    scheduleSave: 'Spara schema',
    scheduleSaving: 'Sparar…',
    scheduleSummary:
      'Dagligen kl. {time} {zone} · högst {perRun} per körning · högst {monthly} i månaden',
    oneRunPerDay:
      'En körning per dag. En upprepad eller omförsökt körning räknas aldrig två gånger.',
    errTime: 'Ange en tid som 07:00.',
    errCredits: 'Ange ett heltal från {min} till {max}.',
    errOrder: 'Månadsgränsen måste vara minst lika hög som gränsen för en körning.',
    collecting: 'Samlar in dagligen kl. {time} · senast läst {last} · {rows} rader',
    collectingNever: 'Samlar in dagligen kl. {time} · inte läst än',
    awaitingData: 'Begärd {date} · samlar inte in än',
    stopped: 'Stoppad: {reason}',
    reconnect: 'Anslut igen',
    skippedBudget: 'Hoppade över {skipped} av {total} kategorier idag: månadsbudgeten är nådd.',
    gapMarker: 'Ingen data för perioden',
    gapBudget: 'Hoppad över: månadsbudgeten är nådd',
    gapUnsupported: 'Hoppad över: stöds inte än',
    gapFailed: 'Hoppad över: källan svarade inte',
    chartLabel: 'Pris över tid. En överhoppad period är ett avbrott i linjen, inte noll.',
    firstRunTitle: 'Första körningen klar',
    firstRun:
      'Läste {read} källor · {unsupported} stöds inte än · kostnad {cost} · nästa körning {next}',
    firstRunUnmeasured:
      'Läste {read} källor · {unsupported} stöds inte än · kostnad inte uppmätt än · nästa körning {next}',
    firstRunNoNext:
      'Läste {read} källor · {unsupported} stöds inte än · kostnad {cost} · ingen körning är schemalagd',
    lastRunNone: 'Ingen körning än. Den första körs vid den schemalagda tiden.',
    paused: 'Pausad',
    pause: 'Pausa',
    resume: 'Återuppta',
    historyFrom: 'Historik från {source} sedan {since} (datum från källan)',
    historyImportedMark: 'Importerad',
    historyToday: 'Historiken börjar idag, {date}.',
    budgetSave: 'Spara budget',
    creditsUnit: 'krediter',
    budgetInvalid: 'De här gränserna kan inte sparas. Kontrollera dem och försök igen.',
    budgetSummary: 'Högst {perRun} per körning · högst {monthly} i månaden',
    budgetNone: 'Ingen budget är angiven än.',
    budgetUnavailable: 'Budgeten är inte tillgänglig just nu.',
    budgetDenied: 'Du kan inte ändra den här budgeten.',
    budgetChanged:
      'Budgeten har ändrats sedan du öppnade den. Kontrollera gränserna och försök igen.',
    budgetUnknown:
      'Vi kunde inte bekräfta om den sparades. Försök igen; den tillämpas inte två gånger.',
    scheduleFacts: 'Körs {schedule} ({zone})',
    scheduleNext: 'Nästa körning {next}',
    scheduleOn: 'På',
    scheduleNotScheduled: 'Inte schemalagd',
    scheduleEnable: 'Slå på',
    scheduleUnavailable: 'Schemat är inte tillgängligt just nu.',
    scheduleDenied: 'Du kan inte ändra det här schemat.',
    scheduleConflict: 'Schemat har ändrats sedan du öppnade det. Kontrollera det och försök igen.',
    scheduleUnknown:
      'Vi kunde inte bekräfta om schemat ändrades. Försök igen; ändringen tillämpas inte två gånger.',
    budgetHeading: 'Budget',
    scheduleOnlyHeading: 'Schema',
    scheduleJobOrdinal: 'Schemalagd körning {n}',
    budgetUnconfirmed: 'Vi kunde inte bekräfta om den sparades.',
    scheduleUnconfirmed: 'Vi kunde inte bekräfta om schemat ändrades.',
    scheduleTimeNote: 'Tiderna gäller i schemats egen tidszon.',
    scheduleWeekdays: 'Körs vardagar kl. {time}',
    scheduleWeekly: 'Körs varje {days} kl. {time}',
    scheduleCustom: 'Körs enligt eget schema',
    scheduleThen: 'Sedan {runs}',
    scheduleChange: 'Ändra schema',
    scheduleChoose: 'Välj en annan tid',
    pickerHeading: 'När den körs',
    presetEvery15: 'Var 15:e minut',
    presetEvery30: 'Var 30:e minut',
    presetHourly: 'Varje timme',
    presetDaily: 'Varje dag',
    presetWeekdays: 'Vardagar',
    presetWeekly: 'Varje vecka',
    presetCustom: 'Eget',
    pickerTime: 'Tid',
    pickerDay: 'Dag',
    pickerCron: 'Cron-uttryck',
    pickerCronHelp:
      'Fem fält: minut, timme, dag i månaden, månad och veckodag, till exempel 0 9 1 * * för kl. 09:00 den första varje månad.',
    pickerCustomPreview: 'När du sparar kontrolleras schemat och nästa körningar visas.',
    pickerCancel: 'Avbryt',
    scheduleTooFrequent: 'Körningar kan ske högst var 15:e minut.',
    scheduleInvalidChoice: 'Det schemat kan inte användas. Kontrollera uttrycket och tidszonen.',
    scheduleBusy: 'En körning pågår. Försök igen när den är klar.',
    runsHeading: 'Senaste körningar',
    runsEmpty: 'Inga körningar ännu.',
    runsUnavailable: 'De senaste körningarna är inte tillgängliga just nu.',
    runSucceeded: 'Lyckades',
    runFailed: 'Misslyckades',
    runUnknown: 'Obekräftad',
    runManual: 'Körd för hand',
    runScheduled: 'Schemalagd',
    runWebhook: 'Webhook',
    runEvent: 'Dataändring',
    runShowLog: 'Visa logg',
    runHideLog: 'Dölj logg',
    runLogEmpty: 'Den skrev inget.',
    runLogDropped: '{n} tidigare rader sparades inte.',
    runError: 'Fel',
    runPrinted: 'Vad den skrev',
    runInput: 'Indata',
    runLogUnavailable: 'Loggen är inte tillgänglig just nu.',
    runFix: 'Rätta i chatten',
    runFixFailed: 'Appens chatt kunde inte öppnas. Försök igen.',
    runsMore: 'Visa fler',
    fixScheduled: 'Den här appens schemalagda körning misslyckades. Hitta orsaken och rätta det.',
    fixTriggered: 'Den här appens körning misslyckades. Hitta orsaken och rätta det.',
    fixUnknown:
      'Den här appens körning avslutades utan bekräftat resultat. Kontrollera vad som hände och rätta det.',
    fixRunLine: 'Funktion {name} · körning {id} · {time}',
    fixError: 'Fel',
    fixPrinted: 'De sista raderna den skrev',
    fixInput: 'Dess indata',
  },
};
export default appTrackerCopy;
